import React from 'react';
import styled from 'styled-components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { useCurrentUserProfile } from '../hooks/useCurrentUserProfile';
import { useMyEventAdminEventIds } from '../hooks/useEventAdmins';
import { Permission, hasPermission } from '../utils/roles';
import EventRegistrantEmailComposer, {
  type ComposerRecipientScope,
  type DraftRecord,
} from '../components/events/EventRegistrantEmailComposer';
import EventRegistrantSmsComposer, {
  type SmsComposerRecipientScope,
} from '../components/events/EventRegistrantSmsComposer';
import { EventRegistrantEditModal } from '../components/events/EventRegistrantEditModal';
import EventWaiverList from '../components/events/EventWaiverList';
import { useEventOrderForm, type EventOrderFormField } from '../hooks/useEvents';
import {
  buildRegistrantExportAttendeeRows,
  buildRegistrationAttendeePeople,
  collectAttendeeResponsesByIndex,
  filterDirectoryAttendeesBySearch,
  filterRegistrationsBySearch,
} from '../utils/eventRegistrantSearch';
import { formatCheckboxResponse } from '../utils/eventRegistrationFormFields';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — react-icons types are not yet compatible with React 19 typings
import { FiCheck as FiCheckRaw, FiMoreVertical as FiMoreVerticalRaw } from 'react-icons/fi';

const FiMoreVerticalIcon = FiMoreVerticalRaw as React.FC<{ 'aria-hidden'?: boolean }>;
const FiCheckIcon = FiCheckRaw as React.FC<{ 'aria-hidden'?: boolean; size?: number }>;

type PublicEventRegistration = {
  id: string;
  event_id: string;
  user_id: string | null;
  full_name: string;
  email: string;
  phone: string | null;
  quantity: number;
  ticket_type_id: string | null;
  ticket_type_name: string | null;
  status: string;
  payment_status: string | null;
  source: string;
  notes: string | null;
  total_amount_cents: number | null;
  amount_paid_cents: number | null;
  balance_due_cents: number | null;
  payment_amount_cents: number | null;
  form_response: Record<string, unknown>;
  created_at: string;
  appointments: PublicEventRegistrationAppointment[];
  events?: {
    id: string;
    title: string;
    start_date: string | null;
    start_time: string | null;
    location: string | null;
  } | null;
};

type PublicEventRegistrationAppointment = {
  slot_id: string;
  label: string;
  starts_at: string | null;
  ends_at: string | null;
};

type EventOrderFormFieldRow = EventOrderFormField;

type EventOrderTshirtRow = {
  id: string;
  user_id: string | null;
  attendee_name: string | null;
  attendee_email: string | null;
  form_response: Record<string, unknown> | null;
  created_at: string;
};

type EventTshirtHandoutRow = {
  attendee_key: string;
  given_at: string | null;
};

type TshirtGivenFilter = 'all' | 'given' | 'not_given';

type EventTicketTypeRow = {
  id: string;
  name: string;
  price_cents: number;
  status: string;
};

type TicketChangeQuote = {
  registrationId: string;
  oldTicketTypeName: string;
  newTicketTypeName: string;
  differenceCents: number;
  requiresPayment: boolean;
  requiresManualRefund: boolean;
};

type TicketChangeHistoryRow = {
  id: string;
  registration_id: string;
  old_ticket_type_name: string;
  new_ticket_type_name: string;
  difference_cents: number;
  status: string;
  payment_status: string;
  requested_by_type: string;
  created_at: string;
  completed_at: string | null;
};

type PublicEventSummary = {
  event_id: string;
  title: string;
  start_date: string | null;
  start_time: string | null;
  location: string | null;
  registration_count: number;
  total_quantity: number;
  last_submitted_at: string | null;
};

type EventDetails = {
  id: string;
  title: string;
  start_date: string | null;
  start_time: string | null;
  location: string | null;
};

type EventAppointmentSlotOption = {
  id: string;
  label: string;
  starts_at: string | null;
  ends_at: string | null;
};

type RegistrationTicketRow = {
  id: string;
  registration_id: string;
  attendee_name: string | null;
  ticket_number: string | null;
  status: string | null;
  checked_in_at: string | null;
};

type RegistrationCheckInStatus = {
  checkedIn: boolean;
  checkedInCount: number;
  totalTickets: number;
  remainingTickets: number;
  lastCheckedInAt: string | null;
  primaryTicket: RegistrationTicketRow | null;
  primaryCheckedIn: boolean;
  tickets: RegistrationTicketRow[];
};

type RegistrationCheckInFunctionResult = {
  result: 'accepted' | 'duplicate' | 'invalid' | 'cancelled';
  checkedInCount?: number;
  attendeeName?: string | null;
  ticket?: Record<string, unknown>;
  tickets?: Array<Record<string, unknown>>;
};

type ManualCheckInInput = {
  registration: PublicEventRegistration;
  ticketId?: string | null;
  checkInAllTickets?: boolean;
  displayName?: string;
};

type RegistrantDirectoryRow = {
  key: string;
  registration: PublicEventRegistration;
  attendeeIndex: number;
  isPrimary: boolean;
  fullName: string;
  email: string;
  phone: string;
  ticket: RegistrationTicketRow | null;
};

type EmailComposerMode = 'single' | 'bulk';

type EmailNotice = {
  kind: 'success' | 'error' | 'info';
  message: string;
};

type EmailSendHistoryRow = {
  id: string;
  send_mode: 'single' | 'bulk';
  recipient_scope: 'single' | 'all' | 'filtered' | 'selected';
  subject: string;
  status: 'queued' | 'sent' | 'partial_failure' | 'failed';
  recipient_count: number;
  sent_count: number;
  failed_count: number;
  skipped_count: number;
  error_summary: string | null;
  created_at: string;
};

type EmailSendRecipientFailureRow = {
  id: string;
  send_id: string;
  email: string;
  full_name: string | null;
  status: 'sent' | 'failed' | 'skipped';
  error_message: string | null;
  created_at: string;
};

type SmsSendHistoryRow = {
  id: string;
  send_mode: 'single' | 'bulk';
  recipient_scope: 'single' | 'all' | 'filtered' | 'selected';
  message_body: string;
  status: 'queued' | 'sent' | 'partial_failure' | 'failed';
  recipient_count: number;
  sent_count: number;
  failed_count: number;
  skipped_count: number;
  error_summary: string | null;
  created_at: string;
};

type SmsSendRecipientFailureRow = {
  id: string;
  send_id: string;
  phone: string;
  full_name: string | null;
  status: 'sent' | 'failed' | 'skipped';
  error_message: string | null;
  created_at: string;
};

const REGISTRATIONS_PAGE_SIZE = 500;
const REGISTRANT_LIST_PAGE_SIZE = 25;
const NO_SHOW_LIST_PAGE_SIZE = 25;
const STRUCTURAL_FORM_RESPONSE_KEYS = new Set([
  'registration_type',
  'registrationtype',
  'primary_contact',
  'primarycontact',
  'attendee_responses',
  'attendeeresponses',
  'group_members',
  'groupmembers',
  'group_mode',
  'groupmode',
  'group_join',
  'groupjoin',
  'group_id',
  'groupid',
  '_mobile_checkout',
  'mobile_checkout',
]);

const statusColor = (value: string) => {
  const normalized = value.toLowerCase();
  if (normalized === 'confirmed') return '#047857';
  if (normalized === 'cancelled') return 'var(--error)';
  return '#92400e';
};

const normalizeTicketName = (value: unknown) => String(value || '').trim().toLowerCase();
const isTicketCancelled = (ticket: { status?: string | null } | null | undefined) =>
  String(ticket?.status || '').trim().toLowerCase() === 'cancelled';
const isTicketCheckedIn = (ticket: { status?: string | null } | null | undefined) =>
  String(ticket?.status || '').trim().toLowerCase() === 'checked_in';

const compareTicketNumber = (left: RegistrationTicketRow, right: RegistrationTicketRow) => {
  const leftNum = Number.parseInt(String(left.ticket_number || ''), 10);
  const rightNum = Number.parseInt(String(right.ticket_number || ''), 10);
  return (Number.isFinite(leftNum) ? leftNum : 0) - (Number.isFinite(rightNum) ? rightNum : 0);
};

const pickPrimaryRegistrationTicket = (
  tickets: RegistrationTicketRow[],
  registrationFullName: string,
) => {
  const sorted = [...tickets].sort(compareTicketNumber);
  const active = sorted.filter((ticket) => !isTicketCancelled(ticket));
  const pool = active.length > 0 ? active : sorted;
  const wanted = normalizeTicketName(registrationFullName);
  if (wanted) {
    const named = pool.find((ticket) => normalizeTicketName(ticket.attendee_name) === wanted);
    if (named) return named;
  }
  return pool[0] || null;
};

const laterTimestamp = (left: string | null, right: string | null) => {
  if (!left) return right;
  if (!right) return left;
  return left < right ? right : left;
};

const formatCheckInCount = (status: RegistrationCheckInStatus) =>
  `${status.checkedInCount} of ${status.totalTickets}`;

const resolveCheckInAttendeeName = (
  result: RegistrationCheckInFunctionResult | null | undefined,
  fallback?: string,
) => {
  const fromResult = String(result?.attendeeName || '').trim();
  if (fromResult) return fromResult;
  const fromTicket = String(result?.ticket?.attendee_name || '').trim();
  if (fromTicket) return fromTicket;
  return String(fallback || '').trim();
};

const matchAttendeeTicket = (
  attendees: Array<{ fullName: string }>,
  tickets: RegistrationTicketRow[],
  index: number,
) => {
  const unused = [...tickets].sort(compareTicketNumber);
  const claimed = new Set<string>();
  for (let attendeeIndex = 0; attendeeIndex < attendees.length; attendeeIndex += 1) {
    const attendee = attendees[attendeeIndex];
    const namedIndex = unused.findIndex(
      (ticket) =>
        !claimed.has(ticket.id) &&
        Boolean(normalizeTicketName(ticket.attendee_name)) &&
        normalizeTicketName(ticket.attendee_name) === normalizeTicketName(attendee.fullName),
    );
    const fallbackIndex = unused.findIndex((ticket) => !claimed.has(ticket.id));
    const matched = namedIndex >= 0 ? unused[namedIndex] : fallbackIndex >= 0 ? unused[fallbackIndex] : null;
    if (matched) claimed.add(matched.id);
    if (attendeeIndex === index) return matched;
  }
  return null;
};

const buildRegistrationDirectoryRows = (
  registration: PublicEventRegistration,
  tickets: RegistrationTicketRow[],
): RegistrantDirectoryRow[] => {
  const people = buildRegistrationAttendeePeople(registration, tickets.length);
  return people
    .map((person, index) => ({
      key: `${registration.id}:${index}`,
      registration,
      attendeeIndex: index,
      isPrimary: index === 0,
      fullName: person.fullName,
      email: person.email,
      phone: person.phone,
      ticket: matchAttendeeTicket(people, tickets, index),
    }))
    .filter((row) => Boolean(row.fullName || row.email || row.phone || row.ticket));
};

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
`;

const SendEmailPage = styled.div`
  max-width: 1120px;
  margin: 0 auto;
`;

const SendEmailHero = styled.header`
  margin-bottom: 1.35rem;
  padding: 1.25rem 1.35rem 1.3rem;
  border-radius: 20px;
  border: 1px solid color-mix(in srgb, var(--outline) 82%, transparent);
  background: linear-gradient(
    145deg,
    color-mix(in srgb, var(--primary) 10%, var(--surface)) 0%,
    var(--surface) 42%,
    color-mix(in srgb, var(--surfaceVariant) 55%, var(--surface)) 100%
  );
  box-shadow:
    0 18px 40px rgba(15, 23, 42, 0.07),
    0 0 0 1px color-mix(in srgb, var(--onSurface) 4%, transparent) inset;
`;

const SendEmailHeroTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 0.85rem;
`;

const SendEmailBackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.42rem 0.85rem;
  border-radius: 999px;
  font-size: 0.84rem;
  font-weight: 600;
  color: var(--onSurface);
  text-decoration: none;
  border: 1px solid color-mix(in srgb, var(--outline) 88%, transparent);
  background: color-mix(in srgb, var(--surface) 70%, transparent);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;
  &::before {
    content: '←';
    font-size: 0.95rem;
    opacity: 0.85;
  }
  &:hover {
    background: var(--surface);
    border-color: color-mix(in srgb, var(--primary) 35%, var(--outline));
    color: var(--primary);
  }
`;

const SendEmailEyebrow = styled.p`
  margin: 0 0 0.35rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: color-mix(in srgb, var(--primary) 55%, var(--onSurfaceVariant));
`;

const SendEmailTitle = styled.h1`
  margin: 0;
  font-size: clamp(1.35rem, 2.8vw, 1.85rem);
  font-weight: 800;
  letter-spacing: -0.035em;
  line-height: 1.2;
  color: var(--onSurface);
`;

const SendEmailMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const SendEmailMetaChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--onSurface);
  background: color-mix(in srgb, var(--primary) 12%, var(--surface));
  border: 1px solid color-mix(in srgb, var(--primary) 22%, transparent);
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: clamp(1.2rem, 4.2vw, 1.8rem);
  line-height: 1.25;
  color: var(--onSurface);
  word-break: break-word;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const SectionTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin: 0 0 1rem;
`;

const SectionTab = styled.button`
  padding: 0.42rem 0.78rem;
  border-radius: 999px;
  border: 1px solid var(--outline);
  background: var(--surface);
  cursor: pointer;
  font-weight: 700;
  font-size: 0.82rem;
  color: var(--onSurfaceVariant);

  &[data-active='true'] {
    background: var(--primary);
    color: var(--onPrimary);
    border-color: var(--primaryDark);
  }
`;

const RowActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const HelperText = styled.p`
  color: var(--onSurfaceVariant);
  margin: 0.5rem 0 1rem;
  font-size: 0.9rem;
`;

const PaginationRow = styled.div`
  margin-top: 0.7rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.65rem;
  flex-wrap: wrap;
`;

const PaginationSummary = styled.span`
  font-size: 0.83rem;
  color: var(--onSurfaceVariant);
`;

const PaginationActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
`;

const PaginationButton = styled.button`
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  border-radius: 12px;
  padding: 0.5rem 0.7rem;
  min-width: 92px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;
  &:hover:not(:disabled) {
    background: var(--surfaceVariant);
    border-color: color-mix(in srgb, var(--onSurface) 12%, var(--outline));
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const ToolbarSurface = styled.div`
  margin-bottom: 1rem;
  border-radius: 16px;
  border: 1px solid color-mix(in srgb, var(--outline) 85%, transparent);
  background: linear-gradient(
    165deg,
    color-mix(in srgb, var(--primary) 6%, var(--surface)) 0%,
    var(--surface) 48%,
    var(--surfaceVariant) 100%
  );
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
  padding: 1rem 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;

  @media (max-width: 640px) {
    padding: 0.85rem 0.75rem;
    border-radius: 14px;
  }
`;

const ToolbarTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.55rem;
  }
`;

const ToolbarTitle = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--onSurfaceVariant);
`;

const ToolbarMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const StatChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--onSurface);
  background: color-mix(in srgb, var(--primary) 14%, var(--surface));
  border: 1px solid color-mix(in srgb, var(--primary) 28%, transparent);
`;

const SearchRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 0.65rem;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 0.55rem;
  }
`;

const ToolbarActionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.65rem;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;

    & > button {
      width: 100%;
      min-height: 44px;
    }
  }
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 0.65rem;
  margin-bottom: 0.9rem;

  @media (max-width: 380px) {
    grid-template-columns: 1fr;
  }
`;

const MetricCard = styled.div`
  border: 1px solid color-mix(in srgb, var(--outline) 82%, transparent);
  border-radius: 14px;
  background: var(--surface);
  padding: 0.7rem 0.85rem;
`;

const MetricLabel = styled.div`
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--onSurfaceVariant);
`;

const MetricValue = styled.div`
  margin-top: 0.32rem;
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--onSurface);
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 220px;
  padding: 0.55rem 0.85rem;
  font-size: 0.9rem;
  border: 1px solid var(--outline);
  border-radius: 12px;
  background: var(--surface);
  color: var(--onSurface);
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.04);
  &::placeholder {
    color: var(--onSurfaceVariant);
    opacity: 0.85;
  }
  &:focus {
    outline: none;
    border-color: color-mix(in srgb, var(--primary) 55%, var(--outline));
    box-shadow:
      inset 0 1px 2px rgba(15, 23, 42, 0.04),
      0 0 0 3px color-mix(in srgb, var(--primary) 22%, transparent);
  }

  @media (max-width: 640px) {
    min-width: 0;
    width: 100%;
    font-size: 16px;
    min-height: 44px;
  }
`;

const SearchHint = styled.span`
  font-size: 0.85rem;
  color: var(--onSurfaceVariant);
`;

const ActionButton = styled.button`
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  border-radius: 12px;
  padding: 0.5rem 0.85rem;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    transform 0.12s ease;
  &:hover:not(:disabled) {
    background: var(--surfaceVariant);
    border-color: color-mix(in srgb, var(--onSurface) 12%, var(--outline));
  }
  &:active:not(:disabled) {
    transform: translateY(1px);
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const PrimaryActionButton = styled(ActionButton)`
  border-color: color-mix(in srgb, var(--primary) 45%, var(--outline));
  background: linear-gradient(
    180deg,
    color-mix(in srgb, var(--primary) 18%, var(--surface)) 0%,
    color-mix(in srgb, var(--primary) 10%, var(--surface)) 100%
  );
  box-shadow: 0 2px 8px color-mix(in srgb, var(--primary) 18%, transparent);
  &:hover:not(:disabled) {
    background: linear-gradient(
      180deg,
      color-mix(in srgb, var(--primary) 24%, var(--surface)) 0%,
      color-mix(in srgb, var(--primary) 14%, var(--surface)) 100%
    );
  }
`;

const DangerActionButton = styled(ActionButton)`
  color: var(--error);
  border-color: color-mix(in srgb, var(--error) 35%, var(--outline));
  &:hover:not(:disabled) {
    color: var(--error);
    border-color: color-mix(in srgb, var(--error) 55%, var(--outline));
    background: color-mix(in srgb, var(--error) 8%, var(--surface));
  }
`;

const ListCheckInButton = styled(PrimaryActionButton)`
  padding: 0.32rem 0.7rem;
  font-size: 0.78rem;
  white-space: nowrap;
`;

const AttendeeCheckInRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
`;

const AttendeeCheckInMeta = styled.div`
  min-width: 0;
  flex: 1;
`;

const Banner = styled.div<{ $kind: 'success' | 'error' | 'info' }>`
  margin: 0.5rem 0 0.9rem;
  border: 1px solid
    ${({ $kind }) => ($kind === 'error' ? 'var(--error)' : $kind === 'success' ? '#047857' : 'var(--outline)')};
  background: ${({ $kind }) =>
    $kind === 'error'
      ? 'color-mix(in srgb, var(--error) 12%, var(--surface))'
      : $kind === 'success'
        ? 'color-mix(in srgb, #047857 12%, var(--surface))'
        : 'var(--surfaceVariant)'};
  color: var(--onSurface);
  border-radius: 10px;
  padding: 0.6rem 0.75rem;
  font-size: 0.86rem;
`;

const SendEmailNotice = styled(Banner)`
  margin: 0 0 1rem;
  border-radius: 12px;
  padding: 0.75rem 0.95rem;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.05);
`;

const SelectionCheckbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

const TshirtGivenToggle = styled.button<{ $given: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 999px;
  border: 1px solid
    ${({ $given }) => ($given ? 'color-mix(in srgb, #047857 45%, var(--outline))' : 'var(--outline)')};
  background: ${({ $given }) =>
    $given ? 'color-mix(in srgb, #047857 16%, var(--surface))' : 'var(--surface)'};
  color: ${({ $given }) =>
    $given ? '#047857' : 'color-mix(in srgb, var(--onSurfaceVariant) 42%, transparent)'};
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;
  &:hover:not(:disabled) {
    border-color: ${({ $given }) =>
      $given ? 'color-mix(in srgb, #047857 65%, var(--outline))' : 'color-mix(in srgb, var(--primary) 45%, var(--outline))'};
    background: ${({ $given }) =>
      $given
        ? 'color-mix(in srgb, #047857 22%, var(--surface))'
        : 'color-mix(in srgb, var(--primary) 10%, var(--surface))'};
    color: ${({ $given }) => ($given ? '#047857' : 'var(--onSurfaceVariant)')};
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 640px) {
    width: 44px;
    height: 44px;
  }
`;

const TshirtGivenRow = styled.tr<{ $given?: boolean }>`
  ${({ $given }) =>
    $given
      ? `
    td {
      background: color-mix(in srgb, #047857 8%, var(--surface)) !important;
    }
  `
      : ''}
`;

const TshirtGivenLabel = styled.span`
  display: none;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--onSurfaceVariant);

  @media (max-width: 640px) {
    display: inline;
  }
`;

const TshirtGivenCell = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;

  @media (max-width: 640px) {
    justify-content: flex-end;
  }
`;

const HistoryCard = styled.div`
  margin: 1.75rem 0 1rem;
  border: 1px solid color-mix(in srgb, var(--outline) 88%, transparent);
  border-radius: 16px;
  background: var(--surface);
  overflow: hidden;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.05);
`;

const HistoryHeader = styled.div<{ $collapsed?: boolean }>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border-bottom: ${({ $collapsed }) => ($collapsed ? 'none' : '1px solid var(--outline)')};
  background: linear-gradient(180deg, var(--surfaceVariant) 0%, var(--surface) 100%);
`;

const HistoryCollapseButton = styled.button`
  flex-shrink: 0;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  border-radius: 999px;
  padding: 0.35rem 0.85rem;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    border-color: color-mix(in srgb, var(--primary) 35%, var(--outline));
    color: var(--primary);
  }
`;

const HistoryTitle = styled.h3`
  margin: 0;
  font-size: 0.98rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--onSurface);
`;

const HistoryChannelHeading = styled.div`
  padding: 0.6rem 1rem;
  border-bottom: 1px solid var(--outline);
  background: color-mix(in srgb, var(--surfaceVariant) 55%, var(--surface));
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--onSurfaceVariant);
`;

const HistoryList = styled.div`
  display: grid;
`;

const HistoryRow = styled.div`
  padding: 0.85rem 1rem;
  border-bottom: 1px solid var(--outline);
  transition: background 0.12s ease;
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: color-mix(in srgb, var(--surfaceVariant) 40%, var(--surface));
  }
`;

const HistoryMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem 0.7rem;
  margin-top: 0.25rem;
  font-size: 0.82rem;
  color: var(--onSurfaceVariant);
`;

const HistoryToggleButton = styled.button`
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  border-radius: 999px;
  padding: 0.28rem 0.65rem;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    border-color: color-mix(in srgb, var(--primary) 35%, var(--outline));
    color: var(--primary);
  }
`;

const HistoryDangerButton = styled(HistoryToggleButton)`
  color: var(--error);
  border-color: color-mix(in srgb, var(--error) 35%, var(--outline));
  &:hover {
    color: var(--error);
    border-color: color-mix(in srgb, var(--error) 55%, var(--outline));
  }
`;

const SendModeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.12rem 0.45rem;
  border-radius: 6px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: var(--surfaceVariant);
  color: var(--onSurfaceVariant);
`;

const StatusPill = styled.span<{ $tone: 'ok' | 'warn' | 'bad' | 'muted' }>`
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  ${({ $tone }) =>
    $tone === 'bad'
      ? `background: color-mix(in srgb, var(--error) 14%, var(--surface)); color: var(--error);`
      : $tone === 'warn'
        ? `background: color-mix(in srgb, #92400e 14%, var(--surface)); color: #92400e;`
        : $tone === 'ok'
          ? `background: color-mix(in srgb, #047857 14%, var(--surface)); color: #047857;`
          : `background: var(--surfaceVariant); color: var(--onSurfaceVariant);`}
`;

const FailureList = styled.div`
  margin-top: 0.45rem;
  border: 1px solid var(--outline);
  border-radius: 10px;
  background: color-mix(in srgb, var(--error) 6%, var(--surface));
`;

const FailureItem = styled.div`
  padding: 0.45rem 0.6rem;
  border-bottom: 1px solid var(--outline);
  font-size: 0.8rem;
  color: var(--onSurfaceVariant);
  &:last-child {
    border-bottom: none;
  }
`;

const FilterSelect = styled.select`
  min-width: 148px;
  padding: 0.55rem 0.75rem;
  font-size: 0.86rem;
  border: 1px solid var(--outline);
  border-radius: 12px;
  background: var(--surface);
  color: var(--onSurface);
  cursor: pointer;
  &:focus {
    outline: none;
    border-color: color-mix(in srgb, var(--primary) 45%, var(--outline));
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 18%, transparent);
  }

  @media (max-width: 640px) {
    width: 100%;
    min-width: 0;
    min-height: 44px;
    font-size: 16px;
  }
`;

const ToggleFilterLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.55rem 0.75rem;
  border: 1px solid var(--outline);
  border-radius: 12px;
  background: var(--surface);
  color: var(--onSurface);
  font-size: 0.84rem;
  font-weight: 600;
  white-space: nowrap;

  input {
    width: 16px;
    height: 16px;
    margin: 0;
  }

  @media (max-width: 640px) {
    width: 100%;
    min-height: 44px;
  }
`;

const TableCard = styled.div`
  background: var(--surface);
  border: 1px solid color-mix(in srgb, var(--outline) 88%, transparent);
  border-radius: 16px;
  box-shadow: 0 14px 32px rgba(17, 24, 39, 0.07);
  overflow: hidden;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    padding: 0.78rem 1rem;
    border-bottom: 1px solid color-mix(in srgb, var(--outline) 70%, transparent);
    text-align: left;
    font-size: 0.9rem;
    color: var(--onSurfaceVariant);
    vertical-align: middle;
  }
  th {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--onSurfaceVariant);
    background: linear-gradient(180deg, var(--surfaceVariant) 0%, color-mix(in srgb, var(--surfaceVariant) 88%, var(--surface)) 100%);
    position: sticky;
    top: 0;
    z-index: 1;
    box-shadow: 0 1px 0 color-mix(in srgb, var(--outline) 70%, transparent);
  }
  tbody tr:nth-child(even) td {
    background: color-mix(in srgb, var(--surfaceVariant) 22%, var(--surface));
  }
  @media (max-width: 768px) {
    th,
    td {
      padding: 0.55rem 0.75rem;
      font-size: 0.85rem;
    }
  }

  @media (max-width: 640px) {
    thead {
      display: none;
    }

    tbody tr {
      display: block;
      border: 1px solid color-mix(in srgb, var(--outline) 82%, transparent);
      border-radius: 14px;
      margin-bottom: 0.65rem;
      overflow: hidden;
      background: var(--surface);
    }

    tbody tr:nth-child(even) td {
      background: transparent;
    }

    tbody td {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 0.65rem;
      border-bottom: 1px solid color-mix(in srgb, var(--outline) 70%, transparent);
      text-align: right;
      padding: 0.55rem 0.75rem;
      font-size: 0.86rem;
    }

    tbody tr td:last-child {
      border-bottom: none;
    }

    tbody td::before {
      content: attr(data-label);
      font-weight: 700;
      font-size: 0.65rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--onSurfaceVariant);
      text-align: left;
      flex: 0 0 auto;
      max-width: 40%;
      padding-top: 0.1rem;
    }
  }
`;

const RegistrantTdValue = styled.div`
  flex: 1;
  min-width: 0;
  text-align: right;

  @media (min-width: 641px) {
    text-align: inherit;
  }
`;

const NotesCell = styled.td`
  max-width: 240px;
  white-space: pre-wrap;

  @media (max-width: 640px) {
    max-width: none;
  }
`;

const ClickableRow = styled.tr`
  cursor: pointer;
  transition: background 0.12s ease;
  &:hover td {
    background: color-mix(in srgb, var(--primary) 7%, var(--surface)) !important;
  }
  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: -2px;
  }
`;

const RegistrantName = styled.div`
  font-weight: 600;
  color: var(--onSurface);
  letter-spacing: -0.01em;
`;

const AdditionalAttendees = styled.div`
  margin-top: 0.18rem;
  font-size: 0.78rem;
  font-weight: 500;
  color: var(--onSurfaceVariant);
  line-height: 1.35;
`;

const ContactBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  font-size: 0.88rem;
`;

const ContactMuted = styled.span`
  font-size: 0.8rem;
  color: var(--onSurfaceVariant);
`;

const TableStatusBadge = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  padding: 0.22rem 0.55rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: capitalize;
  color: ${({ $status }) => statusColor($status)};
  background: ${({ $status }) =>
    `color-mix(in srgb, ${statusColor($status)} 16%, var(--surface))`};
  border: 1px solid ${({ $status }) => `color-mix(in srgb, ${statusColor($status)} 35%, transparent)`};
`;

const ActionLink = styled(Link)`
  color: var(--primary);
  text-decoration: none;
  font-weight: 600;
  &:hover {
    text-decoration: underline;
  }
`;

const BackLink = styled(Link)`
  color: var(--primary);
  text-decoration: none;
  font-size: 0.92rem;
  font-weight: 600;
  &:hover {
    text-decoration: underline;
  }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: color-mix(in srgb, rgb(15, 23, 42) 52%, transparent);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  display: grid;
  place-items: center;
  z-index: 1200;
  padding: 1rem;

  @media (max-width: 640px) {
    padding: 0;
    place-items: end center;
  }
`;

const ModalAccent = styled.div`
  height: 3px;
  width: 100%;
  flex-shrink: 0;
  background: linear-gradient(
    90deg,
    color-mix(in srgb, var(--primary) 82%, transparent) 0%,
    color-mix(in srgb, var(--primary) 42%, transparent) 48%,
    color-mix(in srgb, var(--primary) 10%, transparent) 100%
  );
`;

const Modal = styled.div`
  width: min(720px, 100%);
  max-height: calc(100vh - 2rem);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--surface);
  border: 1px solid color-mix(in srgb, var(--outline) 78%, transparent);
  border-radius: 20px;
  box-shadow:
    0 0 0 1px color-mix(in srgb, white 6%, transparent),
    0 28px 56px rgba(15, 23, 42, 0.14),
    0 10px 24px rgba(15, 23, 42, 0.06);

  @media (max-width: 640px) {
    max-height: min(92dvh, calc(100vh - 1rem));
    border-radius: 20px 20px 0 0;
    width: 100%;
    max-width: 100%;
  }
`;

const ModalScrollBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 1.15rem 1.35rem 1.45rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  position: relative;
  z-index: 1;

  @media (max-width: 640px) {
    padding: 1rem 1.1rem calc(1.25rem + env(safe-area-inset-bottom, 0px));
    gap: 0.9rem;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.85rem;
  flex-shrink: 0;
  padding: 1.1rem 1.35rem 1rem;
  border-bottom: 1px solid color-mix(in srgb, var(--outline) 52%, transparent);
  background: color-mix(in srgb, var(--surfaceVariant) 38%, var(--surface));
  position: relative;
  z-index: 2;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
    padding: 1rem 1.1rem 0.95rem;
  }
`;

const ModalHeaderActions = styled.div`
  display: flex;
  gap: 0.45rem;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;

  @media (max-width: 640px) {
    flex-direction: column;
    width: 100%;

    & > button {
      width: 100%;
      min-height: 44px;
    }
  }
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.22rem;
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.25;
  color: var(--onSurface);
`;

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.35rem;
  padding: 0 0.85rem;
  border: 1px solid color-mix(in srgb, var(--outline) 88%, transparent);
  border-radius: 12px;
  background: var(--surface);
  color: var(--onSurfaceVariant);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;
  &:hover {
    background: color-mix(in srgb, var(--surfaceVariant) 75%, var(--surface));
    border-color: color-mix(in srgb, var(--primary) 32%, var(--outline));
    color: var(--onSurface);
  }
  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
  }
`;

const ModalHeaderToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.45rem;
  flex-shrink: 0;

  @media (max-width: 640px) {
    width: 100%;
  }
`;

const ModalMenuWrap = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const ModalEllipsisButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.35rem;
  height: 2.35rem;
  padding: 0;
  border: 1px solid color-mix(in srgb, var(--outline) 88%, transparent);
  border-radius: 12px;
  background: var(--surface);
  color: var(--onSurfaceVariant);
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;
  &:hover {
    background: color-mix(in srgb, var(--surfaceVariant) 75%, var(--surface));
    border-color: color-mix(in srgb, var(--primary) 32%, var(--outline));
    color: var(--onSurface);
  }
  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
  }
  svg {
    width: 1.2rem;
    height: 1.2rem;
  }
`;

const ModalMenuList = styled.div`
  position: absolute;
  top: calc(100% + 0.35rem);
  right: 0;
  min-width: 220px;
  z-index: 10;
  background: var(--surface);
  border: 1px solid color-mix(in srgb, var(--outline) 70%, transparent);
  border-radius: 12px;
  box-shadow: 0 14px 32px rgba(15, 23, 42, 0.16);
  padding: 0.35rem;
`;

const ModalMenuDivider = styled.div`
  height: 1px;
  margin: 0.3rem 0.35rem;
  background: color-mix(in srgb, var(--outline) 65%, transparent);
`;

const ModalMenuItem = styled.button`
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  padding: 0.55rem 0.65rem;
  border-radius: 8px;
  color: var(--onSurface);
  cursor: pointer;
  font-weight: 600;
  font-size: 0.84rem;
  line-height: 1.35;
  transition: background 0.12s ease;

  &[data-variant='danger'] {
    color: var(--error);
  }

  &:hover:not(:disabled) {
    background: color-mix(in srgb, var(--surfaceVariant) 88%, var(--surface));
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const ModalInfoCard = styled.div`
  border-radius: 14px;
  padding: 0.95rem 1.05rem;
  background: color-mix(in srgb, var(--surfaceVariant) 55%, var(--surface));
  border: 1px solid color-mix(in srgb, var(--outline) 62%, transparent);
  box-shadow: inset 0 1px 0 color-mix(in srgb, white 35%, transparent);
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const ModalLead = styled.p`
  margin: 0;
  font-size: 1.06rem;
  font-weight: 600;
  letter-spacing: -0.025em;
  line-height: 1.35;
  color: var(--onSurface);
`;

const ModalFieldBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
`;

const ModalTextarea = styled.textarea`
  width: 100%;
  margin: 0;
  box-sizing: border-box;
  min-height: 5rem;
  border-radius: 12px;
  border: 1px solid color-mix(in srgb, var(--outline) 85%, transparent);
  padding: 0.65rem 0.8rem;
  background: var(--surface);
  color: var(--onSurface);
  font-size: 0.9rem;
  line-height: 1.45;
  font-family: inherit;
  resize: vertical;
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.04);
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
  &::placeholder {
    color: var(--onSurfaceVariant);
    opacity: 0.85;
  }
  &:focus {
    outline: none;
    border-color: color-mix(in srgb, var(--primary) 50%, var(--outline));
    box-shadow:
      inset 0 1px 2px rgba(15, 23, 42, 0.04),
      0 0 0 3px color-mix(in srgb, var(--primary) 20%, transparent);
  }
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--onSurfaceVariant);
`;

const DetailText = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  font-size: 0.9rem;
  line-height: 1.55;
`;

const NamesList = styled.ol`
  margin: 0;
  padding: 0.65rem 0.85rem 0.75rem 1.45rem;
  list-style-position: outside;
  color: var(--onSurfaceVariant);
  font-size: 0.9rem;
  line-height: 1.5;
  border-radius: 12px;
  background: color-mix(in srgb, var(--surfaceVariant) 40%, var(--surface));
  border: 1px solid color-mix(in srgb, var(--outline) 55%, transparent);
  & li {
    margin-bottom: 0.7rem;
    padding-left: 0.15rem;
  }
  & li:last-child {
    margin-bottom: 0;
  }
`;

const ScannerSection = styled.div`
  margin: 1rem 0;
  padding: 1rem 1.1rem;
  border: 1px solid color-mix(in srgb, var(--outline) 88%, transparent);
  border-radius: 16px;
  background: linear-gradient(145deg, var(--surface) 0%, var(--surfaceVariant) 100%);
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.05);

  @media (max-width: 640px) {
    padding: 0.85rem 0.75rem;
  }
`;

const ScannerHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 0.65rem;
`;

const ScannerTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--onSurface);
  letter-spacing: -0.02em;
`;

const ScannerHint = styled.p`
  margin: 0;
  font-size: 0.82rem;
  color: var(--onSurfaceVariant);
  line-height: 1.45;
  max-width: 52ch;
`;

const ScannerControls = styled.div`
  display: flex;
  gap: 0.55rem;
  flex-wrap: wrap;
  margin-top: 0.65rem;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;

    & > button {
      width: 100%;
      min-height: 44px;
    }
  }
`;

const ScannerButton = styled.button`
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  border-radius: 12px;
  padding: 0.48rem 0.9rem;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;
  &:hover:not(:disabled) {
    background: var(--surfaceVariant);
    border-color: color-mix(in srgb, var(--primary) 30%, var(--outline));
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const ScannerInput = styled.input`
  width: 100%;
  margin-top: 0.35rem;
  padding: 0.62rem 0.85rem;
  border-radius: 12px;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  font-size: 0.9rem;
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.04);
  &:focus {
    outline: none;
    border-color: color-mix(in srgb, var(--primary) 45%, var(--outline));
    box-shadow:
      inset 0 1px 2px rgba(15, 23, 42, 0.04),
      0 0 0 3px color-mix(in srgb, var(--primary) 18%, transparent);
  }
`;

const ScannerVideo = styled.video`
  margin-top: 0.75rem;
  width: min(460px, 100%);
  border-radius: 12px;
  border: 1px solid var(--outline);
  background: #000;
`;

const formatEventSchedule = (event?: { start_date: string | null; start_time: string | null } | null) => {
  if (!event?.start_date) return 'Date TBD';
  return `${event.start_date}${event.start_time ? ` ${event.start_time}` : ''}`;
};

const formatUsdCents = (amountCents?: number | null) => {
  const value = Number(amountCents);
  if (!Number.isFinite(value)) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value / 100);
};

const normalizeRegistrationAppointments = (value: unknown): PublicEventRegistrationAppointment[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const record = row as Record<string, unknown>;
      const slot = record.event_appointment_slots as Record<string, unknown> | null | undefined;
      const slotId = String(record.slot_id || slot?.id || '').trim();
      if (!slotId) return null;
      return {
        slot_id: slotId,
        label: String(slot?.label || '').trim() || 'Appointment',
        starts_at: slot?.starts_at == null ? null : String(slot.starts_at),
        ends_at: slot?.ends_at == null ? null : String(slot.ends_at),
      };
    })
    .filter((row): row is PublicEventRegistrationAppointment => Boolean(row));
};

const formatAppointmentWindow = (startsAt?: string | null, endsAt?: string | null) => {
  if (!startsAt || !endsAt) return '';
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '';
  const sameDay = start.toDateString() === end.toDateString();
  const dateLabel = start.toLocaleDateString([], { month: 'short', day: 'numeric' });
  const startLabel = start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const endLabel = end.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (sameDay) return `${dateLabel}, ${startLabel} - ${endLabel}`;
  const endDateLabel = end.toLocaleDateString([], { month: 'short', day: 'numeric' });
  return `${dateLabel}, ${startLabel} - ${endDateLabel}, ${endLabel}`;
};

const formatAppointmentSummary = (
  appointments: Array<{ label?: string | null; starts_at?: string | null; ends_at?: string | null }>
) =>
  appointments
    .map((appointment) => {
      const windowLabel = formatAppointmentWindow(appointment.starts_at, appointment.ends_at);
      return windowLabel ? `${appointment.label} (${windowLabel})` : appointment.label || '';
    })
    .filter(Boolean)
    .join('; ');

const mapRegistrationRow = (row: any): PublicEventRegistration => {
  const eventRel = Array.isArray(row.events) ? row.events[0] : row.events;
  const rawFormResponse = row.form_response;
  return {
    id: row.id,
    event_id: row.event_id,
    user_id: row.user_id ?? null,
    full_name: row.full_name,
    email: row.email,
    phone: row.phone,
    quantity: Number(row.quantity || 1),
    ticket_type_id: row.ticket_type_id ?? null,
    ticket_type_name: row.ticket_type_name,
    status: row.status,
    payment_status: row.payment_status ?? null,
    source: row.source,
    notes: row.notes,
    total_amount_cents:
      row.total_amount_cents === null || row.total_amount_cents === undefined
        ? null
        : Number(row.total_amount_cents),
    amount_paid_cents:
      row.amount_paid_cents === null || row.amount_paid_cents === undefined
        ? null
        : Number(row.amount_paid_cents),
    balance_due_cents:
      row.balance_due_cents === null || row.balance_due_cents === undefined
        ? null
        : Number(row.balance_due_cents),
    payment_amount_cents:
      row.payment_amount_cents === null || row.payment_amount_cents === undefined
        ? null
        : Number(row.payment_amount_cents),
    form_response: rawFormResponse && typeof rawFormResponse === 'object' ? rawFormResponse : {},
    created_at: row.created_at,
    appointments: normalizeRegistrationAppointments(row.appointment_links ?? row.public_event_registration_appointments),
    events: eventRel
      ? {
          id: eventRel.id,
          title: eventRel.title,
          start_date: eventRel.start_date ?? null,
          start_time: eventRel.start_time ?? null,
          location: eventRel.location ?? null,
        }
      : null,
  };
};

const toCsvValue = (value: unknown) => {
  const text = String(value ?? '');
  if (!/[",\n]/.test(text)) return text;
  return `"${text.replace(/"/g, '""')}"`;
};

const extractGroupJoinPasscode = (formResponse: unknown) => {
  if (!formResponse || typeof formResponse !== 'object' || Array.isArray(formResponse)) return '';
  const record = formResponse as Record<string, unknown>;
  const groupJoin = record.group_join;
  if (!groupJoin || typeof groupJoin !== 'object' || Array.isArray(groupJoin)) return '';
  return String((groupJoin as Record<string, unknown>).passcode || '').trim();
};

const pickString = (record: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = String(record[key] || '').trim();
    if (value) return value;
  }
  return '';
};

const formatCustomResponseLabel = (fieldKey: string) =>
  fieldKey
    .split(/[_-]+/)
    .map((segment) => segment.trim())
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');

const formatCustomResponseValue = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return formatCheckboxResponse(value);
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) {
    const looksLikeAttendeeResponses =
      value.length > 0 &&
      value.every(
        (entry) =>
          entry &&
          typeof entry === 'object' &&
          !Array.isArray(entry) &&
          ('attendee_index' in (entry as Record<string, unknown>) || 'responses' in (entry as Record<string, unknown>))
      );
    if (looksLikeAttendeeResponses) {
      const lines = value
        .map((entry) => entry as Record<string, unknown>)
        .map((entry, index) => {
          const attendeeName = String(entry.attendee_name || '').trim();
          const attendeeLabel = attendeeName || `Attendee ${Number(entry.attendee_index ?? index) + 1}`;
          const responsesRaw = entry.responses;
          const responses =
            responsesRaw && typeof responsesRaw === 'object' && !Array.isArray(responsesRaw)
              ? (responsesRaw as Record<string, unknown>)
              : {};
          const details = Object.entries(responses)
            .map(([key, responseValue]) => {
              const responseText = formatCustomResponseValue(responseValue);
              if (!responseText) return '';
              return `${formatCustomResponseLabel(key)}=${responseText}`;
            })
            .filter(Boolean)
            .join(', ');
          return details ? `${attendeeLabel}: ${details}` : `${attendeeLabel}: No answers`;
        })
        .filter(Boolean);
      return lines.join(' | ');
    }

    const normalized = value
      .map((item) => formatCustomResponseValue(item))
      .map((item) => item.trim())
      .filter(Boolean);
    return normalized.join(', ');
  }
  if (value && typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return '';
    }
  }
  return String(value || '').trim();
};

const getFormResponseRecord = (formResponse: unknown) =>
  formResponse && typeof formResponse === 'object' && !Array.isArray(formResponse)
    ? (formResponse as Record<string, unknown>)
    : {};

const isStructuralFormResponseKey = (key: string) => {
  const normalized = String(key || '').trim().toLowerCase();
  if (normalized.startsWith('_')) return true;
  return STRUCTURAL_FORM_RESPONSE_KEYS.has(normalized);
};

const isCollectPerAttendeeField = (field: Pick<EventOrderFormFieldRow, 'validation'>) =>
  Boolean(
    field.validation &&
      typeof field.validation === 'object' &&
      !Array.isArray(field.validation) &&
      (field.validation as Record<string, unknown>).collect_per_attendee
  );

const extractFunctionErrorMessage = async (error: unknown, fallback: string) => {
  const genericMessage =
    error && typeof error === 'object' && 'message' in error
      ? String((error as { message?: string }).message || '')
      : '';
  const context = error && typeof error === 'object' ? (error as { context?: Response }).context : undefined;
  if (context && typeof context.text === 'function') {
    try {
      const raw = await context.text();
      if (raw) {
        try {
          const parsed = JSON.parse(raw) as { error?: string; message?: string };
          if (parsed?.error) return parsed.error;
          if (parsed?.message) return parsed.message;
        } catch {
          return raw;
        }
      }
    } catch {
      // ignore context parsing failures
    }
  }
  if (genericMessage) return genericMessage;
  return fallback;
};

export const PublicEventRegistrationsPage: React.FC = () => {
  const { user } = useAuth();
  const { data: profile } = useCurrentUserProfile(user?.id);
  const { data: assignedEventIds = [] } = useMyEventAdminEventIds(user?.id);
  const roles = profile?.roles ?? user?.user_metadata?.roles;
  const canManageAllEvents = hasPermission(roles, Permission.MANAGE_EVENTS);
  const { data, isLoading, error } = useQuery({
    queryKey: ['public-event-registrations-events', assignedEventIds],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('public_event_registrations')
        .select('id, event_id, quantity, created_at, events:event_id(id, title, start_date, start_time, location)')
        .order('created_at', { ascending: false })
        .limit(2000);

      if (error) {
        throw new Error(error.message);
      }

      const rows = (data || []) as any[];
      const grouped = new Map<string, PublicEventSummary>();

      rows.forEach((row) => {
        const eventRel = Array.isArray(row.events) ? row.events[0] : row.events;
        if (!eventRel?.id) return;
        const eventId = String(eventRel.id);
        const quantity = Number(row.quantity || 1);
        const createdAt = row.created_at ? String(row.created_at) : null;
        const existing = grouped.get(eventId);

        if (!existing) {
          grouped.set(eventId, {
            event_id: eventId,
            title: eventRel.title ?? 'Untitled event',
            start_date: eventRel.start_date ?? null,
            start_time: eventRel.start_time ?? null,
            location: eventRel.location ?? null,
            registration_count: 1,
            total_quantity: quantity,
            last_submitted_at: createdAt,
          });
          return;
        }

        existing.registration_count += 1;
        existing.total_quantity += quantity;
        if (createdAt && (!existing.last_submitted_at || createdAt > existing.last_submitted_at)) {
          existing.last_submitted_at = createdAt;
        }
      });

      const missingAssignedIds = assignedEventIds.filter((eventId) => !grouped.has(eventId));
      if (missingAssignedIds.length > 0) {
        const { data: assignedEvents, error: assignedError } = await supabase
          .from('events')
          .select('id, title, start_date, start_time, location')
          .in('id', missingAssignedIds);
        if (assignedError) throw new Error(assignedError.message);
        for (const eventRow of assignedEvents || []) {
          const eventId = String((eventRow as { id?: string }).id || '');
          if (!eventId || grouped.has(eventId)) continue;
          grouped.set(eventId, {
            event_id: eventId,
            title: (eventRow as { title?: string | null }).title ?? 'Untitled event',
            start_date: (eventRow as { start_date?: string | null }).start_date ?? null,
            start_time: (eventRow as { start_time?: string | null }).start_time ?? null,
            location: (eventRow as { location?: string | null }).location ?? null,
            registration_count: 0,
            total_quantity: 0,
            last_submitted_at: null,
          });
        }
      }

      return Array.from(grouped.values()).sort((a, b) => {
        if (!a.last_submitted_at && !b.last_submitted_at) return 0;
        if (!a.last_submitted_at) return 1;
        if (!b.last_submitted_at) return -1;
        return a.last_submitted_at < b.last_submitted_at ? 1 : -1;
      });
    },
  });

  return (
    <Page>
      <HeaderTitle>Public Event Registrations</HeaderTitle>
      <HelperText>
        Select an event to view registrations from both the public website and mobile app.
      </HelperText>

      {isLoading ? <HelperText>Loading…</HelperText> : null}
      {error ? (
        <HelperText style={{ color: 'var(--error)' }}>
          Could not load registrations.
        </HelperText>
      ) : null}

      {!isLoading && !error && (data?.length ?? 0) === 0 ? (
        <HelperText>No registrations yet.</HelperText>
      ) : null}

      {!isLoading && !error && (data?.length ?? 0) > 0 ? (
        <TableCard>
          <TableWrap>
            <Table>
              <thead>
              <tr>
                <th>Event</th>
                <th>Date / Time</th>
                <th>Location</th>
                <th>Registrations</th>
                <th>Tickets</th>
                <th>Last Submitted</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data?.map((row) => {
                const eventPath = canManageAllEvents
                  ? `/events/setup/${row.event_id}`
                  : `/events/public-registrations/${row.event_id}`;
                const registrantsPath = canManageAllEvents
                  ? `/events/setup/${row.event_id}?tab=registrants`
                  : `/events/public-registrations/${row.event_id}`;
                const waiverPath = canManageAllEvents
                  ? `/events/setup/${row.event_id}?tab=waiver-list`
                  : `/events/public-registrations/${row.event_id}?view=waivers`;
                return (
                <tr key={row.event_id}>
                  <td>
                    <ActionLink to={eventPath}>{row.title}</ActionLink>
                  </td>
                  <td>{formatEventSchedule({ start_date: row.start_date, start_time: row.start_time })}</td>
                  <td>{row.location ?? '—'}</td>
                  <td>{row.registration_count}</td>
                  <td>{row.total_quantity}</td>
                  <td>
                    {row.last_submitted_at ? new Date(row.last_submitted_at).toLocaleString() : '—'}
                  </td>
                  <td>
                    <RowActions>
                      <ActionLink to={registrantsPath}>View Registrants</ActionLink>
                      <ActionLink to={waiverPath}>Waiver List</ActionLink>
                    </RowActions>
                  </td>
                </tr>
                );
              })}
            </tbody>
            </Table>
          </TableWrap>
        </TableCard>
      ) : null}
    </Page>
  );
};

const filterRegistrationsByTicketStatusAndAppointment = (
  list: PublicEventRegistration[],
  ticketType: string,
  status: string,
  appointmentSlotId: string
): PublicEventRegistration[] => {
  return list.filter((row) => {
    const normalizedTicketType = (row.ticket_type_name ?? 'General').trim();
    const normalizedStatus = (row.status ?? '').trim().toLowerCase();
    const matchesTicketType = ticketType === 'all' || normalizedTicketType === ticketType;
    const matchesStatus = status === 'all' || normalizedStatus === status;
    const matchesAppointment =
      appointmentSlotId === 'all' || row.appointments.some((appointment) => appointment.slot_id === appointmentSlotId);
    return matchesTicketType && matchesStatus && matchesAppointment;
  });
};

const isPendingPaymentRegistration = (row: Pick<PublicEventRegistration, 'status' | 'payment_status'>) => {
  const registrationStatus = String(row.status || '').trim().toLowerCase();
  const paymentStatus = String(row.payment_status || '').trim().toLowerCase();
  return registrationStatus === 'pending_payment' || paymentStatus === 'awaiting_order';
};

const normalizeResponseKey = (value: string) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const appendUniqueNormalizedKeys = (target: string[], seen: Set<string>, keys: readonly string[]) => {
  for (const rawKey of keys) {
    const key = String(rawKey || '').trim();
    const normalizedKey = normalizeResponseKey(key);
    if (!normalizedKey || seen.has(normalizedKey)) continue;
    seen.add(normalizedKey);
    target.push(key);
  }
};

const getValueByNormalizedKey = (record: Record<string, unknown>, exportKey: string) => {
  if (Object.prototype.hasOwnProperty.call(record, exportKey)) return record[exportKey];
  const normalizedExportKey = normalizeResponseKey(exportKey);
  if (!normalizedExportKey) return undefined;
  for (const [candidateKey, value] of Object.entries(record)) {
    if (normalizeResponseKey(candidateKey) === normalizedExportKey) return value;
  }
  return undefined;
};

const looksLikeTshirtFieldLabel = (value: string) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return false;
  const hasSize = normalized.includes('size');
  const hasShirt = normalized.includes('shirt') || normalized.includes('t-shirt') || normalized.includes('tee');
  return hasSize && hasShirt;
};

const formatTshirtSize = (value: unknown) => {
  const raw = formatCustomResponseValue(value);
  if (!raw) return '';
  return raw
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();
};

const isLikelyTshirtSizeValue = (value: string) => {
  const normalized = String(value || '').trim().toUpperCase();
  if (!normalized) return false;
  const compact = normalized.replace(/\s+/g, '');
  const known = new Set([
    'XXS',
    'XS',
    'S',
    'SMALL',
    'M',
    'MEDIUM',
    'L',
    'LARGE',
    'XL',
    'X-LARGE',
    'EXTRALARGE',
    'XXL',
    '2XL',
    'XXXL',
    '3XL',
    '4XL',
    '5XL',
  ]);
  if (known.has(normalized) || known.has(compact)) return true;
  return /^([2-9]X?L|X{1,4}L)$/i.test(compact);
};

const TSHIRT_SIZE_SORT_ORDER = [
  'XXS',
  'XS',
  'S',
  'SMALL',
  'M',
  'MEDIUM',
  'L',
  'LARGE',
  'XL',
  'X-LARGE',
  'EXTRALARGE',
  'XXL',
  '2XL',
  'XXXL',
  '3XL',
  '4XL',
  '5XL',
];

// Ranks recognized sizes smallest-to-largest instead of alphabetically (e.g. "S" before "M" before "L").
const compareTshirtSizes = (a: string, b: string) => {
  const rankA = TSHIRT_SIZE_SORT_ORDER.indexOf(a);
  const rankB = TSHIRT_SIZE_SORT_ORDER.indexOf(b);
  if (rankA !== -1 && rankB !== -1) return rankA - rankB;
  if (rankA !== -1) return -1;
  if (rankB !== -1) return 1;
  return a.localeCompare(b);
};

const TSHIRT_GIVEN_STORAGE_PREFIX = 'ncof:event-tshirt-given:';

const getTshirtGivenStorageKey = (eventId: string) => `${TSHIRT_GIVEN_STORAGE_PREFIX}${eventId}`;

const readGivenTshirtKeysFromStorage = (eventId: string): string[] => {
  try {
    const raw = window.localStorage.getItem(getTshirtGivenStorageKey(eventId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((value): value is string => typeof value === 'string' && Boolean(value.trim()))
      : [];
  } catch {
    return [];
  }
};

const writeGivenTshirtKeysToStorage = (eventId: string, keys: string[]) => {
  try {
    window.localStorage.setItem(getTshirtGivenStorageKey(eventId), JSON.stringify(keys));
  } catch {
    // Ignore quota / private-mode failures; the in-memory query cache still works.
  }
};

const isMissingTshirtHandoutsTableError = (error: unknown) => {
  const message =
    error && typeof error === 'object' && 'message' in error
      ? String((error as { message?: unknown }).message || '')
      : String(error || '');
  return /event_tshirt_handouts/i.test(message) && /does not exist|schema cache|could not find the table/i.test(message);
};

type TshirtAttendeeRow = {
  key: string;
  registrationId: string | null;
  registrantName: string;
  attendeeName: string;
  email: string;
  phone: string;
  size: string;
  normalizedSize: string;
};

type PublicEventRegistrationsEventPageProps = {
  eventId?: string;
  showBackLink?: boolean;
  embedded?: boolean;
  emailComposerStandalone?: boolean;
  smsComposerStandalone?: boolean;
  communicationHistoryOnly?: boolean;
  tshirtsOnly?: boolean;
};

export const PublicEventRegistrationsEventPage: React.FC<PublicEventRegistrationsEventPageProps> = ({
  eventId: eventIdProp,
  showBackLink = true,
  embedded = false,
  emailComposerStandalone = false,
  smsComposerStandalone = false,
  communicationHistoryOnly = false,
  tshirtsOnly = false,
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { eventId: routeEventId = '' } = useParams();
  const eventId = eventIdProp ?? routeEventId;
  const queryParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const routeMode = queryParams.get('mode') === 'single' ? 'single' : 'bulk';
  const routeSingleRecipientId = (queryParams.get('registrationId') || '').trim() || null;
  const routeRecipientScope = (() => {
    const rawValue = (queryParams.get('scope') || '').trim();
    if (rawValue === 'all' || rawValue === 'filtered' || rawValue === 'selected') return rawValue;
    return 'filtered';
  })() as ComposerRecipientScope;
  const routeSelectedRecipientIds = React.useMemo(
    () =>
      (queryParams.get('selected') || '')
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean),
    [queryParams]
  );
  const routeReturnTo = (queryParams.get('returnTo') || '').trim();
  const { user } = useAuth();
  const { data: profile, isLoading: isLoadingProfile } = useCurrentUserProfile(user?.id);
  const { data: assignedEventIds = [], isLoading: isLoadingAssignedEvents } = useMyEventAdminEventIds(user?.id);
  const roles = profile?.roles ?? user?.user_metadata?.roles;
  const canManageAllEvents = hasPermission(roles, Permission.MANAGE_EVENTS);
  const canAccessThisEvent = Boolean(
    !eventId || canManageAllEvents || assignedEventIds.includes(eventId)
  );
  const registrantSection = queryParams.get('view') === 'waivers' ? 'waivers' : 'registrants';
  const showSectionSwitcher =
    !embedded && !tshirtsOnly && !communicationHistoryOnly && !emailComposerStandalone && !smsComposerStandalone;
  const showWaiverList = showSectionSwitcher && registrantSection === 'waivers';
  const waiverFormQuery = useEventOrderForm(showWaiverList ? eventId : null);
  const setRegistrantSection = React.useCallback(
    (next: 'registrants' | 'waivers') => {
      const nextParams = new URLSearchParams(location.search);
      if (next === 'waivers') nextParams.set('view', 'waivers');
      else nextParams.delete('view');
      const search = nextParams.toString();
      navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true });
    },
    [location.pathname, location.search, navigate]
  );
  const composerReturnPath =
    routeReturnTo ||
    (eventId
      ? canManageAllEvents
        ? `/events/setup/${eventId}?tab=registrants`
        : `/events/public-registrations/${eventId}`
      : '/events/public-registrations');
  const [selectedRegistration, setSelectedRegistration] = React.useState<PublicEventRegistration | null>(null);
  const [registrationDetailsMenuOpen, setRegistrationDetailsMenuOpen] = React.useState(false);
  const registrationDetailsMenuRef = React.useRef<HTMLDivElement>(null);
  const [editRegistrantModalOpen, setEditRegistrantModalOpen] = React.useState(false);
  const [editingRegistrant, setEditingRegistrant] = React.useState<PublicEventRegistration | null>(null);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [ticketTypeFilter, setTicketTypeFilter] = React.useState('all');
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [appointmentSlotFilter, setAppointmentSlotFilter] = React.useState('all');
  const [showPendingPayments, setShowPendingPayments] = React.useState(false);
  const [tshirtSizeFilter, setTshirtSizeFilter] = React.useState('all');
  const [tshirtSearchQuery, setTshirtSearchQuery] = React.useState('');
  const [tshirtGivenFilter, setTshirtGivenFilter] = React.useState<TshirtGivenFilter>('all');
  const [scanValue, setScanValue] = React.useState('');
  const [scannerOpen, setScannerOpen] = React.useState(false);
  const [scannerStatus, setScannerStatus] = React.useState<string>('');
  const [checkingIn, setCheckingIn] = React.useState(false);
  const isStandaloneComposer = emailComposerStandalone || smsComposerStandalone;
  const [selectedRecipientIds, setSelectedRecipientIds] = React.useState<string[]>(
    isStandaloneComposer ? routeSelectedRecipientIds : []
  );
  const [ticketChangeRecipientIds, setTicketChangeRecipientIds] = React.useState<string[]>([]);
  const [ticketChangeModalOpen, setTicketChangeModalOpen] = React.useState(false);
  const [ticketChangeTargetTicketId, setTicketChangeTargetTicketId] = React.useState('');
  const [ticketChangePaymentProvider, setTicketChangePaymentProvider] = React.useState<'paypal' | 'square'>('square');
  const [ticketChangeNote, setTicketChangeNote] = React.useState('');
  const [ticketChangeNotice, setTicketChangeNotice] = React.useState<EmailNotice | null>(null);
  const [ticketChangeQuotes, setTicketChangeQuotes] = React.useState<TicketChangeQuote[]>([]);
  const [ticketChangeApprovalLinks, setTicketChangeApprovalLinks] = React.useState<
    Array<{ registrationId: string; approvalUrl: string; provider: 'paypal' | 'square' }>
  >([]);
  const [ticketChangeManualRequestIds, setTicketChangeManualRequestIds] = React.useState<string[]>([]);
  const [emailComposerOpen, setEmailComposerOpen] = React.useState(false);
  const [emailComposerMode, setEmailComposerMode] = React.useState<EmailComposerMode>(
    emailComposerStandalone ? routeMode : 'bulk'
  );
  const [smsComposerOpen, setSmsComposerOpen] = React.useState(false);
  const [smsComposerMode, setSmsComposerMode] = React.useState<EmailComposerMode>(
    smsComposerStandalone ? routeMode : 'bulk'
  );
  const [registrationsNotice, setRegistrationsNotice] = React.useState<EmailNotice | null>(null);
  const [emailNotice, setEmailNotice] = React.useState<EmailNotice | null>(null);
  const [smsNotice, setSmsNotice] = React.useState<EmailNotice | null>(null);
  const [singleEmailRecipientId, setSingleEmailRecipientId] = React.useState<string | null>(
    emailComposerStandalone && routeMode === 'single' ? routeSingleRecipientId : null
  );
  const [singleSmsRecipientId, setSingleSmsRecipientId] = React.useState<string | null>(
    smsComposerStandalone && routeMode === 'single' ? routeSingleRecipientId : null
  );
  const [expandedHistorySendId, setExpandedHistorySendId] = React.useState<string | null>(null);
  const [expandedSmsHistorySendId, setExpandedSmsHistorySendId] = React.useState<string | null>(null);
  const [emailHistorySectionOpen, setEmailHistorySectionOpen] = React.useState(true);
  const [registrationsLimit, setRegistrationsLimit] = React.useState(REGISTRATIONS_PAGE_SIZE);
  const [registrantListPage, setRegistrantListPage] = React.useState(1);
  const [noShowListPage, setNoShowListPage] = React.useState(1);
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  const scanTimerRef = React.useRef<number | null>(null);
  const invalidateCheckinRows = React.useCallback(async () => {
    if (!eventId) return;
    await queryClient.invalidateQueries({ queryKey: ['public-event-registration-tickets', eventId] });
    await queryClient.invalidateQueries({ queryKey: ['event-setup-reporting-registration-tickets', eventId] });
  }, [eventId, queryClient]);

  React.useEffect(() => {
    setRegistrationDetailsMenuOpen(false);
  }, [selectedRegistration]);

  React.useEffect(() => {
    if (!registrationDetailsMenuOpen) return;
    const onMouseDown = (event: MouseEvent) => {
      const node = registrationDetailsMenuRef.current;
      if (node && !node.contains(event.target as Node)) {
        setRegistrationDetailsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [registrationDetailsMenuOpen]);

  React.useEffect(() => {
    if (!registrationDetailsMenuOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setRegistrationDetailsMenuOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [registrationDetailsMenuOpen]);

  const {
    data: registrations,
    isLoading: isLoadingRegistrations,
    error: registrationsError,
  } = useQuery({
    queryKey: ['public-event-registrations', eventId, registrationsLimit],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error, count } = await supabase
        .from('public_event_registrations')
        .select(
          'id, event_id, user_id, full_name, email, phone, quantity, ticket_type_id, ticket_type_name, status, payment_status, source, notes, payment_amount_cents, total_amount_cents, amount_paid_cents, balance_due_cents, form_response, created_at, appointment_links:public_event_registration_appointments(slot_id, event_appointment_slots:slot_id(id, label, starts_at, ends_at)), events:event_id(id, title, start_date, start_time, location)',
          { count: 'exact' }
        )
        .eq('event_id', eventId)
        .order('created_at', { ascending: false })
        .range(0, Math.max(0, registrationsLimit - 1));

      if (error) {
        throw new Error(error.message);
      }

      return {
        rows: (data || []).map(mapRegistrationRow),
        totalCount: count ?? 0,
      };
    },
  });
  const { data: appointmentSlotOptions = [] } = useQuery<EventAppointmentSlotOption[]>({
    queryKey: ['event-appointment-slots-for-registrations', eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('event_appointment_slots')
        .select('id, label, starts_at, ends_at')
        .eq('event_id', eventId)
        .order('sort_order', { ascending: true })
        .order('starts_at', { ascending: true });
      if (error) throw new Error(error.message);
      return ((data || []) as Array<Record<string, unknown>>)
        .map((row) => ({
          id: String(row.id || '').trim(),
          label: String(row.label || '').trim() || 'Appointment',
          starts_at: row.starts_at == null ? null : String(row.starts_at),
          ends_at: row.ends_at == null ? null : String(row.ends_at),
        }))
        .filter((row) => Boolean(row.id));
    },
  });
  const { data: eventOrderFormFields = [] } = useQuery<EventOrderFormField[]>({
    queryKey: ['event-order-form-fields', eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data: formData, error: formError } = await supabase
        .from('event_order_forms')
        .select('id, is_enabled')
        .eq('event_id', eventId)
        .maybeSingle();
      if (formError) throw new Error(formError.message);
      const formRow = (formData as { id?: string; is_enabled?: boolean | null } | null) ?? null;
      if (!formRow?.id) return [] as EventOrderFormFieldRow[];

      const { data: fieldRows, error: fieldError } = await supabase
        .from('event_order_form_fields')
        .select('field_key, label, field_type, is_required, sort_order, options, validation, placeholder, help_text')
        .eq('form_id', formRow.id)
        .order('sort_order', { ascending: true });
      if (fieldError) throw new Error(fieldError.message);

      return ((fieldRows || []) as Array<Record<string, unknown>>)
        .map((row) => ({
          field_key: String(row.field_key || '').trim(),
          label: String(row.label || '').trim(),
          field_type: (String(row.field_type || 'short_text') as EventOrderFormField['field_type']),
          is_required: Boolean(row.is_required),
          sort_order: Number(row.sort_order || 0),
          options: row.options ?? [],
          validation: row.validation ?? {},
          placeholder: row.placeholder == null ? '' : String(row.placeholder),
          help_text: row.help_text == null ? '' : String(row.help_text),
        }))
        .filter((row) => Boolean(row.field_key));
    },
  });
  const {
    data: eventOrdersForTshirts = [],
    isLoading: isLoadingEventOrdersForTshirts,
    error: eventOrdersForTshirtsError,
  } = useQuery({
    queryKey: ['event-orders-for-tshirts', eventId],
    enabled: !!eventId && tshirtsOnly,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('event_orders')
        .select('id, user_id, attendee_name, attendee_email, form_response, created_at')
        .eq('event_id', eventId)
        .order('created_at', { ascending: false })
        .limit(5000);
      if (error) throw new Error(error.message);
      return (data || []) as EventOrderTshirtRow[];
    },
  });
  const tshirtHandoutsQueryKey = ['event-tshirt-handouts', eventId] as const;
  const { data: tshirtHandouts = [] } = useQuery({
    queryKey: tshirtHandoutsQueryKey,
    enabled: !!eventId && tshirtsOnly,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('event_tshirt_handouts')
        .select('attendee_key, given_at')
        .eq('event_id', eventId);
      if (error) {
        if (isMissingTshirtHandoutsTableError(error)) {
          return readGivenTshirtKeysFromStorage(String(eventId)).map((attendee_key) => ({
            attendee_key,
            given_at: null,
          }));
        }
        throw new Error(error.message);
      }
      return (data || []) as EventTshirtHandoutRow[];
    },
  });
  const givenTshirtKeySet = React.useMemo(
    () => new Set((tshirtHandouts || []).map((row) => row.attendee_key).filter(Boolean)),
    [tshirtHandouts]
  );

  const { data: registrationTickets = [] } = useQuery({
    queryKey: ['public-event-registration-tickets', eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('public_event_registration_tickets')
        .select('id, registration_id, attendee_name, ticket_number, status, checked_in_at')
        .eq('event_id', eventId);
      if (error) throw new Error(error.message);
      return (data || []) as RegistrationTicketRow[];
    },
  });

  React.useEffect(() => {
    if (!eventId) return;
    const channel = supabase
      .channel(`public-event-registration-tickets-${eventId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'public_event_registration_tickets',
          filter: `event_id=eq.${eventId}`,
        },
        () => {
          void invalidateCheckinRows();
        }
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [eventId, invalidateCheckinRows]);

  React.useEffect(() => {
    if (!eventId || !tshirtsOnly) return;
    const channel = supabase
      .channel(`event-tshirt-handouts-${eventId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'event_tshirt_handouts',
          filter: `event_id=eq.${eventId}`,
        },
        () => {
          void queryClient.invalidateQueries({ queryKey: ['event-tshirt-handouts', eventId] });
        }
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [eventId, queryClient, tshirtsOnly]);

  React.useEffect(() => {
    setRegistrationsLimit(REGISTRATIONS_PAGE_SIZE);
  }, [eventId]);

  React.useEffect(() => {
    if (!emailComposerStandalone) return;
    setEmailNotice(null);
    setEmailComposerMode(routeMode);
    setSingleEmailRecipientId(routeMode === 'single' ? routeSingleRecipientId : null);
    setSelectedRecipientIds(routeSelectedRecipientIds);
  }, [emailComposerStandalone, routeMode, routeSingleRecipientId, routeSelectedRecipientIds]);

  React.useEffect(() => {
    if (!smsComposerStandalone) return;
    setSmsNotice(null);
    setSmsComposerMode(routeMode);
    setSingleSmsRecipientId(routeMode === 'single' ? routeSingleRecipientId : null);
    setSelectedRecipientIds(routeSelectedRecipientIds);
  }, [smsComposerStandalone, routeMode, routeSingleRecipientId, routeSelectedRecipientIds]);

  const { data: eventDetails, isLoading: isLoadingEvent } = useQuery({
    queryKey: ['event-details-for-registrations', eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('events')
        .select('id, title, start_date, start_time, location')
        .eq('id', eventId)
        .maybeSingle();

      if (error) {
        throw new Error(error.message);
      }

      return (data || null) as EventDetails | null;
    },
  });

  const { data: eventTicketTypes = [] } = useQuery({
    queryKey: ['event-ticket-types', eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('event_ticket_types')
        .select('id, name, price_cents, status')
        .eq('event_id', eventId)
        .eq('status', 'active')
        .order('sort_order', { ascending: true });
      if (error) throw new Error(error.message);
      return ((data || []) as EventTicketTypeRow[]).map((row) => ({
        ...row,
        price_cents: Number(row.price_cents || 0),
      }));
    },
  });
  const { data: eventTicketPrices = [] } = useQuery({
    queryKey: ['event-ticket-prices', eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('event_ticket_types')
        .select('id, name, price_cents')
        .eq('event_id', eventId);
      if (error) throw new Error(error.message);
      return (data || []) as Array<{ id: string; name: string; price_cents: number | null }>;
    },
  });

  const {
    data: ticketChangeHistory = [],
    isLoading: isLoadingTicketChangeHistory,
    error: ticketChangeHistoryError,
  } = useQuery({
    queryKey: ['event-ticket-change-history', eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('public_event_ticket_change_requests')
        .select(
          'id, registration_id, old_ticket_type_name, new_ticket_type_name, difference_cents, status, payment_status, requested_by_type, created_at, completed_at',
        )
        .eq('event_id', eventId)
        .order('created_at', { ascending: false })
        .limit(12);
      if (error) throw new Error(error.message);
      return (data || []) as TicketChangeHistoryRow[];
    },
  });

  const {
    data: emailDrafts = [],
    isLoading: isLoadingEmailDrafts,
  } = useQuery({
    queryKey: ['event-email-drafts', eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('event_email_drafts')
        .select('id, name, subject, header_block, body_block, footer_block, html_body, text_body, updated_at')
        .eq('event_id', eventId)
        .order('updated_at', { ascending: false })
        .limit(50);
      if (error) throw new Error(error.message);
      return (data || []) as DraftRecord[];
    },
  });

  const {
    data: emailHistory = [],
    isLoading: isLoadingEmailHistory,
    error: emailHistoryError,
  } = useQuery({
    queryKey: ['event-email-history', eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('event_email_sends')
        .select(
          'id, send_mode, recipient_scope, subject, status, recipient_count, sent_count, failed_count, skipped_count, error_summary, created_at'
        )
        .eq('event_id', eventId)
        .order('created_at', { ascending: false })
        .limit(8);
      if (error) throw new Error(error.message);
      return (data || []) as EmailSendHistoryRow[];
    },
  });

  const {
    data: smsHistory = [],
    isLoading: isLoadingSmsHistory,
    error: smsHistoryError,
  } = useQuery({
    queryKey: ['event-sms-history', eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('event_sms_sends')
        .select(
          'id, send_mode, recipient_scope, message_body, status, recipient_count, sent_count, failed_count, skipped_count, error_summary, created_at'
        )
        .eq('event_id', eventId)
        .order('created_at', { ascending: false })
        .limit(8);
      if (error) throw new Error(error.message);
      return (data || []) as SmsSendHistoryRow[];
    },
  });

  const expandedHistoryRow = React.useMemo(
    () => emailHistory.find((row) => row.id === expandedHistorySendId) ?? null,
    [emailHistory, expandedHistorySendId]
  );
  const expandedSmsHistoryRow = React.useMemo(
    () => smsHistory.find((row) => row.id === expandedSmsHistorySendId) ?? null,
    [smsHistory, expandedSmsHistorySendId]
  );

  const shouldLoadExpandedFailures = Boolean(expandedHistorySendId && (expandedHistoryRow?.failed_count ?? 0) > 0);
  const {
    data: expandedHistoryFailures = [],
    isLoading: isLoadingExpandedHistoryFailures,
    error: expandedHistoryFailuresError,
  } = useQuery({
    queryKey: ['event-email-history-failures', expandedHistorySendId],
    enabled: shouldLoadExpandedFailures,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('event_email_send_recipients')
        .select('id, send_id, email, full_name, status, error_message, created_at')
        .eq('send_id', expandedHistorySendId as string)
        .eq('status', 'failed')
        .order('created_at', { ascending: true })
        .limit(50);
      if (error) throw new Error(error.message);
      return (data || []) as EmailSendRecipientFailureRow[];
    },
  });
  const shouldLoadExpandedSmsFailures = Boolean(
    expandedSmsHistorySendId && (expandedSmsHistoryRow?.failed_count ?? 0) > 0
  );
  const {
    data: expandedSmsHistoryFailures = [],
    isLoading: isLoadingExpandedSmsHistoryFailures,
    error: expandedSmsHistoryFailuresError,
  } = useQuery({
    queryKey: ['event-sms-history-failures', expandedSmsHistorySendId],
    enabled: shouldLoadExpandedSmsFailures,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('event_sms_send_recipients')
        .select('id, send_id, phone, full_name, status, error_message, created_at')
        .eq('send_id', expandedSmsHistorySendId as string)
        .eq('status', 'failed')
        .order('created_at', { ascending: true })
        .limit(50);
      if (error) throw new Error(error.message);
      return (data || []) as SmsSendRecipientFailureRow[];
    },
  });

  const isLoading = isLoadingRegistrations || isLoadingEvent || (tshirtsOnly && isLoadingEventOrdersForTshirts);
  const error = registrationsError;
  const eventFromRows = registrations?.rows?.[0]?.events ?? null;
  const eventTitle = eventDetails?.title ?? eventFromRows?.title ?? 'Event Registrants';
  const eventSchedule = formatEventSchedule({
    start_date: eventDetails?.start_date ?? eventFromRows?.start_date ?? null,
    start_time: eventDetails?.start_time ?? eventFromRows?.start_time ?? null,
  });
  const eventLocation = eventDetails?.location ?? eventFromRows?.location ?? null;
  const pendingPaymentCount = React.useMemo(
    () => (registrations?.rows ?? []).filter((row) => isPendingPaymentRegistration(row)).length,
    [registrations]
  );
  const registrationsAfterPaymentFilter = React.useMemo(() => {
    const rows = registrations?.rows ?? [];
    if (showPendingPayments) return rows;
    return rows.filter((row) => !isPendingPaymentRegistration(row));
  }, [registrations, showPendingPayments]);
  const ticketTypeOptions = React.useMemo(() => {
    const values = new Set<string>();
    for (const row of registrationsAfterPaymentFilter) {
      values.add((row.ticket_type_name ?? 'General').trim());
    }
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [registrationsAfterPaymentFilter]);
  const statusOptions = React.useMemo(() => {
    const values = new Set<string>();
    for (const row of registrationsAfterPaymentFilter) {
      const value = (row.status ?? '').trim().toLowerCase();
      if (value) values.add(value);
    }
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [registrationsAfterPaymentFilter]);
  const appointmentFilterOptions = React.useMemo(() => {
    const byId = new Map<string, EventAppointmentSlotOption>();
    for (const slot of appointmentSlotOptions) {
      if (!slot.id) continue;
      byId.set(slot.id, slot);
    }
    for (const row of registrationsAfterPaymentFilter) {
      for (const appointment of row.appointments) {
        if (!appointment.slot_id || byId.has(appointment.slot_id)) continue;
        byId.set(appointment.slot_id, {
          id: appointment.slot_id,
          label: appointment.label,
          starts_at: appointment.starts_at,
          ends_at: appointment.ends_at,
        });
      }
    }
    return Array.from(byId.values()).sort((a, b) =>
      `${a.starts_at ?? ''}|${a.label}`.localeCompare(`${b.starts_at ?? ''}|${b.label}`)
    );
  }, [appointmentSlotOptions, registrationsAfterPaymentFilter]);
  const registrationsAfterFacetFilters = React.useMemo(
    () =>
      filterRegistrationsByTicketStatusAndAppointment(
        registrationsAfterPaymentFilter,
        ticketTypeFilter,
        statusFilter,
        appointmentSlotFilter,
      ),
    [registrationsAfterPaymentFilter, ticketTypeFilter, statusFilter, appointmentSlotFilter]
  );
  const filteredRegistrations = React.useMemo(
    () => filterRegistrationsBySearch(registrationsAfterFacetFilters, searchQuery),
    [registrationsAfterFacetFilters, searchQuery]
  );
  const ticketPriceLookup = React.useMemo(() => {
    const byId = new Map<string, number>();
    const byName = new Map<string, number>();
    for (const ticket of eventTicketPrices) {
      const normalizedPrice = Number(ticket.price_cents ?? 0);
      if (!Number.isFinite(normalizedPrice)) continue;
      const normalizedId = String(ticket.id || '').trim();
      if (normalizedId) byId.set(normalizedId, normalizedPrice);
      const normalizedName = String(ticket.name || '').trim().toLowerCase();
      if (normalizedName) byName.set(normalizedName, normalizedPrice);
    }
    return { byId, byName };
  }, [eventTicketPrices]);
  const registrantCostById = React.useMemo(() => {
    const costById = new Map<string, number | null>();
    for (const row of registrationsAfterPaymentFilter) {
      const totalAmount = Number(row.total_amount_cents);
      if (Number.isFinite(totalAmount) && totalAmount >= 0) {
        costById.set(row.id, totalAmount);
        continue;
      }
      const quantity = Math.max(1, Number(row.quantity || 1));
      const priceById = row.ticket_type_id ? ticketPriceLookup.byId.get(row.ticket_type_id) : undefined;
      const priceByName = ticketPriceLookup.byName.get(String(row.ticket_type_name || '').trim().toLowerCase());
      const unitPrice = Number(priceById ?? priceByName);
      if (Number.isFinite(unitPrice) && unitPrice >= 0) {
        costById.set(row.id, Math.round(unitPrice * quantity));
      } else {
        costById.set(row.id, null);
      }
    }
    return costById;
  }, [registrationsAfterPaymentFilter, ticketPriceLookup]);
  const registrantBalanceById = React.useMemo(() => {
    const balanceById = new Map<string, number>();
    for (const row of registrationsAfterPaymentFilter) {
      balanceById.set(row.id, Math.max(0, Number(row.balance_due_cents || 0)));
    }
    return balanceById;
  }, [registrationsAfterPaymentFilter]);
  const checkinByRegistrationId = React.useMemo(() => {
    const grouped = new Map<string, RegistrationTicketRow[]>();
    for (const row of registrationTickets) {
      const key = String(row.registration_id || '').trim();
      if (!key) continue;
      const list = grouped.get(key) ?? [];
      list.push(row);
      grouped.set(key, list);
    }
    const nameByRegistrationId = new Map(
      (registrations?.rows ?? []).map((row) => [row.id, String(row.full_name || '').trim()]),
    );
    const map = new Map<string, RegistrationCheckInStatus>();
    for (const [key, tickets] of Array.from(grouped.entries())) {
      const sorted = [...tickets].sort(compareTicketNumber);
      const activeTickets = sorted.filter((ticket) => !isTicketCancelled(ticket));
      const pool = activeTickets.length > 0 ? activeTickets : sorted;
      const primaryTicket = pickPrimaryRegistrationTicket(sorted, nameByRegistrationId.get(key) || '');
      const checkedInTickets = pool.filter((ticket) => isTicketCheckedIn(ticket));
      let lastCheckedInAt: string | null = null;
      for (const ticket of checkedInTickets) {
        lastCheckedInAt = laterTimestamp(lastCheckedInAt, ticket.checked_in_at || null);
      }
      const totalTickets = pool.length;
      const checkedInCount = checkedInTickets.length;
      map.set(key, {
        checkedIn: totalTickets > 0 && checkedInCount === totalTickets,
        checkedInCount,
        totalTickets,
        remainingTickets: Math.max(0, totalTickets - checkedInCount),
        lastCheckedInAt,
        primaryTicket,
        primaryCheckedIn: isTicketCheckedIn(primaryTicket),
        tickets: sorted,
      });
    }
    return map;
  }, [registrationTickets, registrations]);
  const directoryRows = React.useMemo(
    () =>
      registrationsAfterFacetFilters.flatMap((registration) =>
        buildRegistrationDirectoryRows(
          registration,
          checkinByRegistrationId.get(registration.id)?.tickets || [],
        )
      ),
    [checkinByRegistrationId, registrationsAfterFacetFilters]
  );
  const filteredDirectoryRows = React.useMemo(
    () => filterDirectoryAttendeesBySearch(directoryRows, searchQuery),
    [directoryRows, searchQuery]
  );
  const registrantExportShape = React.useMemo(() => {
    const formKeys: string[] = [];
    const attendeeResponseKeys: string[] = [];
    const seenFormKeys = new Set<string>();
    const seenAttendeeKeys = new Set<string>();
    const appendFormKeys = (keys: readonly string[]) => {
      appendUniqueNormalizedKeys(
        formKeys,
        seenFormKeys,
        keys.filter((key) => !isStructuralFormResponseKey(key))
      );
    };
    appendFormKeys(
      eventOrderFormFields.filter((field) => !isCollectPerAttendeeField(field)).map((field) => field.field_key)
    );
    appendUniqueNormalizedKeys(
      attendeeResponseKeys,
      seenAttendeeKeys,
      eventOrderFormFields.filter((field) => isCollectPerAttendeeField(field)).map((field) => field.field_key)
    );

    for (const registration of filteredRegistrations) {
      const responseRecord = getFormResponseRecord(registration.form_response);
      appendFormKeys(Object.keys(responseRecord));
      for (const responses of Array.from(collectAttendeeResponsesByIndex(responseRecord).values())) {
        appendUniqueNormalizedKeys(attendeeResponseKeys, seenAttendeeKeys, Object.keys(responses));
      }
    }

    appendFormKeys(attendeeResponseKeys);
    return { formKeys };
  }, [eventOrderFormFields, filteredRegistrations]);
  const attendanceMetrics = React.useMemo(() => {
    const rows = registrations?.rows ?? [];
    const totalRegistrations = rows.length;
    const totalTickets = rows.reduce((sum, row) => sum + Math.max(1, Number(row.quantity || 1)), 0);
    const peopleRows = rows.flatMap((registration) => {
      if (String(registration.status || '').trim().toLowerCase() === 'cancelled') return [] as RegistrantDirectoryRow[];
      return buildRegistrationDirectoryRows(
        registration,
        checkinByRegistrationId.get(registration.id)?.tickets || [],
      );
    });
    const checkedInPeople = peopleRows.filter((row) => isTicketCheckedIn(row.ticket));
    const noShowRows = peopleRows.filter((row) => !isTicketCheckedIn(row.ticket) && !isTicketCancelled(row.ticket));
    const showUpRate = peopleRows.length > 0 ? (checkedInPeople.length / peopleRows.length) * 100 : 0;
    return {
      totalRegistrations,
      totalTickets,
      checkedInRegistrations: checkedInPeople.length,
      noShowRows,
      showUpRate,
    };
  }, [registrations, checkinByRegistrationId]);
  const registrantPageCount = Math.max(1, Math.ceil(filteredDirectoryRows.length / REGISTRANT_LIST_PAGE_SIZE));
  const pagedFilteredDirectoryRows = React.useMemo(() => {
    const start = (registrantListPage - 1) * REGISTRANT_LIST_PAGE_SIZE;
    return filteredDirectoryRows.slice(start, start + REGISTRANT_LIST_PAGE_SIZE);
  }, [filteredDirectoryRows, registrantListPage]);
  const noShowPageCount = Math.max(1, Math.ceil(attendanceMetrics.noShowRows.length / NO_SHOW_LIST_PAGE_SIZE));
  const pagedNoShowRows = React.useMemo(() => {
    const start = (noShowListPage - 1) * NO_SHOW_LIST_PAGE_SIZE;
    return attendanceMetrics.noShowRows.slice(start, start + NO_SHOW_LIST_PAGE_SIZE);
  }, [attendanceMetrics.noShowRows, noShowListPage]);
  const tshirtKeyConfig = React.useMemo(() => {
    const explicitKeys = Array.from(
      new Set([
        normalizeResponseKey('t_shirt_size'),
        normalizeResponseKey('tshirt_size'),
        normalizeResponseKey('shirt_size'),
        normalizeResponseKey('tshirt'),
      ]).values()
    ).filter(Boolean);
    const fallbackKeys = new Set<string>();
    for (const field of eventOrderFormFields) {
      const key = normalizeResponseKey(field.field_key);
      if (!key || explicitKeys.includes(key)) continue;
      if (looksLikeTshirtFieldLabel(field.label) || looksLikeTshirtFieldLabel(field.field_key)) {
        fallbackKeys.add(key);
      }
    }
    return {
      explicitKeys,
      fallbackKeys: Array.from(fallbackKeys),
    };
  }, [eventOrderFormFields]);
  const tshirtAttendeeRows = React.useMemo(() => {
    const rows: TshirtAttendeeRow[] = [];
    const getResponseValueByKeys = (record: Record<string, unknown>, keys: string[]) => {
      if (!keys.length) return '';
      for (const key of keys) {
        const direct = record[key];
        const directValue = formatTshirtSize(direct);
        if (directValue) return directValue;
      }
      for (const [key, value] of Object.entries(record)) {
        const normalizedKey = normalizeResponseKey(key);
        if (!keys.includes(normalizedKey)) continue;
        const normalizedValue = formatTshirtSize(value);
        if (normalizedValue) return normalizedValue;
      }
      return '';
    };

    const orderResponsesById = new Map<string, Record<string, unknown>>();
    const orderIdsByUserId = new Map<string, string[]>();
    const orderIdsByEmail = new Map<string, string[]>();
    const matchedOrderIds = new Set<string>();
    for (const row of eventOrdersForTshirts) {
      const raw = row.form_response;
      const parsed =
        raw && typeof raw === 'object' && !Array.isArray(raw)
          ? (raw as Record<string, unknown>)
          : {};
      if (!Object.keys(parsed).length) continue;
      orderResponsesById.set(row.id, parsed);
      const userId = String(row.user_id || '').trim();
      const attendeeEmail = String(row.attendee_email || '').trim().toLowerCase();
      if (userId) {
        const existing = orderIdsByUserId.get(userId) || [];
        existing.push(row.id);
        orderIdsByUserId.set(userId, existing);
      }
      if (attendeeEmail) {
        const existing = orderIdsByEmail.get(attendeeEmail) || [];
        existing.push(row.id);
        orderIdsByEmail.set(attendeeEmail, existing);
      }
    }

    const inferKeysFromRecord = (record: Record<string, unknown>) => {
      const inferred = new Set<string>();
      for (const [rawKey, rawValue] of Object.entries(record)) {
        const normalizedKey = normalizeResponseKey(rawKey);
        if (!normalizedKey || isStructuralFormResponseKey(normalizedKey)) continue;
        const normalizedValue = formatTshirtSize(rawValue);
        if (normalizedValue && isLikelyTshirtSizeValue(normalizedValue)) {
          inferred.add(normalizedKey);
        }
      }
      return inferred;
    };

    for (const registration of registrations?.rows ?? []) {
      const rawResponse = registration.form_response;
      const responseRecord =
        rawResponse && typeof rawResponse === 'object' && !Array.isArray(rawResponse)
          ? (rawResponse as Record<string, unknown>)
          : {};
      const registrationUserId = String(registration.user_id || '').trim();
      const registrationEmail = String(registration.email || '').trim().toLowerCase();
      const linkedOrderIds = new Set<string>([
        ...(registrationUserId ? orderIdsByUserId.get(registrationUserId) || [] : []),
        ...(registrationEmail ? orderIdsByEmail.get(registrationEmail) || [] : []),
      ]);
      const linkedOrders = eventOrdersForTshirts.filter((order) => linkedOrderIds.has(order.id));
      for (const linked of linkedOrders) matchedOrderIds.add(linked.id);
      const linkedOrderResponses = linkedOrders
        .map((order) => orderResponsesById.get(order.id) || {})
        .filter((record) => Object.keys(record).length > 0);
      const primaryLinkedOrder = linkedOrders[0] || null;
      const linkedOrderContactName = String(primaryLinkedOrder?.attendee_name || '').trim();
      const linkedOrderContactEmail = String(primaryLinkedOrder?.attendee_email || '').trim();
      const contacts = [
        {
          fullName: String(registration.full_name || '').trim() || linkedOrderContactName,
          email: String(registration.email || '').trim() || linkedOrderContactEmail,
          phone: String(registration.phone || '').trim(),
        },
      ];
      const groupMembersRaw = Array.isArray(responseRecord.group_members)
        ? (responseRecord.group_members as Record<string, unknown>[])
        : [];
      const linkedGroupMembersRaw = linkedOrderResponses.flatMap((record) =>
        Array.isArray(record.group_members) ? (record.group_members as Record<string, unknown>[]) : []
      );
      const mergedGroupMembersRaw = [...groupMembersRaw, ...linkedGroupMembersRaw];
      for (const member of mergedGroupMembersRaw) {
        const fullName = pickString(member, ['full_name', 'fullName', 'name', 'attendee_name', 'attendeeName']);
        const email = pickString(member, ['email', 'email_address', 'emailAddress']);
        const phone = pickString(member, ['phone', 'phone_number', 'phoneNumber', 'mobile', 'mobile_number']);
        if (!fullName && !email && !phone) continue;
        contacts.push({ fullName, email, phone });
      }
      const responseRowsRaw = Array.isArray(responseRecord.attendee_responses)
        ? (responseRecord.attendee_responses as Record<string, unknown>[])
        : [];
      const linkedOrderResponseRowsRaw = linkedOrderResponses.flatMap((record) =>
        Array.isArray(record.attendee_responses) ? (record.attendee_responses as Record<string, unknown>[]) : []
      );
      const responsesByIndex = new Map<number, { attendeeName: string; responses: Record<string, unknown> }>();
      for (let fallbackIndex = 0; fallbackIndex < responseRowsRaw.length; fallbackIndex += 1) {
        const entry = responseRowsRaw[fallbackIndex];
        if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue;
        const attendeeIndexRaw = Number(entry.attendee_index);
        const attendeeIndex =
          Number.isFinite(attendeeIndexRaw) && attendeeIndexRaw >= 0 ? Math.floor(attendeeIndexRaw) : fallbackIndex;
        const attendeeName = String(entry.attendee_name || '').trim();
        const responses =
          entry.responses && typeof entry.responses === 'object' && !Array.isArray(entry.responses)
            ? (entry.responses as Record<string, unknown>)
            : {};
        responsesByIndex.set(attendeeIndex, { attendeeName, responses });
      }
      for (let fallbackIndex = 0; fallbackIndex < linkedOrderResponseRowsRaw.length; fallbackIndex += 1) {
        const entry = linkedOrderResponseRowsRaw[fallbackIndex];
        if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue;
        const attendeeIndexRaw = Number(entry.attendee_index);
        const attendeeIndex =
          Number.isFinite(attendeeIndexRaw) && attendeeIndexRaw >= 0 ? Math.floor(attendeeIndexRaw) : fallbackIndex;
        if (responsesByIndex.has(attendeeIndex)) continue;
        const attendeeName = String(entry.attendee_name || '').trim();
        const responses =
          entry.responses && typeof entry.responses === 'object' && !Array.isArray(entry.responses)
            ? (entry.responses as Record<string, unknown>)
            : {};
        responsesByIndex.set(attendeeIndex, { attendeeName, responses });
      }

      const explicitKeys = tshirtKeyConfig.explicitKeys;
      const inferredKeys = new Set<string>();
      const orderLevelCandidates = [responseRecord, ...linkedOrderResponses];
      orderLevelCandidates.forEach((candidate) => {
        Array.from(inferKeysFromRecord(candidate)).forEach((inferred) => inferredKeys.add(inferred));
      });
      Array.from(responsesByIndex.values()).forEach((responseMeta) => {
        Array.from(inferKeysFromRecord(responseMeta.responses)).forEach((inferred) => inferredKeys.add(inferred));
      });
      const fallbackKeys = Array.from(new Set([...tshirtKeyConfig.fallbackKeys, ...Array.from(inferredKeys)]));
      const orderExplicitSize =
        orderLevelCandidates.map((candidate) => getResponseValueByKeys(candidate, explicitKeys)).find(Boolean) || '';
      const orderFallbackSize =
        orderLevelCandidates.map((candidate) => getResponseValueByKeys(candidate, fallbackKeys)).find(Boolean) || '';
      const totalRows = Math.max(1, Number(registration.quantity || 1), contacts.length, responsesByIndex.size);
      for (let index = 0; index < totalRows; index += 1) {
        const contact = contacts[index];
        const responseMeta = responsesByIndex.get(index);
        const perAttendeeResponses = responseMeta?.responses || {};
        const perAttendeeExplicitSize = getResponseValueByKeys(perAttendeeResponses, explicitKeys);
        const perAttendeeFallbackSize = getResponseValueByKeys(perAttendeeResponses, fallbackKeys);
        const resolvedSize = perAttendeeExplicitSize || orderExplicitSize || perAttendeeFallbackSize || orderFallbackSize;
        if (!resolvedSize) continue;
        const normalizedSize = resolvedSize.toUpperCase();
        const registrantName = String(registration.full_name || '').trim() || registration.email || 'Registrant';
        const attendeeName = contact?.fullName || responseMeta?.attendeeName || `Attendee ${index + 1}`;
        rows.push({
          key: `${registration.id}-${index}`,
          registrationId: registration.id,
          registrantName,
          attendeeName,
          email: contact?.email || '',
          phone: contact?.phone || '',
          size: resolvedSize,
          normalizedSize,
        });
      }
    }

    // Include order-only records that could not be linked to a registration row yet.
    for (const order of eventOrdersForTshirts) {
      if (matchedOrderIds.has(order.id)) continue;
      const raw = order.form_response;
      const responseRecord =
        raw && typeof raw === 'object' && !Array.isArray(raw)
          ? (raw as Record<string, unknown>)
          : {};
      if (!Object.keys(responseRecord).length) continue;
      const explicitKeys = tshirtKeyConfig.explicitKeys;
      const inferredKeys = new Set<string>();
      Array.from(inferKeysFromRecord(responseRecord)).forEach((inferred) => inferredKeys.add(inferred));
      const orderExplicitSize = getResponseValueByKeys(responseRecord, explicitKeys);
      const responseRowsRaw = Array.isArray(responseRecord.attendee_responses)
        ? (responseRecord.attendee_responses as Record<string, unknown>[])
        : [];
      for (const entry of responseRowsRaw) {
        if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue;
        const responses =
          entry.responses && typeof entry.responses === 'object' && !Array.isArray(entry.responses)
            ? (entry.responses as Record<string, unknown>)
            : {};
        Array.from(inferKeysFromRecord(responses)).forEach((inferred) => inferredKeys.add(inferred));
      }
      const fallbackKeys = Array.from(new Set([...tshirtKeyConfig.fallbackKeys, ...Array.from(inferredKeys)]));
      const orderFallbackSize = getResponseValueByKeys(responseRecord, fallbackKeys);
      const orderContactName = String(order.attendee_name || '').trim() || String(order.attendee_email || '').trim() || 'Order Attendee';
      const orderContactEmail = String(order.attendee_email || '').trim();
      const responsesByIndex = new Map<number, { attendeeName: string; responses: Record<string, unknown> }>();
      for (let fallbackIndex = 0; fallbackIndex < responseRowsRaw.length; fallbackIndex += 1) {
        const entry = responseRowsRaw[fallbackIndex];
        if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue;
        const attendeeIndexRaw = Number(entry.attendee_index);
        const attendeeIndex =
          Number.isFinite(attendeeIndexRaw) && attendeeIndexRaw >= 0 ? Math.floor(attendeeIndexRaw) : fallbackIndex;
        const attendeeName = String(entry.attendee_name || '').trim();
        const responses =
          entry.responses && typeof entry.responses === 'object' && !Array.isArray(entry.responses)
            ? (entry.responses as Record<string, unknown>)
            : {};
        responsesByIndex.set(attendeeIndex, { attendeeName, responses });
      }
      const totalRows = Math.max(1, responsesByIndex.size);
      for (let index = 0; index < totalRows; index += 1) {
        const responseMeta = responsesByIndex.get(index);
        const perAttendeeResponses = responseMeta?.responses || {};
        const perAttendeeExplicitSize = getResponseValueByKeys(perAttendeeResponses, explicitKeys);
        const perAttendeeFallbackSize = getResponseValueByKeys(perAttendeeResponses, fallbackKeys);
        const resolvedSize = perAttendeeExplicitSize || orderExplicitSize || perAttendeeFallbackSize || orderFallbackSize;
        if (!resolvedSize) continue;
        const attendeeName = responseMeta?.attendeeName || (index === 0 ? orderContactName : `Attendee ${index + 1}`);
        rows.push({
          key: `order-${order.id}-${index}`,
          registrationId: null,
          registrantName: orderContactName,
          attendeeName,
          email: index === 0 ? orderContactEmail : '',
          phone: '',
          size: resolvedSize,
          normalizedSize: resolvedSize.toUpperCase(),
        });
      }
    }

    return rows;
  }, [eventOrdersForTshirts, registrations, tshirtKeyConfig]);
  const tshirtSizeOptions = React.useMemo(() => {
    const values = new Set<string>();
    for (const row of tshirtAttendeeRows) {
      if (row.normalizedSize) values.add(row.normalizedSize);
    }
    return Array.from(values).sort(compareTshirtSizes);
  }, [tshirtAttendeeRows]);
  const tshirtGivenCount = React.useMemo(
    () => tshirtAttendeeRows.filter((row) => givenTshirtKeySet.has(row.key)).length,
    [givenTshirtKeySet, tshirtAttendeeRows]
  );
  const filteredTshirtRows = React.useMemo(() => {
    const query = tshirtSearchQuery.trim().toLowerCase();
    return tshirtAttendeeRows.filter((row) => {
      if (tshirtSizeFilter !== 'all' && row.normalizedSize !== tshirtSizeFilter) return false;
      const isGiven = givenTshirtKeySet.has(row.key);
      if (tshirtGivenFilter === 'given' && !isGiven) return false;
      if (tshirtGivenFilter === 'not_given' && isGiven) return false;
      if (!query) return true;
      return (
        row.attendeeName.toLowerCase().includes(query) ||
        row.registrantName.toLowerCase().includes(query) ||
        row.email.toLowerCase().includes(query) ||
        row.phone.toLowerCase().includes(query) ||
        row.size.toLowerCase().includes(query)
      );
    });
  }, [givenTshirtKeySet, tshirtAttendeeRows, tshirtGivenFilter, tshirtSearchQuery, tshirtSizeFilter]);
  React.useEffect(() => {
    if (tshirtSizeFilter === 'all') return;
    if (tshirtSizeOptions.includes(tshirtSizeFilter)) return;
    setTshirtSizeFilter('all');
  }, [tshirtSizeFilter, tshirtSizeOptions]);
  const tshirtSizeSummary = React.useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of filteredTshirtRows) {
      counts.set(row.normalizedSize, (counts.get(row.normalizedSize) || 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([size, count]) => ({ size, count }))
      .sort((a, b) => compareTshirtSizes(a.size, b.size));
  }, [filteredTshirtRows]);
  const tshirtSizeTotal = filteredTshirtRows.length;
  const selectedGroupPasscode = React.useMemo(
    () => extractGroupJoinPasscode(selectedRegistration?.form_response),
    [selectedRegistration]
  );
  const selectedRegistrationCheckin = React.useMemo(
    () => (selectedRegistration ? checkinByRegistrationId.get(selectedRegistration.id) ?? null : null),
    [checkinByRegistrationId, selectedRegistration]
  );
  const selectedAttendees = React.useMemo(() => {
    if (!selectedRegistration) return [] as Array<{
      key: string;
      fullName: string;
      email: string;
      phone: string;
      responses: string;
      ticket: RegistrationTicketRow | null;
    }>;
    const formResponse = selectedRegistration.form_response;
    const responseRecord =
      formResponse && typeof formResponse === 'object' && !Array.isArray(formResponse)
        ? (formResponse as Record<string, unknown>)
        : {};
    const formFieldLabelByKey = new Map(
      eventOrderFormFields.map((field) => [field.field_key, field.label || formatCustomResponseLabel(field.field_key)])
    );
    const responseRowsRaw = Array.isArray(responseRecord.attendee_responses)
      ? (responseRecord.attendee_responses as Record<string, unknown>[])
      : [];
    const responsesByIndex = new Map<number, string>();
    for (let fallbackIndex = 0; fallbackIndex < responseRowsRaw.length; fallbackIndex += 1) {
      const entry = responseRowsRaw[fallbackIndex];
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue;
      const attendeeIndexRaw = Number(entry.attendee_index);
      const attendeeIndex =
        Number.isFinite(attendeeIndexRaw) && attendeeIndexRaw >= 0 ? Math.floor(attendeeIndexRaw) : fallbackIndex;
      const responses =
        entry.responses && typeof entry.responses === 'object' && !Array.isArray(entry.responses)
          ? (entry.responses as Record<string, unknown>)
          : {};
      const details = Object.entries(responses)
        .map(([key, value]) => {
          const formatted = formatCustomResponseValue(value);
          if (!formatted) return '';
          return `${formFieldLabelByKey.get(key) || formatCustomResponseLabel(key)}=${formatted}`;
        })
        .filter(Boolean)
        .join(', ');
      if (details) responsesByIndex.set(attendeeIndex, details);
    }

    const registrationTicketsForSelection = checkinByRegistrationId.get(selectedRegistration.id)?.tickets || [];
    const people = buildRegistrationAttendeePeople(selectedRegistration, registrationTicketsForSelection.length);
    return people
      .map((person, index) => ({
        key: `${index}-${person.fullName || 'attendee'}`,
        fullName: person.fullName,
        email: person.email,
        phone: person.phone,
        responses: responsesByIndex.get(index) || '',
        ticket: matchAttendeeTicket(people, registrationTicketsForSelection, index),
      }))
      .filter((row) => Boolean(row.fullName || row.email || row.phone || row.responses || row.ticket));
  }, [checkinByRegistrationId, eventOrderFormFields, selectedRegistration]);
  const selectedCustomResponses = React.useMemo(() => {
    if (!selectedRegistration) return [] as Array<{ key: string; label: string; value: string }>;
    const response = selectedRegistration.form_response;
    if (!response || typeof response !== 'object' || Array.isArray(response)) return [];

    const responseRecord = response as Record<string, unknown>;
    const formFieldLabelByKey = new Map(
      eventOrderFormFields.map((field) => [field.field_key, field.label || formatCustomResponseLabel(field.field_key)])
    );
    const rows: Array<{ key: string; label: string; value: string }> = [];
    const seen = new Set<string>();

    for (const field of eventOrderFormFields) {
      if (!Object.prototype.hasOwnProperty.call(responseRecord, field.field_key)) continue;
      if (isStructuralFormResponseKey(field.field_key)) continue;
      const value = formatCustomResponseValue(responseRecord[field.field_key]);
      if (!value) continue;
      rows.push({
        key: field.field_key,
        label: formFieldLabelByKey.get(field.field_key) || formatCustomResponseLabel(field.field_key),
        value,
      });
      seen.add(field.field_key);
    }

    for (const [key, rawValue] of Object.entries(responseRecord)) {
      if (seen.has(key) || isStructuralFormResponseKey(key)) continue;
      const value = formatCustomResponseValue(rawValue);
      if (!value) continue;
      rows.push({
        key,
        label: formatCustomResponseLabel(key),
        value,
      });
      seen.add(key);
    }

    return rows;
  }, [eventOrderFormFields, selectedRegistration]);

  const copyToClipboard = React.useCallback(async (value: string) => {
    const normalized = String(value || '').trim();
    if (!normalized) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(normalized);
      } else {
        const area = document.createElement('textarea');
        area.value = normalized;
        area.setAttribute('readonly', 'true');
        area.style.position = 'absolute';
        area.style.left = '-9999px';
        document.body.appendChild(area);
        area.select();
        document.execCommand('copy');
        document.body.removeChild(area);
      }
      setEmailNotice({ kind: 'success', message: 'Group passcode copied.' });
    } catch {
      setEmailNotice({ kind: 'error', message: 'Could not copy the group passcode.' });
    }
  }, []);

  const recipientSelectionSet = React.useMemo(() => new Set(selectedRecipientIds), [selectedRecipientIds]);
  const ticketChangeRows = React.useMemo(() => {
    if (!ticketChangeRecipientIds.length) return [];
    const lookup = new Set(ticketChangeRecipientIds);
    return (registrations?.rows || []).filter((row) => lookup.has(row.id));
  }, [ticketChangeRecipientIds, registrations]);

  const saveDraftMutation = useMutation({
    mutationFn: async (payload: {
      draftId?: string | null;
      draftName: string;
      subject: string;
      headerBlock: string;
      bodyBlock: string;
      footerBlock: string;
      htmlBody: string;
      textBody: string;
    }) => {
      const rowPayload = {
        event_id: eventId,
        name: payload.draftName.trim() || 'Untitled Draft',
        subject: payload.subject.trim(),
        header_block: payload.headerBlock,
        body_block: payload.bodyBlock,
        footer_block: payload.footerBlock,
        html_body: payload.htmlBody,
        text_body: payload.textBody,
        updated_by: user?.id ?? null,
      };
      if (payload.draftId) {
        const { error } = await supabase.from('event_email_drafts').update(rowPayload).eq('id', payload.draftId);
        if (error) throw new Error(error.message);
        return payload.draftId;
      }
      const { data, error } = await supabase
        .from('event_email_drafts')
        .insert({
          ...rowPayload,
          created_by: user?.id ?? null,
        })
        .select('id')
        .single();
      if (error) throw new Error(error.message);
      return String(data?.id || '');
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['event-email-drafts', eventId] });
      setEmailNotice({ kind: 'success', message: 'Draft saved.' });
    },
    onError: (error: unknown) => {
      setEmailNotice({
        kind: 'error',
        message: error instanceof Error ? error.message : 'Could not save draft.',
      });
    },
  });

  const sendEmailMutation = useMutation({
    mutationFn: async (payload: {
      mode: EmailComposerMode;
      recipientScope: ComposerRecipientScope;
      recipientRegistrationIds: string[];
      draftId?: string | null;
      subject: string;
      headerBlock: string;
      bodyBlock: string;
      footerBlock: string;
      htmlBody: string;
      textBody: string;
    }) => {
      const { data, error } = await supabase.functions.invoke('send-event-registrant-email', {
        body: {
          eventId,
          draftId: payload.draftId ?? null,
          mode: payload.mode,
          recipientScope: payload.recipientScope,
          recipientRegistrationIds: payload.recipientRegistrationIds,
          subject: payload.subject,
          headerBlock: payload.headerBlock,
          bodyBlock: payload.bodyBlock,
          footerBlock: payload.footerBlock,
          htmlBody: payload.htmlBody,
          textBody: payload.textBody,
        },
      });
      if (error) throw new Error(error.message);
      const fnError = (data as { error?: string } | null)?.error;
      if (fnError) throw new Error(fnError);
      return (data as any)?.data || {};
    },
    onSuccess: async (data: any) => {
      await queryClient.invalidateQueries({ queryKey: ['event-email-history', eventId] });
      if (!emailComposerStandalone) {
        setEmailComposerOpen(false);
      }
      setEmailNotice({
        kind: 'success',
        message: `Email send finished. Sent ${Number(data?.sent || 0)}, failed ${Number(data?.failed || 0)}, skipped ${Number(data?.skipped || 0)}.`,
      });
      if (emailComposerStandalone) {
        navigate(composerReturnPath);
      }
    },
    onError: (error: unknown) => {
      setEmailNotice({
        kind: 'error',
        message: error instanceof Error ? error.message : 'Could not send email.',
      });
    },
  });

  const resendConfirmationEmailMutation = useMutation({
    mutationFn: async (registrationId: string) => {
      if (!eventId) {
        throw new Error('Event context is missing for this registrant.');
      }
      const { data, error } = await supabase.functions.invoke('resend-public-event-confirmation-email', {
        body: {
          eventId,
          registrationId,
        },
      });
      if (error) throw new Error(error.message);
      const fnError = (data as { error?: string; message?: string } | null)?.error || (data as { message?: string } | null)?.message;
      if (fnError) throw new Error(fnError);
      return (data as { data?: { sent?: number; failed?: number; skipped?: number } } | null)?.data || {};
    },
    onSuccess: (data) => {
      setEmailNotice({
        kind: 'success',
        message: `Confirmation resend finished. Sent ${Number(data?.sent || 0)}, failed ${Number(data?.failed || 0)}, skipped ${Number(data?.skipped || 0)}.`,
      });
    },
    onError: (error: unknown) => {
      setEmailNotice({
        kind: 'error',
        message: error instanceof Error ? error.message : 'Could not resend confirmation email.',
      });
    },
  });

  const sendSmsMutation = useMutation({
    mutationFn: async (payload: {
      mode: EmailComposerMode;
      recipientScope: SmsComposerRecipientScope;
      recipientRegistrationIds: string[];
      messageBody: string;
    }) => {
      const { data, error } = await supabase.functions.invoke('send-event-registrant-sms', {
        body: {
          eventId,
          mode: payload.mode,
          recipientScope: payload.recipientScope,
          recipientRegistrationIds: payload.recipientRegistrationIds,
          messageBody: payload.messageBody,
        },
      });
      if (error) throw new Error(error.message);
      const fnError = (data as { error?: string } | null)?.error;
      if (fnError) throw new Error(fnError);
      return (data as any)?.data || {};
    },
    onSuccess: async (data: any) => {
      await queryClient.invalidateQueries({ queryKey: ['event-sms-history', eventId] });
      if (!smsComposerStandalone) {
        setSmsComposerOpen(false);
      }
      setSmsNotice({
        kind: 'success',
        message: `SMS send finished. Sent ${Number(data?.sent || 0)}, failed ${Number(data?.failed || 0)}, skipped ${Number(data?.skipped || 0)}.`,
      });
      if (smsComposerStandalone) {
        navigate(composerReturnPath);
      }
    },
    onError: (error: unknown) => {
      setSmsNotice({
        kind: 'error',
        message: error instanceof Error ? error.message : 'Could not send SMS.',
      });
    },
  });

  const deleteEmailHistoryMutation = useMutation({
    mutationFn: async (sendId: string) => {
      const { error: rpcError } = await supabase.rpc('delete_event_email_send_history', { p_send_id: sendId });
      if (rpcError) throw new Error(rpcError.message);
      return sendId;
    },
    onSuccess: async (sendId) => {
      if (expandedHistorySendId === sendId) {
        setExpandedHistorySendId(null);
      }
      await queryClient.invalidateQueries({ queryKey: ['event-email-history', eventId] });
      await queryClient.invalidateQueries({ queryKey: ['event-email-history-failures'] });
      setEmailNotice({ kind: 'success', message: 'Email history entry deleted.' });
    },
    onError: (error: unknown) => {
      setEmailNotice({
        kind: 'error',
        message: error instanceof Error ? error.message : 'Could not delete email history entry.',
      });
    },
  });
  const deleteSmsHistoryMutation = useMutation({
    mutationFn: async (sendId: string) => {
      const { error: rpcError } = await supabase.rpc('delete_event_sms_send_history', { p_send_id: sendId });
      if (rpcError) throw new Error(rpcError.message);
      return sendId;
    },
    onSuccess: async (sendId) => {
      if (expandedSmsHistorySendId === sendId) {
        setExpandedSmsHistorySendId(null);
      }
      await queryClient.invalidateQueries({ queryKey: ['event-sms-history', eventId] });
      await queryClient.invalidateQueries({ queryKey: ['event-sms-history-failures'] });
      setSmsNotice({ kind: 'success', message: 'SMS history entry deleted.' });
    },
    onError: (error: unknown) => {
      setSmsNotice({
        kind: 'error',
        message: error instanceof Error ? error.message : 'Could not delete SMS history entry.',
      });
    },
  });
  const deleteRegistrationsMutation = useMutation({
    mutationFn: async (registrationIds: string[]) => {
      const uniqueIds = Array.from(new Set(registrationIds.map((id) => id.trim()).filter(Boolean)));
      if (!eventId || uniqueIds.length === 0) {
        throw new Error('Select at least one registrant to delete.');
      }
      const { data, error } = await supabase.functions.invoke('public-event-registration', {
        body: {
          action: 'delete_registrations',
          eventId,
          registrationIds: uniqueIds,
        },
      });
      const fnError = (data as { error?: string } | null)?.error;
      if (error || fnError) {
        const errorMessage =
          fnError || (error ? await extractFunctionErrorMessage(error, 'Could not delete registrant(s).') : '');
        throw new Error(errorMessage || 'Could not delete registrant(s).');
      }
      const deletedRegistrationIds = ((data as { data?: { deletedRegistrationIds?: string[] } } | null)?.data
        ?.deletedRegistrationIds || []) as string[];
      return deletedRegistrationIds.length > 0 ? deletedRegistrationIds : uniqueIds;
    },
    onSuccess: async (deletedIds) => {
      const deletedIdSet = new Set(deletedIds);
      setSelectedRecipientIds((current) => current.filter((id) => !deletedIdSet.has(id)));
      setTicketChangeRecipientIds((current) => current.filter((id) => !deletedIdSet.has(id)));
      setSelectedRegistration((current) => (current && deletedIdSet.has(current.id) ? null : current));
      await queryClient.invalidateQueries({ queryKey: ['public-event-registrations', eventId] });
      await queryClient.invalidateQueries({ queryKey: ['public-event-registration-tickets', eventId] });
      await queryClient.invalidateQueries({ queryKey: ['public-event-registrations-events'] });
      setRegistrationsNotice({
        kind: 'success',
        message: `Deleted ${deletedIds.length} registrant${deletedIds.length === 1 ? '' : 's'}.`,
      });
    },
    onError: (error: unknown) => {
      setRegistrationsNotice({
        kind: 'error',
        message: error instanceof Error ? error.message : 'Could not delete registrant(s).',
      });
    },
  });
  const manualCheckInMutation = useMutation({
    mutationFn: async (input: ManualCheckInInput) => {
      if (!eventId) {
        throw new Error('Event context is missing for this registrant.');
      }
      const { data, error } = await supabase.functions.invoke('public-event-ticket-check-in', {
        body: {
          eventId,
          registrationId: input.registration.id,
          ticketId: input.ticketId || undefined,
          checkInAllTickets: input.checkInAllTickets === true ? true : undefined,
          scannedBy: user?.id ?? null,
          source: 'manual',
        },
      });
      const fnError = (data as { error?: string } | null)?.error;
      if (error || fnError) {
        const errorMessage =
          fnError || (error ? await extractFunctionErrorMessage(error, 'Could not check in registrant.') : '');
        throw new Error(errorMessage || 'Could not check in registrant.');
      }
      const result = (data as { data?: RegistrationCheckInFunctionResult } | null)?.data;
      if (!result?.result) {
        throw new Error('Check-in response was missing a result.');
      }
      return {
        registrationId: input.registration.id,
        registrantName: input.registration.full_name || 'This registrant',
        displayName: input.displayName || input.registration.full_name || 'This registrant',
        checkInAllTickets: input.checkInAllTickets === true,
        ...result,
      };
    },
    onSuccess: async (result) => {
      await invalidateCheckinRows();
      const checkedInCount = Math.max(0, Number(result.checkedInCount || 0));
      const attendeeName = resolveCheckInAttendeeName(result, result.displayName || result.registrantName) || 'this attendee';
      if (result.result === 'duplicate') {
        setRegistrationsNotice({
          kind: 'info',
          message: `${attendeeName} is already checked in.`,
        });
        return;
      }
      if (result.result === 'cancelled') {
        setRegistrationsNotice({
          kind: 'info',
          message: `Cancelled tickets cannot be checked in for ${attendeeName}.`,
        });
        return;
      }
      if (result.result === 'invalid') {
        setRegistrationsNotice({
          kind: 'error',
          message: `Could not find a valid ticket for ${attendeeName}.`,
        });
        return;
      }
      setRegistrationsNotice({
        kind: 'success',
        message:
          result.checkInAllTickets && checkedInCount > 1
            ? `Checked in ${checkedInCount} tickets for ${result.registrantName}.`
            : `Checked in ${attendeeName}.`,
      });
    },
    onError: (error: unknown) => {
      setRegistrationsNotice({
        kind: 'error',
        message: error instanceof Error ? error.message : 'Could not check in registrant.',
      });
    },
  });
  const isRegistrationCancelled = (row: PublicEventRegistration) =>
    String(row.status || '').trim().toLowerCase() === 'cancelled';
  const requestManualCheckIn = (
    registration: PublicEventRegistration,
    options?: { ticketId?: string | null; checkInAllTickets?: boolean; displayName?: string },
  ) => {
    if (manualCheckInMutation.isPending || isRegistrationCancelled(registration)) return;
    void manualCheckInMutation.mutateAsync({
      registration,
      ticketId: options?.ticketId ?? null,
      checkInAllTickets: options?.checkInAllTickets === true,
      displayName: options?.displayName || registration.full_name,
    });
  };
  const handleDirectoryManualCheckIn = (
    event: React.MouseEvent,
    row: RegistrantDirectoryRow,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    const ticketId =
      row.ticket?.id ||
      (row.isPrimary ? checkinByRegistrationId.get(row.registration.id)?.primaryTicket?.id || null : null);
    requestManualCheckIn(row.registration, {
      ticketId,
      displayName: row.ticket?.attendee_name || row.fullName,
    });
  };
  const renderDirectoryCheckInCell = (row: RegistrantDirectoryRow) => {
    if (isRegistrationCancelled(row.registration)) return 'No';
    if (isTicketCancelled(row.ticket)) return 'Cancelled';
    if (isTicketCheckedIn(row.ticket)) {
      return row.ticket?.checked_in_at
        ? new Date(String(row.ticket.checked_in_at)).toLocaleString()
        : 'Yes';
    }
    const ticketId =
      row.ticket?.id ||
      (row.isPrimary ? checkinByRegistrationId.get(row.registration.id)?.primaryTicket?.id || null : null);
    if (!ticketId) return '—';
    const isPending =
      manualCheckInMutation.isPending &&
      manualCheckInMutation.variables?.registration.id === row.registration.id &&
      !manualCheckInMutation.variables?.checkInAllTickets &&
      (manualCheckInMutation.variables?.ticketId || null) === ticketId;
    return (
      <ListCheckInButton
        type="button"
        aria-label={`Check in ${row.fullName || 'attendee'}`}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => handleDirectoryManualCheckIn(event, row)}
        disabled={manualCheckInMutation.isPending}
      >
        {isPending ? 'Checking in…' : 'Check in'}
      </ListCheckInButton>
    );
  };
  const editRegistrantMutation = useMutation({
    mutationFn: async (payload: {
      registrationId: string;
      fullName: string;
      email: string;
      phone: string;
      notes: string;
      quantity: number;
      formResponse: Record<string, unknown>;
    }) => {
      if (!eventId) {
        throw new Error('Event is required to edit a registrant.');
      }
      const { data, error } = await supabase.functions.invoke('public-event-registration', {
        body: {
          action: 'edit_registration',
          eventId,
          registrationId: payload.registrationId,
          fullName: payload.fullName.trim(),
          email: payload.email.trim(),
          phone: payload.phone.trim(),
          notes: payload.notes,
          quantity: payload.quantity,
          formResponse: payload.formResponse,
        },
      });
      const fnError = (data as { error?: string } | null)?.error;
      if (error || fnError) {
        const errorMessage =
          fnError || (error ? await extractFunctionErrorMessage(error, 'Could not update registrant.') : '');
        throw new Error(errorMessage || 'Could not update registrant.');
      }
      const updatedRow = (data as { data?: { registration?: Record<string, unknown> } } | null)?.data?.registration;
      if (!updatedRow) {
        throw new Error('Registrant update did not return a record.');
      }
      return mapRegistrationRow(updatedRow);
    },
    onSuccess: async (updatedRegistration) => {
      setSelectedRegistration((current) =>
        current?.id === updatedRegistration.id
          ? {
              ...updatedRegistration,
              events: updatedRegistration.events ?? current.events ?? null,
            }
          : current
      );
      setEditingRegistrant(null);
      setEditRegistrantModalOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['public-event-registrations', eventId] });
      await queryClient.invalidateQueries({ queryKey: ['public-event-registration-tickets', eventId] });
      await queryClient.invalidateQueries({ queryKey: ['public-event-registrations-events'] });
      setRegistrationsNotice({
        kind: 'success',
        message: 'Registrant details updated.',
      });
    },
    onError: () => {
      // Errors are surfaced inside EventRegistrantEditModal.
    },
  });

  const ticketChangeMutation = useMutation({
    mutationFn: async (args: {
      registrationIds: string[];
      targetTicketTypeId: string;
      note: string;
      paymentProvider: 'paypal' | 'square';
    }) => {
      const uniqueIds = Array.from(new Set(args.registrationIds.filter(Boolean)));
      const quotes: TicketChangeQuote[] = [];
      const approvalLinks: Array<{ registrationId: string; approvalUrl: string; provider: 'paypal' | 'square' }> = [];
      const manualRefundIds: string[] = [];

      for (const registrationId of uniqueIds) {
        const { data: quoteData, error: quoteError } = await supabase.functions.invoke('public-event-registration', {
          body: {
            action: 'quote_ticket_change',
            eventId,
            registrationId,
            targetTicketTypeId: args.targetTicketTypeId,
          },
        });
        const quoteFnError = (quoteData as { error?: string } | null)?.error;
        const quotePayload = (quoteData as { data?: TicketChangeQuote } | null)?.data;
        if (quoteError || quoteFnError || !quotePayload) {
          const errorMessage = quoteFnError
            || (quoteError ? await extractFunctionErrorMessage(quoteError, 'Could not quote ticket change.') : '');
          throw new Error(errorMessage || 'Could not quote ticket change.');
        }
        quotes.push(quotePayload);

        const { data: initiateData, error: initiateError } = await supabase.functions.invoke('public-event-registration', {
          body: {
            action: 'initiate_ticket_change',
            eventId,
            registrationId,
            targetTicketTypeId: args.targetTicketTypeId,
            note: args.note || null,
          },
        });
        const initiateFnError = (initiateData as { error?: string } | null)?.error;
        const initiatePayload = (initiateData as { data?: Record<string, unknown> } | null)?.data;
        if (initiateError || initiateFnError || !initiatePayload) {
          const errorMessage = initiateFnError
            || (initiateError ? await extractFunctionErrorMessage(initiateError, 'Could not start ticket change.') : '');
          throw new Error(errorMessage || 'Could not start ticket change.');
        }
        const changeRequestId = String(initiatePayload.changeRequestId || '').trim();
        const requiresPayment = Boolean(initiatePayload.requiresPayment);
        const requiresManualRefund = Boolean(initiatePayload.requiresManualRefund);
        if (requiresManualRefund) {
          manualRefundIds.push(changeRequestId);
          continue;
        }
        if (requiresPayment && changeRequestId) {
          const returnUrl = new URL(window.location.href);
          returnUrl.searchParams.set(args.paymentProvider === 'square' ? 'square' : 'paypal', 'success');
          returnUrl.searchParams.set('paymentMethod', args.paymentProvider);
          returnUrl.searchParams.set('eventTicketChange', '1');
          const cancelUrl = new URL(window.location.href);
          cancelUrl.searchParams.set(args.paymentProvider === 'square' ? 'square' : 'paypal', 'cancel');
          cancelUrl.searchParams.set('paymentMethod', args.paymentProvider);
          cancelUrl.searchParams.set('eventTicketChange', '1');
          const functionName = args.paymentProvider === 'square'
            ? 'public-event-square-create-checkout'
            : 'public-event-paypal-create-order';
          const { data: orderData, error: orderError } = await supabase.functions.invoke(functionName, {
            body: args.paymentProvider === 'square'
              ? {
                  changeRequestId,
                  returnUrl: returnUrl.toString(),
                }
              : {
                  changeRequestId,
                  returnUrl: returnUrl.toString(),
                  cancelUrl: cancelUrl.toString(),
                },
          });
          const orderFnError = (orderData as { error?: string } | null)?.error;
          const orderPayload = (orderData as { data?: Record<string, unknown> } | null)?.data;
          if (orderError || orderFnError || !orderPayload) {
            const errorMessage = orderFnError
              || (orderError ? await extractFunctionErrorMessage(orderError, 'Could not create PayPal order.') : '');
            throw new Error(errorMessage || 'Could not create PayPal order.');
          }
          const approvalUrl = String(orderPayload.checkoutUrl || orderPayload.approvalUrl || '').trim();
          if (approvalUrl) {
            approvalLinks.push({
              registrationId,
              approvalUrl,
              provider: args.paymentProvider,
            });
          }
        }
      }

      return { quotes, approvalLinks, manualRefundIds };
    },
    onSuccess: async (result) => {
      setTicketChangeQuotes(result.quotes);
      setTicketChangeApprovalLinks(result.approvalLinks);
      setTicketChangeManualRequestIds(result.manualRefundIds.filter(Boolean));
      if (result.manualRefundIds.length > 0) {
        setTicketChangeNotice({
          kind: 'info',
          message: `${result.manualRefundIds.length} downgrade request(s) are now pending manual refund completion.`,
        });
      } else if (result.approvalLinks.length > 0) {
        setTicketChangeNotice({
          kind: 'success',
          message: 'Ticket change initiated. Use the payment links below to collect payment differences.',
        });
      } else {
        setTicketChangeNotice({
          kind: 'success',
          message: 'Ticket change completed successfully.',
        });
      }
      await queryClient.invalidateQueries({ queryKey: ['public-event-registrations', eventId] });
      await queryClient.invalidateQueries({ queryKey: ['public-event-registration-tickets', eventId] });
      await queryClient.invalidateQueries({ queryKey: ['event-ticket-change-history', eventId] });
    },
    onError: (error: unknown) => {
      setTicketChangeNotice({
        kind: 'error',
        message: error instanceof Error ? error.message : 'Could not change ticket type.',
      });
    },
  });

  const completeManualDowngradeMutation = useMutation({
    mutationFn: async (args: { changeRequestIds: string[]; note: string }) => {
      for (const changeRequestId of args.changeRequestIds.filter(Boolean)) {
        const { data, error } = await supabase.functions.invoke('public-event-registration', {
          body: {
            action: 'complete_manual_downgrade',
            eventId,
            changeRequestId,
            note: args.note || null,
          },
        });
        const fnError = (data as { error?: string } | null)?.error;
        if (error || fnError) {
          const errorMessage = fnError
            || (error ? await extractFunctionErrorMessage(error, 'Could not complete manual downgrade.') : '');
          throw new Error(errorMessage || 'Could not complete manual downgrade.');
        }
      }
      return true;
    },
    onSuccess: async () => {
      setTicketChangeManualRequestIds([]);
      setTicketChangeNotice({ kind: 'success', message: 'Manual refund downgrade marked complete.' });
      await queryClient.invalidateQueries({ queryKey: ['public-event-registrations', eventId] });
      await queryClient.invalidateQueries({ queryKey: ['public-event-registration-tickets', eventId] });
      await queryClient.invalidateQueries({ queryKey: ['event-ticket-change-history', eventId] });
    },
    onError: (error: unknown) => {
      setTicketChangeNotice({
        kind: 'error',
        message: error instanceof Error ? error.message : 'Could not complete manual downgrade.',
      });
    },
  });

  const manualLinkPreviousOrdersMutation = useMutation({
    mutationFn: async () => {
      const rows = registrations?.rows ?? [];
      const unlinkedRows = rows.filter(
        (row) => !row.user_id && /^\S+@\S+\.\S+$/.test(String(row.email || '').trim())
      );
      if (unlinkedRows.length === 0) {
        return { inspected: 0, linked: 0, skipped: 0 };
      }

      const candidateEmails = Array.from(
        new Set(unlinkedRows.map((row) => String(row.email || '').trim().toLowerCase()).filter(Boolean))
      );
      const { data: userRows, error: userLookupError } = await supabase
        .from('users')
        .select('id, email')
        .in('email', candidateEmails);
      if (userLookupError) {
        throw new Error(userLookupError.message || 'Could not load user accounts for matching.');
      }

      const userIdByEmail = new Map<string, string>();
      for (const row of (userRows ?? []) as Array<{ id?: string | null; email?: string | null }>) {
        const emailKey = String(row.email || '').trim().toLowerCase();
        const userId = String(row.id || '').trim();
        if (!emailKey || !userId) continue;
        if (!userIdByEmail.has(emailKey)) {
          userIdByEmail.set(emailKey, userId);
        }
      }

      let linked = 0;
      let skipped = 0;
      for (const row of unlinkedRows) {
        const normalizedEmail = String(row.email || '').trim().toLowerCase();
        const targetUserId = userIdByEmail.get(normalizedEmail);
        if (!targetUserId) {
          skipped += 1;
          continue;
        }

        const { data, error } = await supabase.functions.invoke('public-event-registration', {
          body: {
            action: 'attach_user',
            registrationId: row.id,
            email: row.email,
            userId: targetUserId,
          },
        });
        const fnError = (data as { error?: string } | null)?.error;
        if (error || fnError) {
          const errorMessage = fnError || (error ? await extractFunctionErrorMessage(error, 'Could not link previous orders.') : '');
          throw new Error(errorMessage || 'Could not link previous orders.');
        }
        linked += 1;
      }

      return { inspected: unlinkedRows.length, linked, skipped };
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['public-event-registrations', eventId] });
      await queryClient.invalidateQueries({ queryKey: ['public-event-registration-tickets', eventId] });
      if (result.inspected === 0) {
        setEmailNotice({ kind: 'info', message: 'No unlinked orders found for this event.' });
        return;
      }
      if (result.linked === 0) {
        setEmailNotice({
          kind: 'info',
          message: 'No matching account was found by email for these orders yet.',
        });
        return;
      }
      setEmailNotice({
        kind: 'success',
        message: `Linked ${result.linked} previous order${result.linked === 1 ? '' : 's'}${result.skipped > 0 ? ` (${result.skipped} skipped)` : ''}.`,
      });
    },
    onError: (error: unknown) => {
      setEmailNotice({
        kind: 'error',
        message: error instanceof Error ? error.message : 'Could not link previous orders.',
      });
    },
  });

  const backfillMobileOrdersMutation = useMutation({
    mutationFn: async () => {
      if (!eventId) {
        throw new Error('Missing event id for mobile order backfill.');
      }

      const { data, error } = await supabase.functions.invoke('public-event-registration', {
        body: {
          action: 'backfill_mobile_orders',
          eventId,
        },
      });
      const fnError = (data as { error?: string } | null)?.error;
      if (error || fnError) {
        const errorMessage = fnError || (error ? await extractFunctionErrorMessage(error, 'Could not backfill mobile orders.') : '');
        throw new Error(errorMessage || 'Could not backfill mobile orders.');
      }

      return ((data as { data?: {
        eventId?: string;
        inspected?: number;
        synced?: number;
        createdRegistrations?: number;
        updatedRegistrations?: number;
        createdTickets?: number;
        failed?: number;
      } } | null)?.data) ?? {};
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['public-event-registrations', eventId] });
      await queryClient.invalidateQueries({ queryKey: ['public-event-registration-tickets', eventId] });
      await queryClient.invalidateQueries({ queryKey: ['event-setup-reporting-registrations', eventId] });
      await queryClient.invalidateQueries({ queryKey: ['event-setup-reporting-registration-tickets', eventId] });

      const inspected = Math.max(0, Number(result.inspected || 0));
      const synced = Math.max(0, Number(result.synced || 0));
      const createdRegistrations = Math.max(0, Number(result.createdRegistrations || 0));
      const updatedRegistrations = Math.max(0, Number(result.updatedRegistrations || 0));
      const createdTickets = Math.max(0, Number(result.createdTickets || 0));
      const failed = Math.max(0, Number(result.failed || 0));

      if (inspected === 0) {
        setEmailNotice({ kind: 'info', message: 'No successful mobile orders were found for this event.' });
        return;
      }

      const detail = `Backfilled ${synced} mobile attendee${synced === 1 ? '' : 's'}; ${createdRegistrations} created, ${updatedRegistrations} updated, ${createdTickets} ticket${createdTickets === 1 ? '' : 's'} issued${failed > 0 ? `, ${failed} failed` : ''}.`;
      setEmailNotice({
        kind: failed > 0 ? 'info' : 'success',
        message: detail,
      });
    },
    onError: (error: unknown) => {
      setEmailNotice({
        kind: 'error',
        message: error instanceof Error ? error.message : 'Could not backfill mobile orders.',
      });
    },
  });

  const stopScanner = React.useCallback(() => {
    if (scanTimerRef.current) {
      window.clearInterval(scanTimerRef.current);
      scanTimerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setScannerOpen(false);
  }, []);

  const attachStreamToVideo = React.useCallback(async () => {
    const video = videoRef.current;
    const stream = streamRef.current;
    if (!video || !stream) return;
    if (video.srcObject !== stream) {
      video.srcObject = stream;
    }
    try {
      await video.play();
    } catch {
      // Some browsers require a short delay before the stream is playable.
    }
  }, []);

  const submitCheckIn = React.useCallback(async (value: string) => {
    const scanInput = value.trim();
    if (!scanInput || !eventId) return;
    setCheckingIn(true);
    setScannerStatus('');
    const { data, error } = await supabase.functions.invoke('public-event-ticket-check-in', {
      body: {
        eventId,
        scanValue: scanInput,
        scannedBy: user?.id ?? null,
        source: 'web',
      },
    });
    setCheckingIn(false);
    const fnError = (data as { error?: string } | null)?.error;
    if (error || fnError) {
      setScannerStatus(fnError || error?.message || 'Could not check in this ticket.');
      return;
    }
    const payload = ((data as { data?: RegistrationCheckInFunctionResult } | null)?.data || null);
    const result = String(payload?.result || '').toLowerCase();
    const attendeeName = resolveCheckInAttendeeName(payload);
    if (result === 'accepted') {
      setScannerStatus(attendeeName ? `Checked in ${attendeeName}.` : 'Ticket accepted and checked in.');
      void invalidateCheckinRows();
    } else if (result === 'duplicate') {
      setScannerStatus(attendeeName ? `${attendeeName} was already checked in.` : 'This ticket was already checked in.');
      void invalidateCheckinRows();
    } else if (result === 'cancelled') {
      setScannerStatus(attendeeName ? `${attendeeName}'s ticket is cancelled.` : 'This ticket is cancelled.');
    } else {
      setScannerStatus('Invalid ticket code.');
    }
  }, [eventId, invalidateCheckinRows, user?.id]);

  const submitCheckInFromInput = React.useCallback(
    async (value: string) => {
      const trimmed = value.trim();
      if (!trimmed || checkingIn) return;
      await submitCheckIn(trimmed);
    },
    [checkingIn, submitCheckIn]
  );

  const startScanner = React.useCallback(async () => {
    if (!navigator?.mediaDevices?.getUserMedia) {
      setScannerStatus('Camera is not available in this browser. Use manual QR code entry.');
      return;
    }
    stopScanner();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
      });
      streamRef.current = stream;
      setScannerOpen(true);
      setScannerStatus('Camera started. Point it at a QR code.');
      await attachStreamToVideo();

      const DetectorCtor = (window as any).BarcodeDetector;
      if (!DetectorCtor) {
        setScannerStatus('Camera started, but QR detection is unsupported here. Use manual entry.');
        return;
      }
      const detector = new DetectorCtor({ formats: ['qr_code'] });
      scanTimerRef.current = window.setInterval(async () => {
        if (!videoRef.current) return;
        try {
          const detected = await detector.detect(videoRef.current);
          if (Array.isArray(detected) && detected.length > 0) {
            const rawValue = String(detected[0]?.rawValue || '').trim();
            if (rawValue) {
              setScanValue(rawValue);
              setScannerStatus('QR detected. Checking in...');
              stopScanner();
              await submitCheckIn(rawValue);
            }
          }
        } catch {
          // Ignore intermittent detection errors while stream is warming up.
        }
      }, 600);
    } catch (error: any) {
      setScannerStatus(error?.message || 'Unable to access camera.');
    }
  }, [attachStreamToVideo, stopScanner, submitCheckIn]);

  React.useEffect(() => {
    if (!scannerOpen) return;
    void attachStreamToVideo();
  }, [attachStreamToVideo, scannerOpen]);

  React.useEffect(() => () => stopScanner(), [stopScanner]);

  React.useEffect(() => {
    const rows = registrationsAfterPaymentFilter;
    if (rows.length === 0) {
      setSelectedRecipientIds((current) => (current.length > 0 ? [] : current));
      return;
    }
    const validIds = new Set(rows.map((row) => row.id));
    setSelectedRecipientIds((current) => current.filter((id) => validIds.has(id)));
  }, [registrationsAfterPaymentFilter]);

  React.useEffect(() => {
    setRegistrantListPage(1);
  }, [searchQuery, ticketTypeFilter, statusFilter, appointmentSlotFilter, tshirtSizeFilter, showPendingPayments]);

  React.useEffect(() => {
    setRegistrantListPage((current) => Math.min(current, registrantPageCount));
  }, [registrantPageCount]);

  React.useEffect(() => {
    setNoShowListPage((current) => Math.min(current, noShowPageCount));
  }, [noShowPageCount]);

  const openBulkComposer = React.useCallback(() => {
    if (!eventId) return;
    const params = new URLSearchParams();
    params.set('mode', 'bulk');
    params.set('scope', selectedRecipientIds.length > 0 ? 'selected' : 'filtered');
    if (selectedRecipientIds.length > 0) {
      params.set('selected', selectedRecipientIds.join(','));
    }
    params.set('returnTo', `${location.pathname}${location.search}`);
    navigate(`/events/public-registrations/${eventId}/email?${params.toString()}`);
  }, [eventId, location.pathname, location.search, navigate, selectedRecipientIds]);

  const openSingleComposer = React.useCallback((registrationId: string) => {
    if (!eventId) return;
    const params = new URLSearchParams();
    params.set('mode', 'single');
    params.set('registrationId', registrationId);
    params.set('returnTo', `${location.pathname}${location.search}`);
    navigate(`/events/public-registrations/${eventId}/email?${params.toString()}`);
  }, [eventId, location.pathname, location.search, navigate]);

  const openBulkSmsComposer = React.useCallback(() => {
    if (!eventId) return;
    const params = new URLSearchParams();
    params.set('mode', 'bulk');
    params.set('scope', selectedRecipientIds.length > 0 ? 'selected' : 'filtered');
    if (selectedRecipientIds.length > 0) {
      params.set('selected', selectedRecipientIds.join(','));
    }
    params.set('returnTo', `${location.pathname}${location.search}`);
    navigate(`/events/public-registrations/${eventId}/sms?${params.toString()}`);
  }, [eventId, location.pathname, location.search, navigate, selectedRecipientIds]);

  const openSingleSmsComposer = React.useCallback((registrationId: string) => {
    if (!eventId) return;
    const params = new URLSearchParams();
    params.set('mode', 'single');
    params.set('registrationId', registrationId);
    params.set('returnTo', `${location.pathname}${location.search}`);
    navigate(`/events/public-registrations/${eventId}/sms?${params.toString()}`);
  }, [eventId, location.pathname, location.search, navigate]);

  const toggleRecipientSelection = React.useCallback((registrationId: string, checked: boolean) => {
    setSelectedRecipientIds((current) => {
      if (checked) {
        if (current.includes(registrationId)) return current;
        return [...current, registrationId];
      }
      return current.filter((id) => id !== registrationId);
    });
  }, []);

  const selectAllFiltered = React.useCallback(() => {
    setSelectedRecipientIds((current) => {
      const next = new Set(current);
      for (const row of filteredRegistrations) next.add(row.id);
      return Array.from(next);
    });
  }, [filteredRegistrations]);

  const clearSelected = React.useCallback(() => setSelectedRecipientIds([]), []);

  const requestDeleteRegistrants = React.useCallback(
    (registrationIds: string[]) => {
      const uniqueIds = Array.from(new Set(registrationIds.map((id) => id.trim()).filter(Boolean)));
      if (uniqueIds.length === 0) {
        setRegistrationsNotice({ kind: 'info', message: 'Select one or more registrants first.' });
        return;
      }
      const checkedInCount = uniqueIds.filter((id) => (checkinByRegistrationId.get(id)?.checkedInCount || 0) > 0).length;
      if (checkedInCount > 0) {
        setRegistrationsNotice({
          kind: 'error',
          message: `Cannot delete ${checkedInCount} checked-in registrant${checkedInCount === 1 ? '' : 's'}.`,
        });
        return;
      }
      const registrantCount = uniqueIds.length;
      if (registrantCount > 1) {
        const phrase = `DELETE ${registrantCount}`;
        const typedValue = window.prompt(
          `Type "${phrase}" to confirm deleting ${registrantCount} registrants. This cannot be undone.`,
          ''
        );
        if (typedValue !== phrase) {
          setRegistrationsNotice({
            kind: 'info',
            message: 'Bulk delete cancelled because the confirmation phrase did not match.',
          });
          return;
        }
      }
      const confirmed = window.confirm(
        `Delete ${registrantCount} registrant${registrantCount === 1 ? '' : 's'}? This permanently removes their registration record and cannot be undone.`
      );
      if (!confirmed) return;
      setRegistrationsNotice(null);
      void deleteRegistrationsMutation.mutateAsync(uniqueIds);
    },
    [checkinByRegistrationId, deleteRegistrationsMutation]
  );

  const openTicketChangeModal = React.useCallback((registrationId?: string) => {
    const targetIds = registrationId ? [registrationId] : selectedRecipientIds;
    if (targetIds.length === 0) {
      setTicketChangeNotice({ kind: 'info', message: 'Select one or more registrants first.' });
      return;
    }
    setTicketChangeRecipientIds(Array.from(new Set(targetIds)));
    setTicketChangeTargetTicketId('');
    setTicketChangeNote('');
    setTicketChangeQuotes([]);
    setTicketChangeApprovalLinks([]);
    setTicketChangeManualRequestIds([]);
    setTicketChangeModalOpen(true);
    setTicketChangeNotice(null);
  }, [selectedRecipientIds]);
  const openEditRegistrantModal = React.useCallback((registration: PublicEventRegistration) => {
    setEditingRegistrant(registration);
    setEditRegistrantModalOpen(true);
    setRegistrationsNotice(null);
  }, []);

  const toggleTshirtGivenMutation = useMutation({
    mutationFn: async (args: { attendeeKey: string; registrationId: string | null; given: boolean }) => {
      if (!eventId) {
        throw new Error('Event context is missing for this T-shirt row.');
      }
      if (args.given) {
        const { error } = await supabase.from('event_tshirt_handouts').upsert(
          {
            event_id: eventId,
            attendee_key: args.attendeeKey,
            registration_id: args.registrationId || null,
            given_by: user?.id ?? null,
            given_at: new Date().toISOString(),
          },
          { onConflict: 'event_id,attendee_key' }
        );
        if (error) {
          if (isMissingTshirtHandoutsTableError(error)) {
            const next = new Set(readGivenTshirtKeysFromStorage(eventId));
            next.add(args.attendeeKey);
            writeGivenTshirtKeysToStorage(eventId, Array.from(next));
            return { attendeeKey: args.attendeeKey, given: true };
          }
          throw new Error(error.message);
        }
        return { attendeeKey: args.attendeeKey, given: true };
      }
      const { error } = await supabase
        .from('event_tshirt_handouts')
        .delete()
        .eq('event_id', eventId)
        .eq('attendee_key', args.attendeeKey);
      if (error) {
        if (isMissingTshirtHandoutsTableError(error)) {
          const next = new Set(readGivenTshirtKeysFromStorage(eventId));
          next.delete(args.attendeeKey);
          writeGivenTshirtKeysToStorage(eventId, Array.from(next));
          return { attendeeKey: args.attendeeKey, given: false };
        }
        throw new Error(error.message);
      }
      return { attendeeKey: args.attendeeKey, given: false };
    },
    onMutate: async (args) => {
      await queryClient.cancelQueries({ queryKey: ['event-tshirt-handouts', eventId] });
      const previous = queryClient.getQueryData<EventTshirtHandoutRow[]>(['event-tshirt-handouts', eventId]);
      queryClient.setQueryData<EventTshirtHandoutRow[]>(['event-tshirt-handouts', eventId], (current = []) => {
        if (args.given) {
          if (current.some((row) => row.attendee_key === args.attendeeKey)) return current;
          return [...current, { attendee_key: args.attendeeKey, given_at: new Date().toISOString() }];
        }
        return current.filter((row) => row.attendee_key !== args.attendeeKey);
      });
      return { previous };
    },
    onError: (error: unknown, _args, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['event-tshirt-handouts', eventId], context.previous);
      }
      setRegistrationsNotice({
        kind: 'error',
        message: error instanceof Error ? error.message : 'Could not update T-shirt given status.',
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['event-tshirt-handouts', eventId] });
    },
  });

  const exportTshirtSizeReport = React.useCallback(() => {
    if (filteredTshirtRows.length === 0) {
      setRegistrationsNotice({ kind: 'info', message: 'No T-shirt sizes to export.' });
      return;
    }
    const summaryLines = [
      'Size,Count',
      ...tshirtSizeSummary.map((entry) => `${toCsvValue(entry.size)},${toCsvValue(entry.count)}`),
      `Total,${toCsvValue(tshirtSizeTotal)}`,
    ];
    const detailLines = [
      'Given,Registrant,Attendee,Email,Phone,Size',
      ...filteredTshirtRows.map((row) =>
        [
          givenTshirtKeySet.has(row.key) ? 'Yes' : 'No',
          row.registrantName,
          row.attendeeName,
          row.email,
          row.phone,
          row.size,
        ]
          .map(toCsvValue)
          .join(',')
      ),
    ];
    const csvContent = [...summaryLines, '', ...detailLines].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const dateStamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `event-tshirt-sizes-${eventId || 'export'}-${dateStamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setRegistrationsNotice({ kind: 'success', message: 'Exported T-shirt size report to CSV.' });
  }, [eventId, filteredTshirtRows, givenTshirtKeySet, tshirtSizeSummary, tshirtSizeTotal]);

  const exportFilteredRegistrants = React.useCallback(() => {
    if (filteredDirectoryRows.length === 0) {
      setEmailNotice({ kind: 'info', message: 'No filtered registrants to export.' });
      return;
    }
    const exportRows = buildRegistrantExportAttendeeRows(filteredDirectoryRows);
    const baseHeaders = [
      'registration_id',
      'event_id',
      'event_title',
      'event_start_date',
      'event_start_time',
      'event_location',
      'user_id',
      'is_primary',
      'attendee_index',
      'primary_registrant',
      'full_name',
      'email',
      'phone',
      'ticket_number',
      'ticket_type_id',
      'ticket_type_name',
      'quantity',
      'status',
      'payment_status',
      'source',
      'checked_in',
      'checkin_time',
      'submitted_at',
      'total_amount_cents',
      'amount_paid_cents',
      'balance_due_cents',
      'payment_amount_cents',
      'notes',
      'appointment_slot_ids',
      'appointment_labels',
      'appointment_schedule',
    ];
    const formHeaders = registrantExportShape.formKeys.map(
      (key) => `form_${normalizeResponseKey(key) || key}`
    );
    const headers = [...baseHeaders, ...formHeaders];
    const csvRows = exportRows.map((exportRow) => {
      const row = exportRow.registration;
      const responseRecord = getFormResponseRecord(row.form_response);
      const appointmentSummary = formatAppointmentSummary(row.appointments || []);
      const isPrimary = exportRow.isPrimary;
      const values = [
        toCsvValue(row.id ?? ''),
        toCsvValue(row.event_id ?? ''),
        toCsvValue(row.events?.title ?? ''),
        toCsvValue(row.events?.start_date ?? ''),
        toCsvValue(row.events?.start_time ?? ''),
        toCsvValue(row.events?.location ?? ''),
        toCsvValue(row.user_id ?? ''),
        toCsvValue(isPrimary ? 'yes' : 'no'),
        toCsvValue(exportRow.attendeeIndex + 1),
        toCsvValue(exportRow.primaryRegistrantName),
        toCsvValue(exportRow.fullName),
        toCsvValue(exportRow.email),
        toCsvValue(exportRow.phone),
        toCsvValue(exportRow.ticketNumber),
        toCsvValue(row.ticket_type_id ?? ''),
        toCsvValue(row.ticket_type_name ?? 'General'),
        toCsvValue(exportRow.quantity),
        toCsvValue(row.status ?? ''),
        toCsvValue(row.payment_status ?? ''),
        toCsvValue(row.source ?? ''),
        toCsvValue(exportRow.checkedIn ? 'yes' : 'no'),
        toCsvValue(exportRow.checkinTime ? new Date(exportRow.checkinTime).toISOString() : ''),
        toCsvValue(row.created_at ? new Date(row.created_at).toISOString() : ''),
        toCsvValue(isPrimary ? row.total_amount_cents ?? '' : ''),
        toCsvValue(isPrimary ? row.amount_paid_cents ?? '' : ''),
        toCsvValue(isPrimary ? row.balance_due_cents ?? '' : ''),
        toCsvValue(isPrimary ? row.payment_amount_cents ?? '' : ''),
        toCsvValue(isPrimary ? row.notes ?? '' : ''),
        toCsvValue((row.appointments || []).map((appointment) => appointment.slot_id).filter(Boolean).join('; ')),
        toCsvValue((row.appointments || []).map((appointment) => appointment.label).filter(Boolean).join('; ')),
        toCsvValue(appointmentSummary),
        ...registrantExportShape.formKeys.map((key) => {
          const attendeeValue = getValueByNormalizedKey(exportRow.responses, key);
          const value = attendeeValue !== undefined ? attendeeValue : getValueByNormalizedKey(responseRecord, key);
          return toCsvValue(formatCustomResponseValue(value));
        }),
      ];

      return values.join(',');
    });
    const csvContent = [headers.join(','), ...csvRows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const dateStamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `event-registrants-${eventId || 'export'}-${dateStamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setEmailNotice({
      kind: 'success',
      message: `Exported ${exportRows.length} attendee${exportRows.length === 1 ? '' : 's'} to CSV.`,
    });
  }, [eventId, filteredDirectoryRows, registrantExportShape]);

  const resolveRecipientIds = React.useCallback(
    (scope: ComposerRecipientScope | SmsComposerRecipientScope, singleRecipientId: string | null): string[] => {
      if (scope === 'single') {
        if (!singleRecipientId) return [];
        return [singleRecipientId];
      }
      if (scope === 'selected') {
        return selectedRecipientIds;
      }
      if (scope === 'filtered') {
        return filteredRegistrations.map((row) => row.id);
      }
      return (registrations?.rows || []).map((row) => row.id);
    },
    [selectedRecipientIds, filteredRegistrations, registrations]
  );

  const composerCounts = React.useMemo(
    () => ({
      single: singleEmailRecipientId || singleSmsRecipientId ? 1 : 0,
      all: registrations?.rows?.length ?? 0,
      filtered: filteredRegistrations.length,
      selected: selectedRecipientIds.length,
    }),
    [singleEmailRecipientId, singleSmsRecipientId, registrations, filteredRegistrations.length, selectedRecipientIds.length]
  );

  const currentSingleRecipient = React.useMemo(() => {
    if (!singleEmailRecipientId) return null;
    return (registrations?.rows || []).find((row) => row.id === singleEmailRecipientId) ?? null;
  }, [singleEmailRecipientId, registrations]);

  const currentSingleSmsRecipient = React.useMemo(() => {
    if (!singleSmsRecipientId) return null;
    return (registrations?.rows || []).find((row) => row.id === singleSmsRecipientId) ?? null;
  }, [singleSmsRecipientId, registrations]);

  const historyStatusTone = (status: 'queued' | 'sent' | 'partial_failure' | 'failed') => {
    if (status === 'failed') return 'bad';
    if (status === 'partial_failure') return 'warn';
    if (status === 'sent') return 'ok';
    return 'muted';
  };

  const emailHistorySection = (
    <HistoryCard>
      <HistoryHeader $collapsed={!emailHistorySectionOpen}>
        <div style={{ minWidth: 0 }}>
          <HistoryTitle id="email-history-heading">Communication history</HistoryTitle>
          <SearchHint style={{ marginTop: '0.2rem', display: 'block' }}>
            Recent email and SMS sends, scopes, and delivery outcomes
          </SearchHint>
        </div>
        <HistoryCollapseButton
          type="button"
          onClick={() => setEmailHistorySectionOpen((open) => !open)}
          aria-expanded={emailHistorySectionOpen}
          aria-controls="email-history-panel"
        >
          {emailHistorySectionOpen ? 'Collapse' : 'Expand'}
        </HistoryCollapseButton>
      </HistoryHeader>
      {emailHistorySectionOpen ? (
        <HistoryList id="email-history-panel" role="region" aria-labelledby="email-history-heading">
        <HistoryChannelHeading>Email</HistoryChannelHeading>
        {isLoadingEmailHistory ? <HistoryRow>Loading email history...</HistoryRow> : null}
        {!isLoadingEmailHistory && emailHistoryError ? (
          <HistoryRow style={{ color: 'var(--error)' }}>Could not load email history.</HistoryRow>
        ) : null}
        {!isLoadingEmailHistory && !emailHistoryError && emailHistory.length === 0 ? (
          <HistoryRow>No email sends yet for this event.</HistoryRow>
        ) : null}
        {!isLoadingEmailHistory && !emailHistoryError
          ? emailHistory.map((row) => {
              const statusTone = historyStatusTone(row.status);
              const isExpanded = expandedHistorySendId === row.id;
              return (
                <HistoryRow key={row.id}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.6rem' }}>
                    <strong style={{ color: 'var(--onSurface)', fontSize: '0.92rem' }}>
                      {row.subject || '(No subject)'}
                    </strong>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.45rem',
                        flexWrap: 'wrap',
                        justifyContent: 'flex-end',
                      }}
                    >
                      {row.failed_count > 0 ? (
                        <HistoryToggleButton
                          type="button"
                          onClick={() => setExpandedHistorySendId(isExpanded ? null : row.id)}
                        >
                          {isExpanded ? 'Hide failures' : `Failures (${row.failed_count})`}
                        </HistoryToggleButton>
                      ) : null}
                      <HistoryDangerButton
                        type="button"
                        disabled={deleteEmailHistoryMutation.isPending}
                        onClick={() => {
                          const confirmed = window.confirm(
                            `Delete this email history entry${row.subject ? ` for "${row.subject}"` : ''}? This cannot be undone.`
                          );
                          if (!confirmed) return;
                          void deleteEmailHistoryMutation.mutateAsync(row.id);
                        }}
                      >
                        {deleteEmailHistoryMutation.isPending ? 'Deleting...' : 'Delete'}
                      </HistoryDangerButton>
                      <StatusPill $tone={statusTone}>{row.status.replace('_', ' ')}</StatusPill>
                    </div>
                  </div>
                  <HistoryMeta>
                    <span>{new Date(row.created_at).toLocaleString()}</span>
                    <SendModeBadge>{row.send_mode === 'bulk' ? 'Bulk send' : 'Single'}</SendModeBadge>
                    <span>Scope: {row.recipient_scope}</span>
                    <span>Sent: {row.sent_count}</span>
                    <span>Failed: {row.failed_count}</span>
                    <span>Skipped: {row.skipped_count}</span>
                  </HistoryMeta>
                  {row.error_summary ? (
                    <HistoryMeta>
                      <span style={{ color: 'var(--error)' }}>Failure summary: {row.error_summary}</span>
                    </HistoryMeta>
                  ) : null}
                  {isExpanded ? (
                    <FailureList>
                      {isLoadingExpandedHistoryFailures ? <FailureItem>Loading failures...</FailureItem> : null}
                      {!isLoadingExpandedHistoryFailures && expandedHistoryFailuresError ? (
                        <FailureItem style={{ color: 'var(--error)' }}>Could not load recipient failures.</FailureItem>
                      ) : null}
                      {!isLoadingExpandedHistoryFailures &&
                      !expandedHistoryFailuresError &&
                      expandedHistoryFailures.length === 0 ? (
                        <FailureItem>No failed recipients found for this send.</FailureItem>
                      ) : null}
                      {!isLoadingExpandedHistoryFailures && !expandedHistoryFailuresError
                        ? expandedHistoryFailures.map((failure) => (
                            <FailureItem key={failure.id}>
                              <strong style={{ color: 'var(--onSurface)' }}>{failure.full_name || failure.email}</strong>
                              <div>{failure.email}</div>
                              <div style={{ color: 'var(--error)' }}>
                                {failure.error_message || 'Provider returned an unknown failure.'}
                              </div>
                            </FailureItem>
                          ))
                        : null}
                    </FailureList>
                  ) : null}
                </HistoryRow>
              );
            })
          : null}
        <HistoryChannelHeading>SMS</HistoryChannelHeading>
        {isLoadingSmsHistory ? <HistoryRow>Loading SMS history...</HistoryRow> : null}
        {!isLoadingSmsHistory && smsHistoryError ? (
          <HistoryRow style={{ color: 'var(--error)' }}>Could not load SMS history.</HistoryRow>
        ) : null}
        {!isLoadingSmsHistory && !smsHistoryError && smsHistory.length === 0 ? (
          <HistoryRow>No SMS sends yet for this event.</HistoryRow>
        ) : null}
        {!isLoadingSmsHistory && !smsHistoryError
          ? smsHistory.map((row) => {
              const statusTone = historyStatusTone(row.status);
              const isExpanded = expandedSmsHistorySendId === row.id;
              return (
                <HistoryRow key={`sms-${row.id}`}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.6rem' }}>
                    <strong style={{ color: 'var(--onSurface)', fontSize: '0.92rem' }}>
                      {row.message_body?.trim() || '(No message body)'}
                    </strong>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.45rem',
                        flexWrap: 'wrap',
                        justifyContent: 'flex-end',
                      }}
                    >
                      {row.failed_count > 0 ? (
                        <HistoryToggleButton
                          type="button"
                          onClick={() => setExpandedSmsHistorySendId(isExpanded ? null : row.id)}
                        >
                          {isExpanded ? 'Hide failures' : `Failures (${row.failed_count})`}
                        </HistoryToggleButton>
                      ) : null}
                      <HistoryDangerButton
                        type="button"
                        disabled={deleteSmsHistoryMutation.isPending}
                        onClick={() => {
                          const confirmed = window.confirm('Delete this SMS history entry? This cannot be undone.');
                          if (!confirmed) return;
                          void deleteSmsHistoryMutation.mutateAsync(row.id);
                        }}
                      >
                        {deleteSmsHistoryMutation.isPending ? 'Deleting...' : 'Delete'}
                      </HistoryDangerButton>
                      <StatusPill $tone={statusTone}>{row.status.replace('_', ' ')}</StatusPill>
                    </div>
                  </div>
                  <HistoryMeta>
                    <span>{new Date(row.created_at).toLocaleString()}</span>
                    <SendModeBadge>{row.send_mode === 'bulk' ? 'Bulk send' : 'Single'}</SendModeBadge>
                    <span>Scope: {row.recipient_scope}</span>
                    <span>Sent: {row.sent_count}</span>
                    <span>Failed: {row.failed_count}</span>
                    <span>Skipped: {row.skipped_count}</span>
                  </HistoryMeta>
                  {row.error_summary ? (
                    <HistoryMeta>
                      <span style={{ color: 'var(--error)' }}>Failure summary: {row.error_summary}</span>
                    </HistoryMeta>
                  ) : null}
                  {isExpanded ? (
                    <FailureList>
                      {isLoadingExpandedSmsHistoryFailures ? <FailureItem>Loading failures...</FailureItem> : null}
                      {!isLoadingExpandedSmsHistoryFailures && expandedSmsHistoryFailuresError ? (
                        <FailureItem style={{ color: 'var(--error)' }}>Could not load recipient failures.</FailureItem>
                      ) : null}
                      {!isLoadingExpandedSmsHistoryFailures &&
                      !expandedSmsHistoryFailuresError &&
                      expandedSmsHistoryFailures.length === 0 ? (
                        <FailureItem>No failed recipients found for this send.</FailureItem>
                      ) : null}
                      {!isLoadingExpandedSmsHistoryFailures && !expandedSmsHistoryFailuresError
                        ? expandedSmsHistoryFailures.map((failure) => (
                            <FailureItem key={failure.id}>
                              <strong style={{ color: 'var(--onSurface)' }}>{failure.full_name || failure.phone}</strong>
                              <div>{failure.phone}</div>
                              <div style={{ color: 'var(--error)' }}>
                                {failure.error_message || 'Provider returned an unknown failure.'}
                              </div>
                            </FailureItem>
                          ))
                        : null}
                    </FailureList>
                  ) : null}
                </HistoryRow>
              );
            })
          : null}
        </HistoryList>
      ) : null}
    </HistoryCard>
  );

  const emailComposerElement = (
    <EventRegistrantEmailComposer
      open={emailComposerStandalone || emailComposerOpen}
      variant={emailComposerStandalone ? 'page' : 'modal'}
      embeddedInParentPage={emailComposerStandalone}
      mode={emailComposerMode}
      eventTitle={eventTitle}
      drafts={emailDrafts}
      loadingDrafts={isLoadingEmailDrafts}
      savingDraft={saveDraftMutation.isPending}
      sending={sendEmailMutation.isPending}
      counts={composerCounts}
      initialValues={{
        recipientScope:
          emailComposerMode === 'single' ? 'single' : emailComposerStandalone ? routeRecipientScope : 'filtered',
        headerBlock:
          emailComposerMode === 'single' && currentSingleRecipient
            ? `Hello ${currentSingleRecipient.full_name || '{{full_name}}'},`
            : 'Hello {{full_name}},',
        subject: emailComposerMode === 'single' ? `Update for ${eventTitle}` : `Update for {{event_title}}`,
        footerBlock: 'Thank you,\nNCOF Events Team',
      }}
      onClose={() => {
        if (emailComposerStandalone) {
          navigate(composerReturnPath);
          return;
        }
        setEmailComposerOpen(false);
      }}
      onSaveDraft={async (payload) => {
        await saveDraftMutation.mutateAsync({
          draftId: payload.draftId,
          draftName: payload.draftName,
          subject: payload.subject,
          headerBlock: payload.headerBlock,
          bodyBlock: payload.bodyBlock,
          footerBlock: payload.footerBlock,
          htmlBody: payload.htmlBody,
          textBody: payload.textBody,
        });
      }}
      onSend={async (payload) => {
        const scope = emailComposerMode === 'single' ? 'single' : payload.recipientScope;
        const recipientRegistrationIds = resolveRecipientIds(scope, singleEmailRecipientId);
        if (recipientRegistrationIds.length === 0) {
          setEmailNotice({
            kind: 'error',
            message: 'No recipients available for the chosen scope.',
          });
          return;
        }
        if (emailComposerMode === 'bulk') {
          const confirmed = window.confirm(
            `Send this email to ${recipientRegistrationIds.length} registrant${recipientRegistrationIds.length === 1 ? '' : 's'}?`
          );
          if (!confirmed) return;
        }
        await sendEmailMutation.mutateAsync({
          mode: emailComposerMode,
          recipientScope: scope,
          recipientRegistrationIds,
          draftId: payload.draftId,
          subject: payload.subject,
          headerBlock: payload.headerBlock,
          bodyBlock: payload.bodyBlock,
          footerBlock: payload.footerBlock,
          htmlBody: payload.htmlBody,
          textBody: payload.textBody,
        });
      }}
    />
  );

  const smsComposerElement = (
    <EventRegistrantSmsComposer
      open={smsComposerStandalone || smsComposerOpen}
      variant={smsComposerStandalone ? 'page' : 'modal'}
      pageBackHref={composerReturnPath}
      mode={smsComposerMode}
      eventTitle={eventTitle}
      sending={sendSmsMutation.isPending}
      counts={composerCounts}
      initialValues={{
        recipientScope: smsComposerMode === 'single' ? 'single' : smsComposerStandalone ? routeRecipientScope : 'filtered',
        messageBody:
          smsComposerMode === 'single' && currentSingleSmsRecipient
            ? `Hi ${currentSingleSmsRecipient.full_name || '{{full_name}}'}, reminder for {{event_title}} on {{event_start_date}} at {{event_start_time}}.`
            : 'Hi {{full_name}}, reminder for {{event_title}} on {{event_start_date}} at {{event_start_time}}.',
      }}
      onClose={() => {
        if (smsComposerStandalone) {
          navigate(composerReturnPath);
          return;
        }
        setSmsComposerOpen(false);
      }}
      onSend={async (payload) => {
        const scope = smsComposerMode === 'single' ? 'single' : payload.recipientScope;
        const recipientRegistrationIds = resolveRecipientIds(scope, singleSmsRecipientId);
        if (recipientRegistrationIds.length === 0) {
          setSmsNotice({
            kind: 'error',
            message: 'No recipients available for the chosen scope.',
          });
          return;
        }
        if (smsComposerMode === 'bulk') {
          const confirmed = window.confirm(
            `Send this SMS to ${recipientRegistrationIds.length} registrant${recipientRegistrationIds.length === 1 ? '' : 's'}?`
          );
          if (!confirmed) return;
        }
        await sendSmsMutation.mutateAsync({
          mode: smsComposerMode,
          recipientScope: scope,
          recipientRegistrationIds,
          messageBody: payload.messageBody,
        });
      }}
    />
  );

  if (!embedded && (isLoadingProfile || isLoadingAssignedEvents)) {
    return <Page><HelperText>Loading…</HelperText></Page>;
  }
  if (!embedded && eventId && !canAccessThisEvent) {
    return <Navigate to="/events/public-registrations" replace />;
  }

  if (emailComposerStandalone) {
    const standaloneContent = (
      <SendEmailPage>
        <SendEmailHero>
          <SendEmailHeroTop>
            <SendEmailBackLink to={composerReturnPath}>
              {canManageAllEvents ? 'Back to event setup' : 'Back to registrants'}
            </SendEmailBackLink>
          </SendEmailHeroTop>
          <SendEmailEyebrow>Send email</SendEmailEyebrow>
          <SendEmailTitle>{eventTitle}</SendEmailTitle>
          <SendEmailMeta>
            {eventSchedule ? <SendEmailMetaChip>{eventSchedule}</SendEmailMetaChip> : null}
            {eventLocation ? <SendEmailMetaChip>{eventLocation}</SendEmailMetaChip> : null}
          </SendEmailMeta>
         
        </SendEmailHero>
        {emailNotice ? <SendEmailNotice $kind={emailNotice.kind}>{emailNotice.message}</SendEmailNotice> : null}
        {isLoading ? <HelperText>Loading…</HelperText> : null}
        {error ? (
          <HelperText style={{ color: 'var(--error)' }}>
            Could not load registrations for this event.
          </HelperText>
        ) : null}
        {emailComposerElement}
      </SendEmailPage>
    );
    if (embedded) return standaloneContent;
    return <Page>{standaloneContent}</Page>;
  }

  if (smsComposerStandalone) {
    const standaloneContent = (
      <SendEmailPage>
        <SendEmailHero>
          <SendEmailHeroTop>
            <SendEmailBackLink to={composerReturnPath}>
              {canManageAllEvents ? 'Back to event setup' : 'Back to registrants'}
            </SendEmailBackLink>
          </SendEmailHeroTop>
          <SendEmailEyebrow>Send SMS</SendEmailEyebrow>
          <SendEmailTitle>{eventTitle}</SendEmailTitle>
          <SendEmailMeta>
            {eventSchedule ? <SendEmailMetaChip>{eventSchedule}</SendEmailMetaChip> : null}
            {eventLocation ? <SendEmailMetaChip>{eventLocation}</SendEmailMetaChip> : null}
          </SendEmailMeta>
        </SendEmailHero>
        {smsNotice ? <SendEmailNotice $kind={smsNotice.kind}>{smsNotice.message}</SendEmailNotice> : null}
        {isLoading ? <HelperText>Loading…</HelperText> : null}
        {error ? (
          <HelperText style={{ color: 'var(--error)' }}>
            Could not load registrations for this event.
          </HelperText>
        ) : null}
        {smsComposerElement}
      </SendEmailPage>
    );
    if (embedded) return standaloneContent;
    return <Page>{standaloneContent}</Page>;
  }

  const content = (
    <>
      <HeaderRow>
        <HeaderTitle>{eventTitle}</HeaderTitle>
        {showBackLink ? <BackLink to="/events/public-registrations">Back to Events</BackLink> : null}
      </HeaderRow>
      <HelperText>
        {eventSchedule}
        {eventLocation ? ` • ${eventLocation}` : ''}
      </HelperText>
      {registrationsNotice ? <Banner $kind={registrationsNotice.kind}>{registrationsNotice.message}</Banner> : null}
      {emailNotice ? <Banner $kind={emailNotice.kind}>{emailNotice.message}</Banner> : null}
      {smsNotice ? <Banner $kind={smsNotice.kind}>{smsNotice.message}</Banner> : null}
      {showSectionSwitcher ? (
        <SectionTabs role="tablist" aria-label="Event registrant sections">
          <SectionTab
            type="button"
            role="tab"
            aria-selected={registrantSection === 'registrants'}
            data-active={registrantSection === 'registrants'}
            onClick={() => setRegistrantSection('registrants')}
          >
            Registrants
          </SectionTab>
          <SectionTab
            type="button"
            role="tab"
            aria-selected={registrantSection === 'waivers'}
            data-active={registrantSection === 'waivers'}
            onClick={() => setRegistrantSection('waivers')}
          >
            Waiver List
          </SectionTab>
        </SectionTabs>
      ) : null}
      {showWaiverList ? (
        waiverFormQuery.isLoading ? (
          <HelperText>Loading waiver list…</HelperText>
        ) : (
          <EventWaiverList
            eventId={eventId}
            eventTitle={eventDetails?.title ?? eventFromRows?.title ?? null}
            enabled={waiverFormQuery.data?.waiver_enabled === true}
            fields={waiverFormQuery.data?.fields ?? []}
            canConfigure={canManageAllEvents}
          />
        )
      ) : tshirtsOnly ? (
        <>
          <HelperText>
            Attendee-level T-shirt sizes captured from registration form responses. Check someone off when you hand them a
            shirt.
          </HelperText>
          {isLoading ? <HelperText>Loading…</HelperText> : null}
          {error ? (
            <HelperText style={{ color: 'var(--error)' }}>
              Could not load registrations for this event.
            </HelperText>
          ) : null}
          {!error && eventOrdersForTshirtsError ? (
            <HelperText style={{ color: 'var(--error)' }}>
              Could not load order form responses. Showing registration records only.
            </HelperText>
          ) : null}
          {!isLoading && !error && tshirtAttendeeRows.length === 0 ? (
            <HelperText>No attendee T-shirt sizes have been submitted for this event yet.</HelperText>
          ) : null}
          {!isLoading && !error && tshirtAttendeeRows.length > 0 ? (
            <>
              <ToolbarSurface>
                <ToolbarTop>
                  <div>
                    <ToolbarTitle>T-shirt size directory</ToolbarTitle>
                    <SearchHint style={{ marginTop: '0.35rem', display: 'block', maxWidth: '56ch' }}>
                      Search by name, email, phone, or size. Use the check mark to mark a shirt as given.
                    </SearchHint>
                  </div>
                  <ToolbarMeta>
                    <StatChip>
                      {filteredTshirtRows.length === tshirtAttendeeRows.length
                        ? `${filteredTshirtRows.length} shown`
                        : `${filteredTshirtRows.length} of ${tshirtAttendeeRows.length}`}
                    </StatChip>
                    <StatChip>
                      Given · {tshirtGivenCount} / {tshirtAttendeeRows.length}
                    </StatChip>
                    <ActionButton type="button" onClick={exportTshirtSizeReport} disabled={filteredTshirtRows.length === 0}>
                      Export CSV
                    </ActionButton>
                  </ToolbarMeta>
                </ToolbarTop>
                <ToolbarMeta>
                  {tshirtSizeSummary.map((entry) => (
                    <StatChip key={entry.size}>
                      {entry.size} · {entry.count}
                    </StatChip>
                  ))}
                  <StatChip>Total · {tshirtSizeTotal}</StatChip>
                </ToolbarMeta>
                <SearchRow>
                  <SearchInput
                    type="search"
                    placeholder="Search by name, email, phone, or size…"
                    value={tshirtSearchQuery}
                    onChange={(event) => setTshirtSearchQuery(event.target.value)}
                    aria-label="Search T-shirt attendees"
                  />
                  <FilterSelect
                    value={tshirtSizeFilter}
                    onChange={(event) => setTshirtSizeFilter(event.target.value)}
                    aria-label="Filter by t-shirt size"
                  >
                    <option value="all">All sizes</option>
                    {tshirtSizeOptions.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </FilterSelect>
                  <FilterSelect
                    value={tshirtGivenFilter}
                    onChange={(event) => setTshirtGivenFilter(event.target.value as TshirtGivenFilter)}
                    aria-label="Filter by T-shirt given status"
                  >
                    <option value="all">All shirts</option>
                    <option value="not_given">Not given</option>
                    <option value="given">Given</option>
                  </FilterSelect>
                </SearchRow>
              </ToolbarSurface>
              {filteredTshirtRows.length === 0 ? (
                <HelperText>No attendees match your search or filters.</HelperText>
              ) : (
                <TableCard>
                  <TableWrap>
                    <Table>
                      <thead>
                        <tr>
                          <th>Given</th>
                          <th>Registrant</th>
                          <th>Attendee</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>T-shirt Size</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTshirtRows.map((row) => {
                          const isGiven = givenTshirtKeySet.has(row.key);
                          return (
                            <TshirtGivenRow key={row.key} $given={isGiven}>
                              <td data-label="Given">
                                <TshirtGivenCell>
                                  <TshirtGivenToggle
                                    type="button"
                                    $given={isGiven}
                                    aria-pressed={isGiven}
                                    aria-label={
                                      isGiven
                                        ? `Mark shirt as not given for ${row.attendeeName}`
                                        : `Mark shirt as given for ${row.attendeeName}`
                                    }
                                    disabled={
                                      toggleTshirtGivenMutation.isPending &&
                                      toggleTshirtGivenMutation.variables?.attendeeKey === row.key
                                    }
                                    onClick={() =>
                                      toggleTshirtGivenMutation.mutate({
                                        attendeeKey: row.key,
                                        registrationId: row.registrationId,
                                        given: !isGiven,
                                      })
                                    }
                                  >
                                    <FiCheckIcon aria-hidden size={16} />
                                  </TshirtGivenToggle>
                                  <TshirtGivenLabel>{isGiven ? 'Given' : 'Not yet'}</TshirtGivenLabel>
                                </TshirtGivenCell>
                              </td>
                              <td data-label="Registrant">{row.registrantName}</td>
                              <td data-label="Attendee">{row.attendeeName}</td>
                              <td data-label="Email">{row.email || '—'}</td>
                              <td data-label="Phone">{row.phone || '—'}</td>
                              <td data-label="T-shirt Size">{row.size}</td>
                            </TshirtGivenRow>
                          );
                        })}
                      </tbody>
                    </Table>
                  </TableWrap>
                </TableCard>
              )}
            </>
          ) : null}
        </>
      ) : communicationHistoryOnly ? (
        <>
          <HelperText>Review recent email and SMS activity for this event.</HelperText>
          {isLoading ? <HelperText>Loading…</HelperText> : null}
          {error ? (
            <HelperText style={{ color: 'var(--error)' }}>
              Could not load registrations for this event.
            </HelperText>
          ) : null}
          {emailHistorySection}
        </>
      ) : (
        <>
          <HelperText>Click a row to view full registration details and group member names.</HelperText>
          <ScannerSection>
            <ScannerHead>
              <div>
                <ScannerTitle>QR check-in</ScannerTitle>
                <ScannerHint>
                  Validate tickets at the door by pasting a scanned code or using the camera scanner.
                </ScannerHint>
              </div>
            </ScannerHead>
            <ScannerInput
              type="text"
              value={scanValue}
              onChange={(e) => setScanValue(e.target.value)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter') return;
                event.preventDefault();
                void submitCheckInFromInput(scanValue);
              }}
              onPaste={(event) => {
                const pasted = event.clipboardData.getData('text');
                if (!pasted.trim()) return;
                event.preventDefault();
                setScanValue(pasted);
                void submitCheckInFromInput(pasted);
              }}
              placeholder="Paste scanned QR payload or ticket code"
              aria-label="QR code input"
            />
            <ScannerControls>
              <ScannerButton type="button" onClick={() => submitCheckIn(scanValue)} disabled={checkingIn || !scanValue.trim()}>
                {checkingIn ? 'Checking In...' : 'Validate & Check In'}
              </ScannerButton>
              {!scannerOpen ? (
                <ScannerButton type="button" onClick={startScanner}>
                  Open Camera Scanner
                </ScannerButton>
              ) : (
                <ScannerButton type="button" onClick={stopScanner}>
                  Stop Camera
                </ScannerButton>
              )}
            </ScannerControls>
            {scannerOpen ? <ScannerVideo ref={videoRef} autoPlay muted playsInline /> : null}
            {scannerStatus ? <HelperText>{scannerStatus}</HelperText> : null}
          </ScannerSection>

          {isLoading ? <HelperText>Loading…</HelperText> : null}
          {error ? (
            <HelperText style={{ color: 'var(--error)' }}>
              Could not load registrations for this event.
            </HelperText>
          ) : null}

          {!isLoading && !error && (registrations?.rows?.length ?? 0) === 0 ? (
            <HelperText>No registrations yet for this event.</HelperText>
          ) : null}

          {!isLoading && !error && (registrations?.rows?.length ?? 0) > 0 ? (
            <>
          <MetricsGrid>
            <MetricCard>
              <MetricLabel>Total registrations</MetricLabel>
              <MetricValue>{attendanceMetrics.totalRegistrations}</MetricValue>
            </MetricCard>
            <MetricCard>
              <MetricLabel>Total tickets</MetricLabel>
              <MetricValue>{attendanceMetrics.totalTickets}</MetricValue>
            </MetricCard>
            <MetricCard>
              <MetricLabel>Checked in</MetricLabel>
              <MetricValue>{attendanceMetrics.checkedInRegistrations}</MetricValue>
            </MetricCard>
            <MetricCard>
              <MetricLabel>Show-up rate</MetricLabel>
              <MetricValue>{attendanceMetrics.showUpRate.toFixed(1)}%</MetricValue>
            </MetricCard>
            <MetricCard>
              <MetricLabel>No-show count</MetricLabel>
              <MetricValue>{attendanceMetrics.noShowRows.length}</MetricValue>
            </MetricCard>
          </MetricsGrid>
          <ToolbarSurface>
            <ToolbarTop>
              <div>
                <ToolbarTitle>Registrant directory</ToolbarTitle>
                <SearchHint style={{ marginTop: '0.35rem', display: 'block', maxWidth: '56ch' }}>
                  Additional attendees appear as their own rows. Search a guest name to check that person in from the
                  table, then filter, select rows for targeted email/SMS, or message everyone who matches.
                </SearchHint>
              </div>
              <ToolbarMeta>
                <StatChip>
                  {filteredDirectoryRows.length === directoryRows.length
                    ? `${filteredDirectoryRows.length} shown`
                    : `${filteredDirectoryRows.length} of ${directoryRows.length}`}
                </StatChip>
                {(registrations?.totalCount ?? 0) > (registrations?.rows?.length ?? 0) ? (
                  <StatChip>{registrations?.totalCount ?? 0} total</StatChip>
                ) : null}
                {!showPendingPayments && pendingPaymentCount > 0 ? (
                  <StatChip>{pendingPaymentCount} pending hidden</StatChip>
                ) : null}
                {selectedRecipientIds.length > 0 ? (
                  <StatChip style={{ background: 'color-mix(in srgb, var(--primary) 22%, var(--surface))' }}>
                    {selectedRecipientIds.length} selected
                  </StatChip>
                ) : null}
              </ToolbarMeta>
            </ToolbarTop>
            <SearchRow>
              <SearchInput
                type="search"
                placeholder="Search by name, email, phone, additional attendees, notes, ticket, appointment, or status…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search registrations"
              />
              <FilterSelect
                value={ticketTypeFilter}
                onChange={(event) => setTicketTypeFilter(event.target.value)}
                aria-label="Filter by ticket type"
              >
                <option value="all">All ticket types</option>
                {ticketTypeOptions.map((ticketType) => (
                  <option key={ticketType} value={ticketType}>
                    {ticketType}
                  </option>
                ))}
              </FilterSelect>
              <FilterSelect
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                aria-label="Filter by status"
              >
                <option value="all">All statuses</option>
                {statusOptions.map((status) => (
                  <option key={status} value={status}>
                  {status}
                </option>
                ))}
              </FilterSelect>
              <FilterSelect
                value={appointmentSlotFilter}
                onChange={(event) => setAppointmentSlotFilter(event.target.value)}
                aria-label="Filter by appointment slot"
              >
                <option value="all">All appointment slots</option>
                {appointmentFilterOptions.map((slot) => (
                  <option key={slot.id} value={slot.id}>
                    {formatAppointmentWindow(slot.starts_at, slot.ends_at)
                      ? `${slot.label} • ${formatAppointmentWindow(slot.starts_at, slot.ends_at)}`
                      : slot.label}
                  </option>
                ))}
              </FilterSelect>
              <ToggleFilterLabel>
                <input
                  type="checkbox"
                  checked={showPendingPayments}
                  onChange={(event) => setShowPendingPayments(event.target.checked)}
                  aria-label="Show pending payments"
                />
                Show pending payments
              </ToggleFilterLabel>
            </SearchRow>
            <ToolbarActionRow>
              <ActionButton type="button" onClick={selectAllFiltered} disabled={filteredRegistrations.length === 0}>
                Select filtered
              </ActionButton>
              <ActionButton type="button" onClick={clearSelected} disabled={selectedRecipientIds.length === 0}>
                Clear selected
              </ActionButton>
              <ActionButton type="button" onClick={exportFilteredRegistrants} disabled={filteredDirectoryRows.length === 0}>
                Export CSV
              </ActionButton>
              <ActionButton
                type="button"
                onClick={() => backfillMobileOrdersMutation.mutate()}
                disabled={!eventId || backfillMobileOrdersMutation.isPending}
              >
                {backfillMobileOrdersMutation.isPending ? 'Backfilling...' : 'Backfill mobile orders'}
              </ActionButton>
              <ActionButton
                type="button"
                onClick={() => manualLinkPreviousOrdersMutation.mutate()}
                disabled={(registrations?.rows?.length ?? 0) === 0 || manualLinkPreviousOrdersMutation.isPending}
              >
                {manualLinkPreviousOrdersMutation.isPending ? 'Linking...' : 'Link previous orders'}
              </ActionButton>
              <PrimaryActionButton type="button" onClick={() => openTicketChangeModal()} disabled={selectedRecipientIds.length === 0}>
                Change ticket type
              </PrimaryActionButton>
              <PrimaryActionButton
                type="button"
                onClick={openBulkComposer}
                disabled={(registrations?.rows?.length ?? 0) === 0}
              >
                Send bulk email
              </PrimaryActionButton>
              <PrimaryActionButton
                type="button"
                onClick={openBulkSmsComposer}
                disabled={(registrations?.rows?.length ?? 0) === 0}
              >
                Send bulk SMS
              </PrimaryActionButton>
              <DangerActionButton
                type="button"
                onClick={() => requestDeleteRegistrants(selectedRecipientIds)}
                disabled={selectedRecipientIds.length === 0 || deleteRegistrationsMutation.isPending}
              >
                {deleteRegistrationsMutation.isPending ? 'Deleting...' : `Delete selected (${selectedRecipientIds.length})`}
              </DangerActionButton>
            </ToolbarActionRow>
          </ToolbarSurface>
          <TableCard>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <th>
                      <SelectionCheckbox
                        type="checkbox"
                        aria-label="Select all filtered registrants"
                        checked={
                          filteredRegistrations.length > 0 &&
                          filteredRegistrations.every((row) => recipientSelectionSet.has(row.id))
                        }
                        onChange={(event) => {
                          if (event.target.checked) {
                            selectAllFiltered();
                          } else {
                            clearSelected();
                          }
                        }}
                      />
                    </th>
                    <th>Registrant</th>
                    <th>Contact</th>
                    <th>Qty</th>
                    <th>Ticket</th>
                    <th>Appointments</th>
                    <th>Cost</th>
                    <th>Status</th>
                    <th>Checked In</th>
                    <th>Submitted</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
              {pagedFilteredDirectoryRows.map((row) => {
                const registration = row.registration;
                return (
                <ClickableRow
                  key={row.key}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedRegistration(registration)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setSelectedRegistration(registration);
                    }
                  }}
                >
                  <td data-label="Select">
                    <RegistrantTdValue>
                      <SelectionCheckbox
                        type="checkbox"
                        checked={recipientSelectionSet.has(registration.id)}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event) => toggleRecipientSelection(registration.id, event.target.checked)}
                        aria-label={`Select ${row.fullName || registration.full_name}`}
                      />
                    </RegistrantTdValue>
                  </td>
                  <td data-label="Registrant">
                    <RegistrantTdValue>
                      <RegistrantName>{row.fullName || '—'}</RegistrantName>
                      {!row.isPrimary ? (
                        <AdditionalAttendees>In {registration.full_name}'s group</AdditionalAttendees>
                      ) : null}
                    </RegistrantTdValue>
                  </td>
                  <td data-label="Contact">
                    <RegistrantTdValue>
                      <ContactBlock>
                        <span>{row.email || '—'}</span>
                        <ContactMuted>{row.phone || '—'}</ContactMuted>
                      </ContactBlock>
                    </RegistrantTdValue>
                  </td>
                  <td data-label="Qty">
                    <RegistrantTdValue>{row.isPrimary ? registration.quantity ?? 1 : 1}</RegistrantTdValue>
                  </td>
                  <td data-label="Ticket">
                    <RegistrantTdValue>{registration.ticket_type_name ?? 'General'}</RegistrantTdValue>
                  </td>
                  <td data-label="Appointments">
                    <RegistrantTdValue>{registration.appointments.length > 0 ? formatAppointmentSummary(registration.appointments) : '—'}</RegistrantTdValue>
                  </td>
                  <td data-label="Cost">
                    <RegistrantTdValue>
                      {row.isPrimary
                        ? `${formatUsdCents(registrantCostById.get(registration.id))}${
                            registrantBalanceById.get(registration.id)
                              ? ` • Due ${formatUsdCents(registrantBalanceById.get(registration.id))}`
                              : ''
                          }`
                        : '—'}
                    </RegistrantTdValue>
                  </td>
                  <td data-label="Status">
                    <RegistrantTdValue>
                      <TableStatusBadge $status={registration.status}>{registration.status}</TableStatusBadge>
                    </RegistrantTdValue>
                  </td>
                  <td data-label="Checked In">
                    <RegistrantTdValue>
                      {renderDirectoryCheckInCell(row)}
                    </RegistrantTdValue>
                  </td>
                  <td data-label="Submitted">
                    <RegistrantTdValue>
                      {registration.created_at ? new Date(registration.created_at).toLocaleString() : '—'}
                    </RegistrantTdValue>
                  </td>
                  <NotesCell data-label="Notes">
                    <RegistrantTdValue>{row.isPrimary ? registration.notes ?? '—' : '—'}</RegistrantTdValue>
                  </NotesCell>
                </ClickableRow>
                );
              })}
            </tbody>
              </Table>
            </TableWrap>
          </TableCard>
          {filteredDirectoryRows.length > 0 ? (
            <PaginationRow>
              <PaginationSummary>
                Showing{' '}
                {((registrantListPage - 1) * REGISTRANT_LIST_PAGE_SIZE + 1).toLocaleString()}-
                {Math.min(registrantListPage * REGISTRANT_LIST_PAGE_SIZE, filteredDirectoryRows.length).toLocaleString()}{' '}
                of {filteredDirectoryRows.length.toLocaleString()} attendees
              </PaginationSummary>
              <PaginationActions>
                <PaginationButton
                  type="button"
                  onClick={() => setRegistrantListPage((current) => Math.max(1, current - 1))}
                  disabled={registrantListPage <= 1}
                >
                  Previous
                </PaginationButton>
                <PaginationButton
                  type="button"
                  onClick={() => setRegistrantListPage((current) => Math.min(registrantPageCount, current + 1))}
                  disabled={registrantListPage >= registrantPageCount}
                >
                  Next
                </PaginationButton>
              </PaginationActions>
            </PaginationRow>
          ) : null}
          {filteredDirectoryRows.length === 0 ? (
            <HelperText>
              No attendees match the current filters
              {searchQuery.trim() ? ` and search "${searchQuery.trim()}"` : ''}.
            </HelperText>
          ) : null}
          {(registrations?.totalCount ?? 0) > (registrations?.rows?.length ?? 0) ? (
            <ToolbarActionRow>
              <ActionButton
                type="button"
                onClick={() => setRegistrationsLimit((prev) => prev + REGISTRATIONS_PAGE_SIZE)}
                disabled={isLoadingRegistrations}
              >
                {isLoadingRegistrations
                  ? 'Loading...'
                  : `Load more (${(registrations?.rows?.length ?? 0).toLocaleString()} of ${(registrations?.totalCount ?? 0).toLocaleString()})`}
              </ActionButton>
            </ToolbarActionRow>
          ) : null}
          {attendanceMetrics.noShowRows.length > 0 ? (
            <>
              <TableCard style={{ marginTop: '1rem' }}>
                <TableWrap>
                  <Table>
                    <thead>
                      <tr>
                        <th>No-show attendee</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Quantity</th>
                        <th>Check in</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedNoShowRows.map((row) => (
                        <tr key={`noshow-${row.key}`}>
                          <td data-label="Registrant">
                            <RegistrantTdValue>
                              {row.fullName}
                              {!row.isPrimary ? (
                                <AdditionalAttendees>In {row.registration.full_name}'s group</AdditionalAttendees>
                              ) : null}
                            </RegistrantTdValue>
                          </td>
                          <td data-label="Email">
                            <RegistrantTdValue>{row.email || '—'}</RegistrantTdValue>
                          </td>
                          <td data-label="Phone">
                            <RegistrantTdValue>{row.phone || '—'}</RegistrantTdValue>
                          </td>
                          <td data-label="Quantity">
                            <RegistrantTdValue>{row.isPrimary ? row.registration.quantity || 1 : 1}</RegistrantTdValue>
                          </td>
                          <td data-label="Check in">
                            <RegistrantTdValue>
                              {isRegistrationCancelled(row.registration) ? '—' : renderDirectoryCheckInCell(row)}
                            </RegistrantTdValue>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrap>
              </TableCard>
              <PaginationRow>
                <PaginationSummary>
                  Showing {((noShowListPage - 1) * NO_SHOW_LIST_PAGE_SIZE + 1).toLocaleString()}-
                  {Math.min(noShowListPage * NO_SHOW_LIST_PAGE_SIZE, attendanceMetrics.noShowRows.length).toLocaleString()}{' '}
                  of {attendanceMetrics.noShowRows.length.toLocaleString()} no-show attendees
                </PaginationSummary>
                <PaginationActions>
                  <PaginationButton
                    type="button"
                    onClick={() => setNoShowListPage((current) => Math.max(1, current - 1))}
                    disabled={noShowListPage <= 1}
                  >
                    Previous
                  </PaginationButton>
                  <PaginationButton
                    type="button"
                    onClick={() => setNoShowListPage((current) => Math.min(noShowPageCount, current + 1))}
                    disabled={noShowListPage >= noShowPageCount}
                  >
                    Next
                  </PaginationButton>
                </PaginationActions>
              </PaginationRow>
            </>
          ) : null}
          <TableCard style={{ marginTop: '1rem' }}>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <th colSpan={7}>Recent Ticket Change Requests</th>
                  </tr>
                  <tr>
                    <th>Requested</th>
                    <th>Registration</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Difference</th>
                    <th>Status</th>
                    <th>Payment</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoadingTicketChangeHistory ? (
                    <tr>
                      <td colSpan={7}>
                        <RegistrantTdValue>Loading ticket change requests...</RegistrantTdValue>
                      </td>
                    </tr>
                  ) : null}
                  {!isLoadingTicketChangeHistory && ticketChangeHistoryError ? (
                    <tr>
                      <td colSpan={7}>
                        <RegistrantTdValue style={{ color: 'var(--error)' }}>
                          Could not load ticket change history. If this is a new rollout, apply the
                          `20260411000001_add_public_event_ticket_change_requests.sql` migration first.
                        </RegistrantTdValue>
                      </td>
                    </tr>
                  ) : null}
                  {!isLoadingTicketChangeHistory && ticketChangeHistory.length === 0 ? (
                    <tr>
                      <td colSpan={7}>
                        <RegistrantTdValue>No ticket change requests yet.</RegistrantTdValue>
                      </td>
                    </tr>
                  ) : null}
                  {!isLoadingTicketChangeHistory
                    ? ticketChangeHistory.map((row) => (
                        <tr key={`ticket-change-${row.id}`}>
                          <td>
                            <RegistrantTdValue>{new Date(row.created_at).toLocaleString()}</RegistrantTdValue>
                          </td>
                          <td>
                            <RegistrantTdValue>#{row.registration_id.slice(0, 8).toUpperCase()}</RegistrantTdValue>
                          </td>
                          <td>
                            <RegistrantTdValue>{row.old_ticket_type_name || 'General'}</RegistrantTdValue>
                          </td>
                          <td>
                            <RegistrantTdValue>{row.new_ticket_type_name || 'General'}</RegistrantTdValue>
                          </td>
                          <td>
                            <RegistrantTdValue>${(Number(row.difference_cents || 0) / 100).toFixed(2)}</RegistrantTdValue>
                          </td>
                          <td>
                            <RegistrantTdValue>
                              <TableStatusBadge $status={row.status}>{row.status}</TableStatusBadge>
                            </RegistrantTdValue>
                          </td>
                          <td>
                            <RegistrantTdValue>{row.payment_status || 'not_required'}</RegistrantTdValue>
                          </td>
                        </tr>
                      ))
                    : null}
                </tbody>
              </Table>
            </TableWrap>
          </TableCard>
            </>
          ) : null}
        </>
      )}
      {emailComposerElement}
      {smsComposerElement}
      {ticketChangeModalOpen ? (
        <Overlay onClick={() => setTicketChangeModalOpen(false)}>
          <Modal onClick={(event) => event.stopPropagation()}>
            <ModalAccent />
            <ModalHeader>
              <ModalTitle>Change Ticket Type</ModalTitle>
              <ModalHeaderActions>
                <CloseButton type="button" onClick={() => setTicketChangeModalOpen(false)}>
                  Close
                </CloseButton>
              </ModalHeaderActions>
            </ModalHeader>
            <ModalScrollBody>
              {ticketChangeNotice ? <Banner $kind={ticketChangeNotice.kind}>{ticketChangeNotice.message}</Banner> : null}
              <DetailText>
                Updating <strong>{ticketChangeRows.length}</strong> registrant{ticketChangeRows.length === 1 ? '' : 's'}.
              </DetailText>
              <ModalFieldBlock>
                <SectionTitle>Target Ticket Type</SectionTitle>
                <FilterSelect
                  value={ticketChangeTargetTicketId}
                  onChange={(event) => setTicketChangeTargetTicketId(event.target.value)}
                  aria-label="Select target ticket type"
                  style={{ width: '100%' }}
                >
                  <option value="">Select ticket type</option>
                  {eventTicketTypes.map((ticket) => (
                    <option key={ticket.id} value={ticket.id}>
                      {ticket.name} (${(Number(ticket.price_cents || 0) / 100).toFixed(2)})
                    </option>
                  ))}
                </FilterSelect>
              </ModalFieldBlock>
              <ModalFieldBlock>
                <SectionTitle>Manual Refund / Internal Note</SectionTitle>
                <ModalTextarea
                  value={ticketChangeNote}
                  onChange={(event) => setTicketChangeNote(event.target.value)}
                  rows={3}
                  placeholder="Optional note (required to document manual refunds)."
                  aria-label="Refund or internal note"
                />
              </ModalFieldBlock>
              <ModalFieldBlock>
                <SectionTitle>Checkout Provider</SectionTitle>
                <FilterSelect
                  value={ticketChangePaymentProvider}
                  onChange={(event) =>
                    setTicketChangePaymentProvider(event.target.value === 'paypal' ? 'paypal' : 'square')
                  }
                  aria-label="Select payment provider for ticket change links"
                  style={{ width: '100%' }}
                >
                  <option value="square">Square</option>
                  <option value="paypal">PayPal</option>
                </FilterSelect>
              </ModalFieldBlock>
              <ToolbarActionRow>
                <PrimaryActionButton
                  type="button"
                  disabled={!ticketChangeTargetTicketId || ticketChangeRows.length === 0 || ticketChangeMutation.isPending}
                  onClick={() => {
                    if (!ticketChangeTargetTicketId || ticketChangeRows.length === 0) return;
                    void ticketChangeMutation.mutateAsync({
                      registrationIds: ticketChangeRows.map((row) => row.id),
                      targetTicketTypeId: ticketChangeTargetTicketId,
                      note: ticketChangeNote,
                      paymentProvider: ticketChangePaymentProvider,
                    });
                  }}
                >
                  {ticketChangeMutation.isPending ? 'Processing...' : 'Apply change'}
                </PrimaryActionButton>
                <ActionButton
                  type="button"
                  disabled={ticketChangeManualRequestIds.length === 0 || completeManualDowngradeMutation.isPending}
                  onClick={() => {
                    if (ticketChangeManualRequestIds.length === 0) return;
                    if (!ticketChangeNote.trim()) {
                      setTicketChangeNotice({ kind: 'error', message: 'Add a refund note before completing manual downgrades.' });
                      return;
                    }
                    void completeManualDowngradeMutation.mutateAsync({
                      changeRequestIds: ticketChangeManualRequestIds,
                      note: ticketChangeNote,
                    });
                  }}
                >
                  {completeManualDowngradeMutation.isPending
                    ? 'Completing...'
                    : `Mark manual refund complete (${ticketChangeManualRequestIds.length})`}
                </ActionButton>
              </ToolbarActionRow>
            {ticketChangeQuotes.length > 0 ? (
              <>
                <SectionTitle>Quote Preview</SectionTitle>
                <TableCard>
                  <TableWrap>
                    <Table>
                      <thead>
                        <tr>
                          <th>Registration</th>
                          <th>From</th>
                          <th>To</th>
                          <th>Difference</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ticketChangeQuotes.map((quote) => (
                          <tr key={quote.registrationId}>
                            <td>{quote.registrationId.slice(0, 8)}</td>
                            <td>{quote.oldTicketTypeName}</td>
                            <td>{quote.newTicketTypeName}</td>
                            <td>{(Number(quote.differenceCents || 0) / 100).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </TableWrap>
                </TableCard>
              </>
            ) : null}
            {ticketChangeApprovalLinks.length > 0 ? (
              <>
                <SectionTitle>Pay Difference Links</SectionTitle>
                <NamesList>
                  {ticketChangeApprovalLinks.map((entry) => (
                    <li key={`${entry.registrationId}-${entry.approvalUrl}`}>
                      <a href={entry.approvalUrl} target="_blank" rel="noreferrer">
                        Open {entry.provider === 'square' ? 'Square' : 'PayPal'} checkout for {entry.registrationId.slice(0, 8)}
                      </a>
                    </li>
                  ))}
                </NamesList>
              </>
            ) : null}
            </ModalScrollBody>
          </Modal>
        </Overlay>
      ) : null}
      {selectedRegistration ? (
        <Overlay onClick={() => setSelectedRegistration(null)}>
          <Modal onClick={(event) => event.stopPropagation()}>
            <ModalAccent />
            <ModalHeader>
              <ModalTitle>Registration Details</ModalTitle>
              <ModalHeaderActions>
                <ModalHeaderToolbar>
                  <ModalMenuWrap ref={registrationDetailsMenuRef}>
                    <ModalEllipsisButton
                      type="button"
                      aria-expanded={registrationDetailsMenuOpen}
                      aria-haspopup="menu"
                      aria-label="Registration actions"
                      onClick={() => setRegistrationDetailsMenuOpen((open) => !open)}
                    >
                      <FiMoreVerticalIcon aria-hidden />
                    </ModalEllipsisButton>
                    {registrationDetailsMenuOpen ? (
                      <ModalMenuList role="menu" aria-label="Registration actions">
                        <ModalMenuItem
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setRegistrationDetailsMenuOpen(false);
                            openEditRegistrantModal(selectedRegistration);
                          }}
                        >
                          Edit registrant
                        </ModalMenuItem>
                        <ModalMenuItem
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setRegistrationDetailsMenuOpen(false);
                            openTicketChangeModal(selectedRegistration.id);
                          }}
                        >
                          Change ticket
                        </ModalMenuItem>
                        <ModalMenuItem
                          type="button"
                          role="menuitem"
                          disabled={
                            manualCheckInMutation.isPending ||
                            Boolean(selectedRegistrationCheckin?.primaryCheckedIn) ||
                            isRegistrationCancelled(selectedRegistration)
                          }
                          onClick={() => {
                            setRegistrationDetailsMenuOpen(false);
                            requestManualCheckIn(selectedRegistration, {
                              ticketId: selectedRegistrationCheckin?.primaryTicket?.id || null,
                              displayName:
                                selectedRegistrationCheckin?.primaryTicket?.attendee_name ||
                                selectedRegistration.full_name,
                            });
                          }}
                        >
                          {manualCheckInMutation.isPending &&
                          !manualCheckInMutation.variables?.checkInAllTickets &&
                          manualCheckInMutation.variables?.registration.id === selectedRegistration.id
                            ? 'Checking in…'
                            : selectedRegistrationCheckin?.primaryCheckedIn
                              ? 'Already checked in'
                              : 'Check in registrant'}
                        </ModalMenuItem>
                        {(selectedRegistrationCheckin?.remainingTickets || 0) > 1 ? (
                          <ModalMenuItem
                            type="button"
                            role="menuitem"
                            disabled={manualCheckInMutation.isPending || isRegistrationCancelled(selectedRegistration)}
                            onClick={() => {
                              setRegistrationDetailsMenuOpen(false);
                              const remaining = selectedRegistrationCheckin?.remainingTickets || 0;
                              const confirmed = window.confirm(
                                `Check in all remaining attendees for this registration (${remaining} ticket${remaining === 1 ? '' : 's'})?`,
                              );
                              if (!confirmed) return;
                              requestManualCheckIn(selectedRegistration, {
                                checkInAllTickets: true,
                                displayName: selectedRegistration.full_name,
                              });
                            }}
                          >
                            {manualCheckInMutation.isPending &&
                            manualCheckInMutation.variables?.checkInAllTickets &&
                            manualCheckInMutation.variables?.registration.id === selectedRegistration.id
                              ? 'Checking in…'
                              : 'Check in entire group'}
                          </ModalMenuItem>
                        ) : null}
                        <ModalMenuItem
                          type="button"
                          role="menuitem"
                          disabled={resendConfirmationEmailMutation.isPending}
                          onClick={async () => {
                            setRegistrationDetailsMenuOpen(false);
                            const confirmed = window.confirm(
                              'Resend the original confirmation email for this registrant?'
                            );
                            if (!confirmed) return;
                            await resendConfirmationEmailMutation.mutateAsync(selectedRegistration.id);
                          }}
                        >
                          {resendConfirmationEmailMutation.isPending ? 'Resending confirmation…' : 'Resend confirmation email'}
                        </ModalMenuItem>
                        <ModalMenuItem
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setRegistrationDetailsMenuOpen(false);
                            openSingleComposer(selectedRegistration.id);
                          }}
                        >
                          Email registrant
                        </ModalMenuItem>
                        <ModalMenuItem
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setRegistrationDetailsMenuOpen(false);
                            openSingleSmsComposer(selectedRegistration.id);
                          }}
                        >
                          SMS registrant
                        </ModalMenuItem>
                        <ModalMenuDivider aria-hidden />
                        <ModalMenuItem
                          type="button"
                          role="menuitem"
                          data-variant="danger"
                          disabled={deleteRegistrationsMutation.isPending}
                          onClick={() => {
                            setRegistrationDetailsMenuOpen(false);
                            requestDeleteRegistrants([selectedRegistration.id]);
                          }}
                        >
                          {deleteRegistrationsMutation.isPending ? 'Deleting…' : 'Delete registrant'}
                        </ModalMenuItem>
                      </ModalMenuList>
                    ) : null}
                  </ModalMenuWrap>
                  <CloseButton
                    type="button"
                    onClick={() => {
                      setRegistrationDetailsMenuOpen(false);
                      setSelectedRegistration(null);
                    }}
                  >
                    Close
                  </CloseButton>
                </ModalHeaderToolbar>
              </ModalHeaderActions>
            </ModalHeader>

            <ModalScrollBody>
              <ModalFieldBlock>
                <SectionTitle>Primary Registrant</SectionTitle>
                <ModalInfoCard>
                  <ModalLead>{selectedRegistration.full_name}</ModalLead>
                  <DetailText>{selectedRegistration.email}</DetailText>
                  <DetailText>{selectedRegistration.phone || 'No phone provided'}</DetailText>
                </ModalInfoCard>
              </ModalFieldBlock>

              <ModalFieldBlock>
                <SectionTitle>Registration Info</SectionTitle>
                <ModalInfoCard>
                  <DetailText>Quantity: {selectedRegistration.quantity || 1}</DetailText>
                  <DetailText>Ticket: {selectedRegistration.ticket_type_name || 'General'}</DetailText>
                  <DetailText>Status: {selectedRegistration.status}</DetailText>
                  <DetailText>
                    Checked In:{' '}
                    {selectedRegistrationCheckin?.checkedIn
                      ? selectedRegistrationCheckin.lastCheckedInAt
                        ? new Date(String(selectedRegistrationCheckin.lastCheckedInAt)).toLocaleString()
                        : 'Yes'
                      : selectedRegistrationCheckin && selectedRegistrationCheckin.totalTickets > 1
                        ? formatCheckInCount(selectedRegistrationCheckin)
                        : 'No'}
                  </DetailText>
                  <DetailText>
                    Submitted:{' '}
                    {selectedRegistration.created_at
                      ? new Date(selectedRegistration.created_at).toLocaleString()
                      : '—'}
                  </DetailText>
                  <DetailText>Notes: {selectedRegistration.notes || '—'}</DetailText>
                  {selectedGroupPasscode ? (
                    <DetailText>
                      Group Passcode: <strong style={{ color: 'var(--onSurface)' }}>{selectedGroupPasscode}</strong>{' '}
                      <ActionButton type="button" onClick={() => void copyToClipboard(selectedGroupPasscode)}>
                        Copy
                      </ActionButton>
                    </DetailText>
                  ) : null}
                </ModalInfoCard>
              </ModalFieldBlock>

              <ModalFieldBlock>
                <SectionTitle>Appointments</SectionTitle>
                {selectedRegistration.appointments.length > 0 ? (
                  <ModalInfoCard>
                    {selectedRegistration.appointments.map((appointment) => (
                      <DetailText key={`${selectedRegistration.id}-${appointment.slot_id}`}>
                        <strong style={{ color: 'var(--onSurface)' }}>{appointment.label}</strong>
                        {formatAppointmentWindow(appointment.starts_at, appointment.ends_at)
                          ? ` — ${formatAppointmentWindow(appointment.starts_at, appointment.ends_at)}`
                          : ''}
                      </DetailText>
                    ))}
                  </ModalInfoCard>
                ) : (
                  <DetailText>No appointments selected.</DetailText>
                )}
              </ModalFieldBlock>

              <ModalFieldBlock>
                <SectionTitle>Custom Responses</SectionTitle>
                {selectedCustomResponses.length > 0 ? (
                  <ModalInfoCard>
                    {selectedCustomResponses.map((response) => (
                      <DetailText key={response.key}>
                        <strong style={{ color: 'var(--onSurface)' }}>{response.label}:</strong> {response.value}
                      </DetailText>
                    ))}
                  </ModalInfoCard>
                ) : (
                  <DetailText>No custom responses submitted.</DetailText>
                )}
              </ModalFieldBlock>

              <ModalFieldBlock>
                <SectionTitle>Attendees</SectionTitle>
                {selectedAttendees.length > 0 ? (
                  <NamesList>
                    {selectedAttendees.map((attendee) => {
                      const ticket = attendee.ticket;
                      const ticketCheckedIn = isTicketCheckedIn(ticket);
                      const ticketCancelled = isTicketCancelled(ticket);
                      const isPending =
                        manualCheckInMutation.isPending &&
                        Boolean(ticket?.id) &&
                        manualCheckInMutation.variables?.ticketId === ticket?.id &&
                        !manualCheckInMutation.variables?.checkInAllTickets;
                      return (
                        <li key={attendee.key}>
                          <AttendeeCheckInRow>
                            <AttendeeCheckInMeta>
                              <strong style={{ color: 'var(--onSurface)' }}>{attendee.fullName}</strong>
                              <div>
                                {[attendee.email || 'No email provided', attendee.phone || 'No phone provided'].join(' • ')}
                              </div>
                              {attendee.responses ? <div>Responses: {attendee.responses}</div> : null}
                              <div>
                                {ticketCheckedIn
                                  ? ticket?.checked_in_at
                                    ? `Checked in ${new Date(String(ticket.checked_in_at)).toLocaleString()}`
                                    : 'Checked in'
                                  : ticketCancelled
                                    ? 'Ticket cancelled'
                                    : 'Not checked in'}
                              </div>
                            </AttendeeCheckInMeta>
                            {ticket && !ticketCheckedIn && !ticketCancelled && !isRegistrationCancelled(selectedRegistration) ? (
                              <ListCheckInButton
                                type="button"
                                aria-label={`Check in ${attendee.fullName || 'attendee'}`}
                                disabled={manualCheckInMutation.isPending}
                                onClick={() =>
                                  requestManualCheckIn(selectedRegistration, {
                                    ticketId: ticket.id,
                                    displayName: ticket.attendee_name || attendee.fullName,
                                  })
                                }
                              >
                                {isPending ? 'Checking in…' : 'Check in'}
                              </ListCheckInButton>
                            ) : null}
                          </AttendeeCheckInRow>
                        </li>
                      );
                    })}
                  </NamesList>
                ) : (
                  <DetailText>No attendee details were submitted.</DetailText>
                )}
              </ModalFieldBlock>
            </ModalScrollBody>
          </Modal>
        </Overlay>
      ) : null}
      {editRegistrantModalOpen && editingRegistrant ? (
        <EventRegistrantEditModal
          registration={editingRegistrant}
          formFields={eventOrderFormFields}
          registrationTickets={registrationTickets}
          isSaving={editRegistrantMutation.isPending}
          onClose={() => {
            if (editRegistrantMutation.isPending) return;
            setEditRegistrantModalOpen(false);
            setEditingRegistrant(null);
          }}
          onSave={async (payload) => {
            await editRegistrantMutation.mutateAsync(payload);
          }}
        />
      ) : null}
    </>
  );

  if (embedded) return content;

  return <Page>{content}</Page>;
};

export const PublicEventRegistrationsEmailPage: React.FC = () => {
  return <PublicEventRegistrationsEventPage showBackLink={false} emailComposerStandalone />;
};

export const PublicEventRegistrationsSmsPage: React.FC = () => {
  return <PublicEventRegistrationsEventPage showBackLink={false} smsComposerStandalone />;
};

export default PublicEventRegistrationsPage;
